/**
 * Dashboard Seating Tab Controller
 * Table management and guest assignment with drag-and-drop
 */
class DashboardSeating {
    static TRANSLATIONS = {
        en: {
            dropGuestsHere: 'Drop guests here',
            noTablesYet: 'No tables yet. Click "Add Table" to get started.',
            allGuestsAssigned: 'All guests assigned!',
            qrSignTitle: 'Ready for the event?',
            qrSignDesc: 'Print a QR code sign for the entrance. Guests scan it to find their table on their phone.',
            qrSignBtn: 'Print QR code',
            noAttendingGuests: 'No attending guests found',
            guest: 'Guest',
            edit: 'Edit',
            delete: 'Delete',
            editTable: 'Edit Table',
            addTable: 'Add Table',
            tableName: 'Table Name',
            tableNamePlaceholder: 'e.g. Table 1, Head Table, VIP',
            capacity: 'Capacity',
            tableShape: 'Table Shape',
            round: 'Round',
            rectangular: 'Rectangular',
            cancel: 'Cancel',
            save: 'Save',
            unassigned: 'unassigned',
            tablesTab: 'Tables',
            unassignedTab: 'Unassigned',
            deleteTableConfirm: 'Delete this table? Guests will be moved to unassigned.',
            createTablesFirst: 'Create tables before auto-assigning',
            assigning: 'Assigning...',
            autoAssign: 'Auto-Assign',
            assignedSuccess: 'Guests assigned successfully!',
            autoAssignFailed: 'Auto-assign failed',
            notEnoughSeats: 'Not enough seats at this table',
            clearAllConfirm: 'Clear all seating assignments?',
            upgradeTitle: 'Upgrade to Signature',
            upgradeDesc: 'Plan your seating arrangement with drag-and-drop table management, auto-assign strategies, and more.',
            upgradeCta: 'Upgrade Now',
            enableCheckinHint: 'Enable QR Check-In to use this feature',
            failedToLoad: 'Failed to load seating configuration',
            modeManual: '📋 Manual',
            modeQrAuto: '📱 QR Auto-Assign',
            strategyLabel: 'Strategy:',
            qrAutoHint: 'Guests will be auto-assigned to a table when they check in via their personal QR code or the Master QR sign',
            qrAutoForwardNote: 'Only applies to guests who check in after this mode is enabled',
            qrAutoRequiresCheckin: 'Enable QR Check-In in form settings to use this mode',
            exportSeating: 'Export',
            downloadCSV: '📥 Download CSV',
            printView: '🖨️ Print View',
            noAssignmentsToExport: 'No table assignments to export',
            undo: 'Undo', redo: 'Redo',
            keepTogether: 'Keep Together', keepApart: 'Keep Apart',
            addRule: 'Add Rule', ruleConflict: '⚠️ Rule conflict',
            selectGuest: '— Select guest —',
            editTags: 'Edit tags',
            tagsHint: "Group guests by shared tags (e.g. College Friends, Bride's side). Only you can see these.",
            memberTagsHint: 'Tags for this guest only — the rest of the party isn\'t affected. Only you can see them.',
            addTagPlaceholder: 'Type a tag, press Enter',
            saveTags: 'Save tags',
            tagsSaved: 'Tags updated',
            tagsSaveFailed: "Couldn't save tags",
            chartSaveFailed: "Couldn't save the seating chart. Your changes are still on screen — try again.",
            noTagsYet: 'No tags yet',
            bulkTag: 'Select to tag', bulkDone: 'Done',
            bulkTagSelected: 'Tag selected', bulkClearSel: 'Clear',
            selectMultiple: 'Select guests to tag', selectDone: 'Done', selectMultipleHint: 'Tap guests to tag several at once',
            bulkTitle: 'Tag selected guests',
            bulkHint: 'Check a tag to add it to everyone selected; uncheck to remove it. A dash means only some currently have it.',
            bulkApply: 'Apply', bulkApplied: 'Tags updated', bulkSelectGuests: 'Select at least one guest',
            numberOfTables: 'Number of Tables',
            addTables: 'Add Tables',
            renameTableTitle: 'Click to rename',
            dragToReorder: 'Drag to reorder',
            collapseTable: 'Collapse',
            expandTable: 'Expand',
            collapseAll: 'Collapse all tables',
            expandAll: 'Expand all tables',
            filterByCategoryHint: 'Click to filter by category',
            clearFilter: 'Clear',
            filterBtn: 'Filter',
            statusFilterHeading: 'Reply status',
            categoryFilterHeading: 'Category',
            statusComing: 'Coming',
            statusMaybe: 'Maybe',
            statusAwaiting: 'Awaiting reply',
            moreActions: 'More actions',
            expandAll: 'Expand all',
            collapseAll: 'Collapse all',
            deleteCategoryHint: 'Delete this category',
            changeCategoryColorHint: "Click to change this category's color",
            deleteCategoryConfirm: 'Delete the "{name}" category? This removes the "{name}" tag from {count} guest(s). Their seat assignments are kept.',
            categoryDeleted: '"{name}" category deleted',
            categoryDeleteFailed: 'Could not delete the category. Please try again.',
            noMatchingGuests: 'No guests match the current filter',
            emptySeats: 'empty',
            noCategoryLabel: 'Uncategorized',
            dragCategoryHint: 'Drag this category onto a table to seat them together',
            dragGroupHint: 'Drag to move entire group',
            assignTo: 'Assign to…',
            partialFillToast: 'Seated {placed} of {total}. {remaining} couldn’t fit — pick another table.',
            seatedAllToast: 'Seated {count} guests.',
            childGuest: 'Child',
            maybeGuest: 'Maybe attending',
            kidsAtTable: '{count} child(ren) at this table',
            maybeAtTable: '{count} maybe-attending guest(s) at this table',
            mayOverflow: 'May overflow when everyone arrives: {count} more expected, {free} free seat(s)',
            stillToCome: 'Still to come: {names}',
            markAsChild: 'Mark as child',
            markAsAdult: 'Mark as adult',
            kidLabel: 'Kid',
            groupSizeHint: 'Party size',
            togglePartyHint: 'Show or hide this party',
            customSeat: 'Custom',
            customSeatHint: 'Seated apart from the rest of this party',
            tableDescription: 'Description (internal)',
            tableDescriptionPlaceholder: 'e.g. Near the dance floor — only you see this',
            tableDescriptionHint: 'A private note for you. It is never sent to guests.',
            typeTableHint: 'Type a table number to seat this guest',
            tableNotFound: 'No table called “{value}”. Type an existing table number or name.',
            tableAmbiguous: 'Several tables match “{value}”. Type the full table name.'
        },
        es: {
            dropGuestsHere: 'Arrastra invitados aquí',
            noTablesYet: 'Aún no hay mesas. Haz clic en "Agregar Mesa" para comenzar.',
            allGuestsAssigned: '¡Todos los invitados asignados!',
            qrSignTitle: '¿Todo listo para el evento?',
            qrSignDesc: 'Imprime un cartel con código QR para la entrada. Los invitados lo escanean para encontrar su mesa en el móvil.',
            qrSignBtn: 'Imprimir código QR',
            noAttendingGuests: 'No se encontraron invitados confirmados',
            guest: 'Invitado',
            edit: 'Editar',
            delete: 'Eliminar',
            editTable: 'Editar Mesa',
            addTable: 'Agregar Mesa',
            tableName: 'Nombre de Mesa',
            tableNamePlaceholder: 'Ej. Mesa 1, Mesa Principal, VIP',
            capacity: 'Capacidad',
            tableShape: 'Forma de Mesa',
            round: 'Redonda',
            rectangular: 'Rectangular',
            cancel: 'Cancelar',
            save: 'Guardar',
            unassigned: 'sin asignar',
            tablesTab: 'Mesas',
            unassignedTab: 'Sin asignar',
            deleteTableConfirm: '¿Eliminar esta mesa? Los invitados se moverán a sin asignar.',
            createTablesFirst: 'Crea mesas antes de asignar automáticamente',
            assigning: 'Asignando...',
            autoAssign: 'Auto-Asignar',
            assignedSuccess: '¡Invitados asignados con éxito!',
            autoAssignFailed: 'La asignación automática falló',
            notEnoughSeats: 'No hay suficientes asientos en esta mesa',
            clearAllConfirm: '¿Borrar todas las asignaciones de asientos?',
            upgradeTitle: 'Actualizar a Signature',
            upgradeDesc: 'Planifica la disposición de asientos con gestión de mesas, estrategias de asignación automática y más.',
            upgradeCta: 'Actualizar Ahora',
            enableCheckinHint: 'Activa QR Check-In para usar esta función',
            failedToLoad: 'Error al cargar la configuración de asientos',
            modeManual: '📋 Manual',
            modeQrAuto: '📱 QR Auto-Asignar',
            strategyLabel: 'Estrategia:',
            qrAutoHint: 'Los invitados serán asignados automáticamente a una mesa al registrarse con su QR personal o con el cartel QR Master',
            qrAutoForwardNote: 'Solo aplica a invitados que registren asistencia después de activar este modo',
            qrAutoRequiresCheckin: 'Activa QR Check-In en la configuración del formulario para usar este modo',
            exportSeating: 'Exportar',
            downloadCSV: '📥 Descargar CSV',
            printView: '🖨️ Vista de Impresión',
            noAssignmentsToExport: 'No hay asignaciones de mesa para exportar',
            undo: 'Deshacer', redo: 'Rehacer',
            keepTogether: 'Juntos', keepApart: 'Separados',
            addRule: 'Agregar Regla', ruleConflict: '⚠️ Conflicto de regla',
            selectGuest: '— Seleccionar invitado —',
            editTags: 'Editar etiquetas',
            tagsHint: 'Agrupa a los invitados por etiquetas compartidas (p. ej. Amigos de la universidad, Lado de la novia). Solo tú las ves.',
            memberTagsHint: 'Etiquetas solo para este invitado — el resto del grupo no se ve afectado. Solo tú las ves.',
            addTagPlaceholder: 'Escribe una etiqueta y pulsa Enter',
            saveTags: 'Guardar etiquetas',
            tagsSaved: 'Etiquetas actualizadas',
            tagsSaveFailed: 'No se pudieron guardar las etiquetas',
            chartSaveFailed: 'No se pudo guardar el plano de mesas. Tus cambios siguen en pantalla: inténtalo de nuevo.',
            noTagsYet: 'Aún no hay etiquetas',
            bulkTag: 'Seleccionar para etiquetar', bulkDone: 'Listo',
            bulkTagSelected: 'Etiquetar selección', bulkClearSel: 'Limpiar',
            selectMultiple: 'Selecciona invitados para etiquetar', selectDone: 'Listo', selectMultipleHint: 'Toca invitados para etiquetar varios a la vez',
            bulkTitle: 'Etiquetar invitados seleccionados',
            bulkHint: 'Marca una etiqueta para añadirla a todos los seleccionados; desmárcala para quitarla. Un guion indica que solo algunos la tienen.',
            bulkApply: 'Aplicar', bulkApplied: 'Etiquetas actualizadas', bulkSelectGuests: 'Selecciona al menos un invitado',
            numberOfTables: 'Número de Mesas',
            addTables: 'Agregar Mesas',
            renameTableTitle: 'Haz clic para renombrar',
            dragToReorder: 'Arrastra para reordenar',
            collapseTable: 'Contraer',
            expandTable: 'Expandir',
            collapseAll: 'Contraer todas las mesas',
            expandAll: 'Expandir todas las mesas',
            filterByCategoryHint: 'Haz clic para filtrar por categoría',
            clearFilter: 'Limpiar',
            filterBtn: 'Filtrar',
            statusFilterHeading: 'Estado de respuesta',
            categoryFilterHeading: 'Categoría',
            statusComing: 'Asistirán',
            statusMaybe: 'Quizás',
            statusAwaiting: 'Sin responder',
            moreActions: 'Más acciones',
            expandAll: 'Expandir todo',
            collapseAll: 'Contraer todo',
            deleteCategoryHint: 'Eliminar esta categoría',
            changeCategoryColorHint: 'Haz clic para cambiar el color de esta categoría',
            deleteCategoryConfirm: '¿Eliminar la categoría "{name}"? Esto quita la etiqueta "{name}" de {count} invitado(s). Se conservan sus asignaciones de asiento.',
            categoryDeleted: 'Categoría "{name}" eliminada',
            categoryDeleteFailed: 'No se pudo eliminar la categoría. Inténtalo de nuevo.',
            noMatchingGuests: 'Ningún invitado coincide con el filtro actual',
            emptySeats: 'vacíos',
            noCategoryLabel: 'Sin categoría',
            dragCategoryHint: 'Arrastra esta categoría a una mesa para sentarlos juntos',
            dragGroupHint: 'Arrastra para mover todo el grupo',
            assignTo: 'Asignar a…',
            partialFillToast: '{placed} de {total} asignados. {remaining} no caben; elige otra mesa.',
            seatedAllToast: '{count} invitados asignados.',
            childGuest: 'Niño/a',
            maybeGuest: 'Asistencia tentativa',
            kidsAtTable: '{count} niño(s) en esta mesa',
            maybeAtTable: '{count} invitado(s) tentativo(s) en esta mesa',
            mayOverflow: 'Puede desbordarse cuando lleguen todos: faltan {count}, {free} asiento(s) libre(s)',
            stillToCome: 'Aún por llegar: {names}',
            markAsChild: 'Marcar como niño/a',
            markAsAdult: 'Marcar como adulto',
            kidLabel: 'Niño',
            groupSizeHint: 'Tamaño del grupo',
            customSeat: 'Personalizado',
            customSeatHint: 'Sentado/a aparte del resto del grupo',
            tableDescription: 'Descripción (interna)',
            tableDescriptionPlaceholder: 'p. ej. Cerca de la pista — solo tú lo ves',
            tableDescriptionHint: 'Una nota privada para ti. Nunca se envía a los invitados.',
            typeTableHint: 'Escribe un número de mesa para sentar a este invitado',
            tableNotFound: 'No hay ninguna mesa llamada «{value}». Escribe un número o nombre de mesa existente.',
            tableAmbiguous: 'Varias mesas coinciden con «{value}». Escribe el nombre completo de la mesa.'
        },
        fr: {
            dropGuestsHere: 'Déposez les invités ici',
            noTablesYet: 'Pas encore de tables. Cliquez sur "Ajouter une Table" pour commencer.',
            allGuestsAssigned: 'Tous les invités sont placés !',
            qrSignTitle: 'Prêt pour l\'événement ?',
            qrSignDesc: 'Imprimez une affiche QR pour l\'entrée. Les invités la scannent pour trouver leur table sur leur téléphone.',
            qrSignBtn: 'Imprimer le code QR',
            noAttendingGuests: 'Aucun invité confirmé trouvé',
            guest: 'Invité',
            edit: 'Modifier',
            delete: 'Supprimer',
            editTable: 'Modifier la Table',
            addTable: 'Ajouter une Table',
            tableName: 'Nom de la Table',
            tableNamePlaceholder: 'Ex. Table 1, Table d\'Honneur, VIP',
            capacity: 'Capacité',
            tableShape: 'Forme de Table',
            round: 'Ronde',
            rectangular: 'Rectangulaire',
            cancel: 'Annuler',
            save: 'Enregistrer',
            unassigned: 'non attribués',
            tablesTab: 'Tables',
            unassignedTab: 'Non attribués',
            deleteTableConfirm: 'Supprimer cette table ? Les invités seront déplacés vers non attribués.',
            createTablesFirst: 'Créez des tables avant d\'attribuer automatiquement',
            assigning: 'Attribution...',
            autoAssign: 'Auto-Attribuer',
            assignedSuccess: 'Invités attribués avec succès !',
            autoAssignFailed: 'L\'attribution automatique a échoué',
            notEnoughSeats: 'Pas assez de places à cette table',
            clearAllConfirm: 'Effacer toutes les attributions de places ?',
            upgradeTitle: 'Passer à Signature',
            upgradeDesc: 'Planifiez vos places avec la gestion des tables, les stratégies d\'attribution automatique et plus encore.',
            upgradeCta: 'Mettre à niveau',
            enableCheckinHint: 'Activez le QR Check-In pour utiliser cette fonctionnalité',
            failedToLoad: 'Échec du chargement de la configuration des places',
            modeManual: '📋 Manuel',
            modeQrAuto: '📱 QR Auto-Attribution',
            strategyLabel: 'Stratégie :',
            qrAutoHint: 'Les invités seront automatiquement attribués à une table lors de leur enregistrement via QR personnel ou le panneau QR Master',
            qrAutoForwardNote: 'S\'applique uniquement aux invités qui s\'enregistrent après l\'activation de ce mode',
            qrAutoRequiresCheckin: 'Activez le QR Check-In dans les paramètres du formulaire pour utiliser ce mode',
            exportSeating: 'Exporter',
            downloadCSV: '📥 Télécharger CSV',
            printView: '🖨️ Aperçu Impression',
            noAssignmentsToExport: 'Aucune attribution de table à exporter',
            undo: 'Annuler', redo: 'Rétablir',
            keepTogether: 'Ensemble', keepApart: 'Séparés',
            addRule: 'Ajouter Règle', ruleConflict: '⚠️ Conflit de règle',
            selectGuest: '— Choisir invité —',
            editTags: 'Modifier les étiquettes',
            tagsHint: 'Regroupez les invités par étiquettes communes (ex. : Amis de fac, Côté de la mariée). Vous seul les voyez.',
            memberTagsHint: 'Étiquettes pour cet invité uniquement — le reste du groupe n\'est pas concerné. Vous seul les voyez.',
            addTagPlaceholder: 'Saisissez une étiquette, puis Entrée',
            saveTags: 'Enregistrer',
            tagsSaved: 'Étiquettes mises à jour',
            tagsSaveFailed: "Impossible d'enregistrer les étiquettes",
            chartSaveFailed: "Impossible d'enregistrer le plan de table. Vos modifications sont toujours à l'écran, réessayez.",
            noTagsYet: 'Aucune étiquette',
            bulkTag: 'Sélectionner pour étiqueter', bulkDone: 'Terminé',
            bulkTagSelected: 'Étiqueter la sélection', bulkClearSel: 'Effacer',
            selectMultiple: 'Sélectionnez des invités à étiqueter', selectDone: 'Terminé', selectMultipleHint: 'Touchez des invités pour en étiqueter plusieurs',
            bulkTitle: 'Étiqueter les invités sélectionnés',
            bulkHint: 'Cochez une étiquette pour l’ajouter à tous les invités sélectionnés ; décochez pour la retirer. Un tiret indique que certains seulement la possèdent.',
            bulkApply: 'Appliquer', bulkApplied: 'Étiquettes mises à jour', bulkSelectGuests: 'Sélectionnez au moins un invité',
            numberOfTables: 'Nombre de Tables',
            addTables: 'Ajouter des Tables',
            renameTableTitle: 'Cliquez pour renommer',
            dragToReorder: 'Glissez pour réorganiser',
            collapseTable: 'Réduire',
            expandTable: 'Développer',
            collapseAll: 'Réduire toutes les tables',
            expandAll: 'Développer toutes les tables',
            filterByCategoryHint: 'Cliquez pour filtrer par catégorie',
            clearFilter: 'Effacer',
            filterBtn: 'Filtrer',
            statusFilterHeading: 'Réponse',
            categoryFilterHeading: 'Catégorie',
            statusComing: 'Présents',
            statusMaybe: 'Peut-être',
            statusAwaiting: 'Sans réponse',
            moreActions: "Plus d'actions",
            expandAll: 'Tout déplier',
            collapseAll: 'Tout replier',
            deleteCategoryHint: 'Supprimer cette catégorie',
            changeCategoryColorHint: 'Cliquez pour changer la couleur de cette catégorie',
            deleteCategoryConfirm: 'Supprimer la catégorie « {name} » ? Cela retire l\'étiquette « {name} » de {count} invité(s). Leurs attributions de places sont conservées.',
            categoryDeleted: 'Catégorie « {name} » supprimée',
            categoryDeleteFailed: 'Impossible de supprimer la catégorie. Veuillez réessayer.',
            noMatchingGuests: 'Aucun invité ne correspond au filtre actuel',
            emptySeats: 'libres',
            noCategoryLabel: 'Sans catégorie',
            dragCategoryHint: 'Glissez cette catégorie sur une table pour les placer ensemble',
            dragGroupHint: 'Glissez pour déplacer tout le groupe',
            assignTo: 'Attribuer à…',
            partialFillToast: '{placed} sur {total} placés. {remaining} ne tiennent pas, choisissez une autre table.',
            seatedAllToast: '{count} invités placés.',
            childGuest: 'Enfant',
            maybeGuest: 'Présence incertaine',
            kidsAtTable: '{count} enfant(s) à cette table',
            maybeAtTable: '{count} invité(s) incertain(s) à cette table',
            mayOverflow: 'Risque de débordement quand tout le monde sera là : encore {count} attendu(s), {free} place(s) libre(s)',
            stillToCome: 'Encore attendus : {names}',
            markAsChild: 'Marquer comme enfant',
            markAsAdult: 'Marquer comme adulte',
            kidLabel: 'Enfant',
            groupSizeHint: 'Taille du groupe',
            customSeat: 'Personnalisé',
            customSeatHint: 'Placé à part du reste du groupe',
            tableDescription: 'Description (interne)',
            tableDescriptionPlaceholder: 'ex. Près de la piste — visible par vous seul',
            tableDescriptionHint: 'Une note privée pour vous. Elle n’est jamais envoyée aux invités.',
            typeTableHint: 'Saisissez un numéro de table pour y placer cet invité',
            tableNotFound: 'Aucune table nommée « {value} ». Saisissez un numéro ou un nom de table existant.',
            tableAmbiguous: 'Plusieurs tables correspondent à « {value} ». Saisissez le nom complet de la table.'
        },
        pt: {
            dropGuestsHere: 'Arraste convidados aqui',
            noTablesYet: 'Ainda sem mesas. Clique em "Adicionar Mesa" para começar.',
            allGuestsAssigned: 'Todos os convidados atribuídos!',
            qrSignTitle: 'Tudo pronto para o evento?',
            qrSignDesc: 'Imprima um cartaz com QR code para a entrada. Os convidados escaneiam para encontrar a mesa no celular.',
            qrSignBtn: 'Imprimir código QR',
            noAttendingGuests: 'Nenhum convidado confirmado encontrado',
            guest: 'Convidado',
            edit: 'Editar',
            delete: 'Excluir',
            editTable: 'Editar Mesa',
            addTable: 'Adicionar Mesa',
            tableName: 'Nome da Mesa',
            tableNamePlaceholder: 'Ex. Mesa 1, Mesa Principal, VIP',
            capacity: 'Capacidade',
            tableShape: 'Formato da Mesa',
            round: 'Redonda',
            rectangular: 'Retangular',
            cancel: 'Cancelar',
            save: 'Salvar',
            unassigned: 'não atribuídos',
            tablesTab: 'Mesas',
            unassignedTab: 'Não atribuídos',
            deleteTableConfirm: 'Excluir esta mesa? Os convidados serão movidos para não atribuídos.',
            createTablesFirst: 'Crie mesas antes de atribuir automaticamente',
            assigning: 'Atribuindo...',
            autoAssign: 'Auto-Atribuir',
            assignedSuccess: 'Convidados atribuídos com sucesso!',
            autoAssignFailed: 'A atribuição automática falhou',
            notEnoughSeats: 'Não há lugares suficientes nesta mesa',
            clearAllConfirm: 'Limpar todas as atribuições de lugares?',
            upgradeTitle: 'Atualizar para Signature',
            upgradeDesc: 'Planeje a disposição dos assentos com gestão de mesas, estratégias de atribuição automática e mais.',
            upgradeCta: 'Atualizar Agora',
            enableCheckinHint: 'Ative o QR Check-In para usar este recurso',
            failedToLoad: 'Falha ao carregar a configuração de assentos',
            modeManual: '📋 Manual',
            modeQrAuto: '📱 QR Auto-Atribuir',
            strategyLabel: 'Estratégia:',
            qrAutoHint: 'Os convidados serão atribuídos automaticamente a uma mesa ao fazer check-in via QR pessoal ou pelo painel QR Master',
            qrAutoForwardNote: 'Aplica-se apenas aos convidados que fizerem check-in após a ativação deste modo',
            qrAutoRequiresCheckin: 'Ative o QR Check-In nas configurações do formulário para usar este modo',
            exportSeating: 'Exportar',
            downloadCSV: '📥 Baixar CSV',
            printView: '🖨️ Visualização para Impressão',
            noAssignmentsToExport: 'Nenhuma atribuição de mesa para exportar',
            undo: 'Desfazer', redo: 'Refazer',
            keepTogether: 'Juntos', keepApart: 'Separados',
            addRule: 'Adicionar Regra', ruleConflict: '⚠️ Conflito de regra',
            selectGuest: '— Selecionar convidado —',
            editTags: 'Editar etiquetas',
            tagsHint: 'Agrupe os convidados por etiquetas em comum (ex.: Amigos da faculdade, Lado da noiva). Só você as vê.',
            memberTagsHint: 'Etiquetas só para este convidado — o resto do grupo não é afetado. Só você as vê.',
            addTagPlaceholder: 'Digite uma etiqueta e pressione Enter',
            saveTags: 'Guardar etiquetas',
            tagsSaved: 'Etiquetas atualizadas',
            tagsSaveFailed: 'Não foi possível guardar as etiquetas',
            chartSaveFailed: 'Não foi possível guardar o plano de mesas. As suas alterações continuam no ecrã — tente novamente.',
            noTagsYet: 'Ainda sem etiquetas',
            bulkTag: 'Selecionar para etiquetar', bulkDone: 'Concluído',
            bulkTagSelected: 'Etiquetar seleção', bulkClearSel: 'Limpar',
            selectMultiple: 'Selecione convidados para etiquetar', selectDone: 'Concluído', selectMultipleHint: 'Toque nos convidados para etiquetar vários de uma vez',
            bulkTitle: 'Etiquetar convidados selecionados',
            bulkHint: 'Marque uma etiqueta para adicioná-la a todos os selecionados; desmarque para removê-la. Um traço indica que apenas alguns a têm.',
            bulkApply: 'Aplicar', bulkApplied: 'Etiquetas atualizadas', bulkSelectGuests: 'Selecione pelo menos um convidado',
            numberOfTables: 'Número de Mesas',
            addTables: 'Adicionar Mesas',
            renameTableTitle: 'Clique para renomear',
            dragToReorder: 'Arraste para reordenar',
            collapseTable: 'Recolher',
            expandTable: 'Expandir',
            collapseAll: 'Recolher todas as mesas',
            expandAll: 'Expandir todas as mesas',
            filterByCategoryHint: 'Clique para filtrar por categoria',
            clearFilter: 'Limpar',
            filterBtn: 'Filtrar',
            statusFilterHeading: 'Resposta',
            categoryFilterHeading: 'Categoria',
            statusComing: 'Vão comparecer',
            statusMaybe: 'Talvez',
            statusAwaiting: 'Sem resposta',
            moreActions: 'Mais ações',
            expandAll: 'Expandir tudo',
            collapseAll: 'Recolher tudo',
            deleteCategoryHint: 'Excluir esta categoria',
            changeCategoryColorHint: 'Clique para alterar a cor desta categoria',
            deleteCategoryConfirm: 'Excluir a categoria "{name}"? Isso remove a etiqueta "{name}" de {count} convidado(s). As atribuições de lugar são mantidas.',
            categoryDeleted: 'Categoria "{name}" excluída',
            categoryDeleteFailed: 'Não foi possível excluir a categoria. Tente novamente.',
            noMatchingGuests: 'Nenhum convidado corresponde ao filtro atual',
            emptySeats: 'vazios',
            noCategoryLabel: 'Sem categoria',
            dragCategoryHint: 'Arraste esta categoria para uma mesa para sentá-los juntos',
            dragGroupHint: 'Arraste para mover o grupo inteiro',
            assignTo: 'Atribuir a…',
            partialFillToast: '{placed} de {total} sentados. {remaining} não couberam — escolha outra mesa.',
            seatedAllToast: '{count} convidados sentados.',
            childGuest: 'Criança',
            maybeGuest: 'Presença incerta',
            kidsAtTable: '{count} criança(s) nesta mesa',
            maybeAtTable: '{count} convidado(s) incerto(s) nesta mesa',
            mayOverflow: 'Pode lotar quando todos chegarem: faltam {count}, {free} lugar(es) livre(s)',
            stillToCome: 'Ainda por chegar: {names}',
            markAsChild: 'Marcar como criança',
            markAsAdult: 'Marcar como adulto',
            kidLabel: 'Criança',
            groupSizeHint: 'Tamanho do grupo',
            customSeat: 'Personalizado',
            customSeatHint: 'Sentado à parte do resto do grupo',
            tableDescription: 'Descrição (interna)',
            tableDescriptionPlaceholder: 'ex. Perto da pista — só você vê',
            tableDescriptionHint: 'Uma nota privada para você. Nunca é enviada aos convidados.',
            typeTableHint: 'Digite um número de mesa para acomodar este convidado',
            tableNotFound: 'Nenhuma mesa chamada “{value}”. Digite um número ou nome de mesa existente.',
            tableAmbiguous: 'Várias mesas correspondem a “{value}”. Digite o nome completo da mesa.'
        },
        de: {
            dropGuestsHere: 'Gäste hier ablegen',
            noTablesYet: 'Noch keine Tische. Klicken Sie auf "Tisch hinzufügen" um zu beginnen.',
            allGuestsAssigned: 'Alle Gäste zugewiesen!',
            qrSignTitle: 'Bereit für die Veranstaltung?',
            qrSignDesc: 'Drucken Sie ein QR-Schild für den Eingang. Gäste scannen es und finden ihren Tisch auf dem Handy.',
            qrSignBtn: 'QR-Code drucken',
            noAttendingGuests: 'Keine bestätigten Gäste gefunden',
            guest: 'Gast',
            edit: 'Bearbeiten',
            delete: 'Löschen',
            editTable: 'Tisch bearbeiten',
            addTable: 'Tisch hinzufügen',
            tableName: 'Tischname',
            tableNamePlaceholder: 'Z.B. Tisch 1, Ehrentisch, VIP',
            capacity: 'Kapazität',
            tableShape: 'Tischform',
            round: 'Rund',
            rectangular: 'Rechteckig',
            cancel: 'Abbrechen',
            save: 'Speichern',
            unassigned: 'nicht zugewiesen',
            tablesTab: 'Tische',
            unassignedTab: 'Nicht zugewiesen',
            deleteTableConfirm: 'Diesen Tisch löschen? Gäste werden zu nicht zugewiesen verschoben.',
            createTablesFirst: 'Erstellen Sie Tische, bevor Sie automatisch zuweisen',
            assigning: 'Zuweisen...',
            autoAssign: 'Auto-Zuweisen',
            assignedSuccess: 'Gäste erfolgreich zugewiesen!',
            autoAssignFailed: 'Automatische Zuweisung fehlgeschlagen',
            notEnoughSeats: 'Nicht genügend Plätze an diesem Tisch',
            clearAllConfirm: 'Alle Sitzplatzzuweisungen löschen?',
            upgradeTitle: 'Auf Signature upgraden',
            upgradeDesc: 'Planen Sie die Sitzordnung mit Tischverwaltung, automatischen Zuweisungsstrategien und mehr.',
            upgradeCta: 'Jetzt upgraden',
            enableCheckinHint: 'Aktivieren Sie QR Check-In, um diese Funktion zu nutzen',
            failedToLoad: 'Sitzplatzkonfiguration konnte nicht geladen werden',
            modeManual: '📋 Manuell',
            modeQrAuto: '📱 QR Auto-Zuweisung',
            strategyLabel: 'Strategie:',
            qrAutoHint: 'Gäste werden automatisch einem Tisch zugewiesen, wenn sie sich per persönlichem QR-Code oder dem Master-QR-Schild einchecken',
            qrAutoForwardNote: 'Gilt nur für Gäste, die sich nach Aktivierung dieses Modus einchecken',
            qrAutoRequiresCheckin: 'Aktivieren Sie QR Check-In in den Formulareinstellungen, um diesen Modus zu nutzen',
            exportSeating: 'Exportieren',
            downloadCSV: '📥 CSV herunterladen',
            printView: '🖨️ Druckansicht',
            noAssignmentsToExport: 'Keine Tischzuweisungen zum Exportieren',
            undo: 'Rückgängig', redo: 'Wiederherstellen',
            keepTogether: 'Zusammen', keepApart: 'Getrennt',
            addRule: 'Regel hinzufügen', ruleConflict: '⚠️ Regelkonflikt',
            selectGuest: '— Gast auswählen —',
            editTags: 'Tags bearbeiten',
            tagsHint: 'Gäste nach gemeinsamen Tags gruppieren (z. B. Studienfreunde, Seite der Braut). Nur du siehst sie.',
            memberTagsHint: 'Tags nur für diesen Gast — der Rest der Gruppe bleibt unberührt. Nur du siehst sie.',
            addTagPlaceholder: 'Tag eingeben, Enter drücken',
            saveTags: 'Tags speichern',
            tagsSaved: 'Tags aktualisiert',
            tagsSaveFailed: 'Tags konnten nicht gespeichert werden',
            chartSaveFailed: 'Der Sitzplan konnte nicht gespeichert werden. Ihre Änderungen sind noch da — bitte erneut versuchen.',
            noTagsYet: 'Noch keine Tags',
            bulkTag: 'Zum Markieren auswählen', bulkDone: 'Fertig',
            bulkTagSelected: 'Auswahl taggen', bulkClearSel: 'Leeren',
            selectMultiple: 'Gäste zum Taggen auswählen', selectDone: 'Fertig', selectMultipleHint: 'Gäste antippen, um mehrere auf einmal zu taggen',
            bulkTitle: 'Ausgewählte Gäste taggen',
            bulkHint: 'Tag ankreuzen, um ihn allen Ausgewählten hinzuzufügen; abwählen zum Entfernen. Ein Strich bedeutet, dass nur einige ihn haben.',
            bulkApply: 'Anwenden', bulkApplied: 'Tags aktualisiert', bulkSelectGuests: 'Wähle mindestens einen Gast',
            numberOfTables: 'Anzahl der Tische',
            addTables: 'Tische hinzufügen',
            renameTableTitle: 'Zum Umbenennen klicken',
            dragToReorder: 'Zum Umsortieren ziehen',
            collapseTable: 'Einklappen',
            expandTable: 'Ausklappen',
            collapseAll: 'Alle Tische einklappen',
            expandAll: 'Alle Tische ausklappen',
            filterByCategoryHint: 'Klicken, um nach Kategorie zu filtern',
            clearFilter: 'Zurücksetzen',
            filterBtn: 'Filtern',
            statusFilterHeading: 'Antwort',
            categoryFilterHeading: 'Kategorie',
            statusComing: 'Kommen',
            statusMaybe: 'Vielleicht',
            statusAwaiting: 'Antwort ausstehend',
            moreActions: 'Weitere Aktionen',
            expandAll: 'Alle ausklappen',
            collapseAll: 'Alle einklappen',
            deleteCategoryHint: 'Diese Kategorie löschen',
            changeCategoryColorHint: 'Klicken, um die Farbe dieser Kategorie zu ändern',
            deleteCategoryConfirm: 'Kategorie „{name}" löschen? Damit wird die Markierung „{name}" von {count} Gast/Gästen entfernt. Die Sitzplatzzuweisungen bleiben erhalten.',
            categoryDeleted: 'Kategorie „{name}" gelöscht',
            categoryDeleteFailed: 'Kategorie konnte nicht gelöscht werden. Bitte versuche es erneut.',
            noMatchingGuests: 'Keine Gäste entsprechen dem aktuellen Filter',
            emptySeats: 'frei',
            noCategoryLabel: 'Ohne Kategorie',
            dragCategoryHint: 'Diese Kategorie auf einen Tisch ziehen, um alle gemeinsam zu setzen',
            dragGroupHint: 'Ziehen, um die ganze Gruppe zu verschieben',
            assignTo: 'Zuweisen zu…',
            partialFillToast: '{placed} von {total} platziert. {remaining} passen nicht — wähle einen anderen Tisch.',
            seatedAllToast: '{count} Gäste platziert.',
            childGuest: 'Kind',
            maybeGuest: 'Teilnahme unsicher',
            kidsAtTable: '{count} Kind(er) an diesem Tisch',
            maybeAtTable: '{count} unsicherer Gast/Gäste an diesem Tisch',
            mayOverflow: 'Könnte überlaufen, wenn alle kommen: noch {count} erwartet, {free} Platz/Plätze frei',
            stillToCome: 'Noch erwartet: {names}',
            markAsChild: 'Als Kind markieren',
            markAsAdult: 'Als Erwachsenen markieren',
            kidLabel: 'Kind',
            groupSizeHint: 'Gruppengröße',
            customSeat: 'Individuell',
            customSeatHint: 'Sitzt getrennt vom Rest der Gruppe',
            tableDescription: 'Beschreibung (intern)',
            tableDescriptionPlaceholder: 'z. B. Nahe der Tanzfläche — nur für Sie sichtbar',
            tableDescriptionHint: 'Eine private Notiz für Sie. Sie wird nie an Gäste gesendet.',
            typeTableHint: 'Tischnummer eingeben, um diesen Gast zu platzieren',
            tableNotFound: 'Kein Tisch mit dem Namen „{value}“. Geben Sie eine vorhandene Tischnummer oder einen Namen ein.',
            tableAmbiguous: 'Mehrere Tische passen zu „{value}“. Geben Sie den vollständigen Tischnamen ein.'
        },
        he: {
            dropGuestsHere: 'גרור אורחים לכאן',
            noTablesYet: 'עדיין אין שולחנות. לחץ על "הוסף שולחן" כדי להתחיל.',
            allGuestsAssigned: 'כל האורחים שובצו!',
            qrSignTitle: 'מוכנים לאירוע?',
            qrSignDesc: 'הדפיסו שלט עם קוד QR לכניסה. האורחים סורקים אותו ומוצאים את השולחן שלהם בטלפון.',
            qrSignBtn: 'הדפסת קוד QR',
            noAttendingGuests: 'לא נמצאו אורחים מאשרים',
            guest: 'אורח',
            edit: 'ערוך',
            delete: 'מחק',
            editTable: 'ערוך שולחן',
            addTable: 'הוסף שולחן',
            tableName: 'שם השולחן',
            tableNamePlaceholder: 'לדוגמה: שולחן 1, שולחן כבוד, VIP',
            capacity: 'קיבולת',
            tableShape: 'צורת השולחן',
            round: 'עגול',
            rectangular: 'מלבני',
            cancel: 'ביטול',
            save: 'שמור',
            unassigned: 'לא משויכים',
            tablesTab: 'שולחנות',
            unassignedTab: 'לא משויכים',
            deleteTableConfirm: 'למחוק שולחן זה? האורחים יועברו ללא משויכים.',
            createTablesFirst: 'צור שולחנות לפני שיבוץ אוטומטי',
            assigning: 'משבץ...',
            autoAssign: 'שיבוץ אוטומטי',
            assignedSuccess: 'אורחים שובצו בהצלחה!',
            autoAssignFailed: 'השיבוץ האוטומטי נכשל',
            notEnoughSeats: 'אין מספיק מקומות בשולחן זה',
            clearAllConfirm: 'לנקות את כל שיבוצי הישיבה?',
            upgradeTitle: 'שדרוג ל-Signature',
            upgradeDesc: 'תכנן סידורי ישיבה עם ניהול שולחנות, אסטרטגיות שיבוץ אוטומטי ועוד.',
            upgradeCta: 'שדרג עכשיו',
            enableCheckinHint: 'הפעל QR צ׳ק-אין כדי להשתמש בתכונה זו',
            failedToLoad: 'נכשל טעינת תצורת הישיבה',
            modeManual: '📋 ידני',
            modeQrAuto: '📱 שיבוץ QR אוטומטי',
            strategyLabel: 'אסטרטגיה:',
            qrAutoHint: 'אורחים ישובצו אוטומטית לשולחן כשהם עושים צ׳ק-אין דרך קוד QR אישי או שלט ה-QR הראשי',
            qrAutoForwardNote: 'חל רק על אורחים שעושים צ׳ק-אין אחרי הפעלת מצב זה',
            qrAutoRequiresCheckin: 'הפעל QR צ׳ק-אין בהגדרות הטופס כדי להשתמש במצב זה',
            exportSeating: 'ייצוא',
            downloadCSV: '📥 הורד CSV',
            printView: '🖨️ תצוגת הדפסה',
            noAssignmentsToExport: 'אין שיבוצים לייצוא',
            undo: 'בטל', redo: 'בצע שוב',
            keepTogether: 'ביחד', keepApart: 'בנפרד',
            addRule: 'הוסף כלל', ruleConflict: '⚠️ קונפליקט',
            selectGuest: '— בחר אורח —',
            editTags: 'עריכת תגיות',
            tagsHint: 'קבצו אורחים לפי תגיות משותפות (למשל חברים מהאוניברסיטה, צד הכלה). רק אתם רואים אותן.',
            memberTagsHint: 'תגיות לאורח הזה בלבד — שאר בני הקבוצה לא מושפעים. רק אתם רואים אותן.',
            addTagPlaceholder: 'הקלידו תגית ולחצו Enter',
            saveTags: 'שמירת תגיות',
            tagsSaved: 'התגיות עודכנו',
            tagsSaveFailed: 'לא ניתן היה לשמור את התגיות',
            chartSaveFailed: 'לא ניתן היה לשמור את סידור ההושבה. השינויים עדיין כאן — נסו שוב.',
            noTagsYet: 'אין תגיות עדיין',
            bulkTag: 'בחירה לתיוג', bulkDone: 'סיום',
            bulkTagSelected: 'תיוג הנבחרים', bulkClearSel: 'ניקוי',
            selectMultiple: 'בחרו אורחים לתיוג', selectDone: 'סיום', selectMultipleHint: 'הקישו על אורחים כדי לתייג כמה בבת אחת',
            bulkTitle: 'תיוג האורחים שנבחרו',
            bulkHint: 'סמנו תגית כדי להוסיף אותה לכל הנבחרים; בטלו סימון כדי להסיר. קו מציין שרק לחלק מהם יש אותה.',
            bulkApply: 'החלה', bulkApplied: 'התגיות עודכנו', bulkSelectGuests: 'בחרו לפחות אורח אחד',
            numberOfTables: 'מספר שולחנות',
            addTables: 'הוסף שולחנות',
            renameTableTitle: 'לחץ לשינוי השם',
            dragToReorder: 'גרור כדי לסדר מחדש',
            collapseTable: 'כווץ',
            expandTable: 'הרחב',
            collapseAll: 'כווץ את כל השולחנות',
            expandAll: 'הרחב את כל השולחנות',
            filterByCategoryHint: 'לחץ לסינון לפי קטגוריה',
            clearFilter: 'נקה',
            filterBtn: 'סינון',
            statusFilterHeading: 'סטטוס תשובה',
            categoryFilterHeading: 'קטגוריה',
            statusComing: 'מגיעים',
            statusMaybe: 'אולי',
            statusAwaiting: 'ממתין לתשובה',
            moreActions: 'פעולות נוספות',
            expandAll: 'הרחב הכול',
            collapseAll: 'כווץ הכול',
            deleteCategoryHint: 'מחק קטגוריה זו',
            changeCategoryColorHint: 'לחצו כדי לשנות את צבע הקטגוריה',
            deleteCategoryConfirm: 'למחוק את הקטגוריה "{name}"? פעולה זו מסירה את התווית "{name}" מ-{count} אורחים. שיבוצי הישיבה נשמרים.',
            categoryDeleted: 'הקטגוריה "{name}" נמחקה',
            categoryDeleteFailed: 'לא ניתן למחוק את הקטגוריה. נסה שוב.',
            noMatchingGuests: 'אין אורחים שתואמים לסינון הנוכחי',
            emptySeats: 'פנויים',
            noCategoryLabel: 'ללא קטגוריה',
            dragCategoryHint: 'גרור קטגוריה זו לשולחן כדי להושיב את כולם יחד',
            dragGroupHint: 'גרור כדי להעביר את הקבוצה כולה',
            assignTo: 'שייך לשולחן…',
            partialFillToast: 'שובצו {placed} מתוך {total}. {remaining} לא נכנסו — בחר שולחן אחר.',
            seatedAllToast: 'שובצו {count} אורחים.',
            childGuest: 'ילד/ה',
            maybeGuest: 'בסימן שאלה',
            kidsAtTable: '{count} ילדים בשולחן זה',
            maybeAtTable: '{count} אורחים בסימן שאלה בשולחן זה',
            mayOverflow: 'עלול לחרוג מהקיבולת כשכולם יגיעו: עוד {count} צפויים, {free} מקומות פנויים',
            stillToCome: 'עוד בדרך: {names}',
            markAsChild: 'סמן כילד/ה',
            markAsAdult: 'סמן כמבוגר/ת',
            kidLabel: 'ילד',
            groupSizeHint: 'גודל הקבוצה',
            customSeat: 'מותאם',
            customSeatHint: 'יושב/ת בנפרד משאר הקבוצה',
            tableDescription: 'תיאור (פנימי)',
            tableDescriptionPlaceholder: 'למשל ליד רחבת הריקודים — רק אתה רואה זאת',
            tableDescriptionHint: 'הערה פרטית עבורך. היא לעולם לא נשלחת לאורחים.',
            typeTableHint: 'הקלד מספר שולחן כדי להושיב את האורח',
            tableNotFound: 'אין שולחן בשם "{value}". הקלד מספר או שם של שולחן קיים.',
            tableAmbiguous: 'יותר משולחן אחד מתאים ל-"{value}". הקלד את שם השולחן המלא.'
        },
        ar: {
            dropGuestsHere: 'اسحب الضيوف إلى هنا',
            noTablesYet: 'لا توجد طاولات بعد. انقر على "إضافة طاولة" للبدء.',
            allGuestsAssigned: 'تم تعيين جميع الضيوف!',
            qrSignTitle: 'هل أنت جاهز للمناسبة؟',
            qrSignDesc: 'اطبع لافتة رمز QR للمدخل. يمسحها الضيوف ليجدوا طاولتهم على هواتفهم.',
            qrSignBtn: 'طباعة رمز QR',
            noAttendingGuests: 'لم يتم العثور على ضيوف مؤكدين',
            guest: 'ضيف',
            edit: 'تعديل',
            delete: 'حذف',
            editTable: 'تعديل الطاولة',
            addTable: 'إضافة طاولة',
            tableName: 'اسم الطاولة',
            tableNamePlaceholder: 'مثال: طاولة 1، طاولة الشرف، VIP',
            capacity: 'السعة',
            tableShape: 'شكل الطاولة',
            round: 'دائرية',
            rectangular: 'مستطيلة',
            cancel: 'إلغاء',
            save: 'حفظ',
            unassigned: 'غير معينين',
            tablesTab: 'الطاولات',
            unassignedTab: 'غير معينين',
            deleteTableConfirm: 'حذف هذه الطاولة؟ سيتم نقل الضيوف إلى غير معينين.',
            createTablesFirst: 'أنشئ طاولات قبل التعيين التلقائي',
            assigning: 'جاري التعيين...',
            autoAssign: 'تعيين تلقائي',
            assignedSuccess: 'تم تعيين الضيوف بنجاح!',
            autoAssignFailed: 'فشل التعيين التلقائي',
            notEnoughSeats: 'لا توجد مقاعد كافية في هذه الطاولة',
            clearAllConfirm: 'مسح جميع تعيينات المقاعد؟',
            upgradeTitle: 'الترقية إلى Signature',
            upgradeDesc: 'خطط ترتيب الجلوس مع إدارة الطاولات واستراتيجيات التعيين التلقائي والمزيد.',
            upgradeCta: 'ترقية الآن',
            enableCheckinHint: 'فعّل QR تسجيل الحضور لاستخدام هذه الميزة',
            failedToLoad: 'فشل تحميل تكوين المقاعد',
            modeManual: '📋 يدوي',
            modeQrAuto: '📱 تعيين QR تلقائي',
            strategyLabel: 'الاستراتيجية:',
            qrAutoHint: 'سيتم تعيين الضيوف تلقائيًا لطاولة عند تسجيل حضورهم عبر رمز QR الشخصي أو لوحة QR الرئيسية',
            qrAutoForwardNote: 'ينطبق فقط على الضيوف الذين يسجلون حضورهم بعد تفعيل هذا الوضع',
            qrAutoRequiresCheckin: 'فعّل QR تسجيل الحضور في إعدادات النموذج لاستخدام هذا الوضع',
            exportSeating: 'تصدير',
            downloadCSV: '📥 تحميل CSV',
            printView: '🖨️ عرض الطباعة',
            noAssignmentsToExport: 'لا توجد تعيينات طاولة للتصدير',
            undo: 'تراجع', redo: 'إعادة',
            keepTogether: 'معاً', keepApart: 'منفصلين',
            addRule: 'إضافة قاعدة', ruleConflict: '⚠️ تعارض',
            selectGuest: '— اختر ضيفاً —',
            editTags: 'تعديل الوسوم',
            tagsHint: 'جمّع الضيوف حسب الوسوم المشتركة (مثل أصدقاء الجامعة، جهة العروس). أنت وحدك من يراها.',
            memberTagsHint: 'وسوم لهذا الضيف فقط — بقية المجموعة لا تتأثر. أنت وحدك من يراها.',
            addTagPlaceholder: 'اكتب وسماً واضغط Enter',
            saveTags: 'حفظ الوسوم',
            tagsSaved: 'تم تحديث الوسوم',
            tagsSaveFailed: 'تعذّر حفظ الوسوم',
            chartSaveFailed: 'تعذّر حفظ مخطط الجلوس. تغييراتك ما زالت موجودة — حاول مرة أخرى.',
            noTagsYet: 'لا توجد وسوم بعد',
            bulkTag: 'تحديد للوسم', bulkDone: 'تم',
            bulkTagSelected: 'وسم المحدد', bulkClearSel: 'مسح',
            selectMultiple: 'حدد ضيوفًا لوسمهم', selectDone: 'تم', selectMultipleHint: 'اضغط على الضيوف لوسم عدة دفعة واحدة',
            bulkTitle: 'وسم الضيوف المحددين',
            bulkHint: 'حدّد وسمًا لإضافته إلى جميع المحددين؛ ألغِ التحديد لإزالته. الشرطة تعني أن بعضهم فقط لديه هذا الوسم.',
            bulkApply: 'تطبيق', bulkApplied: 'تم تحديث الوسوم', bulkSelectGuests: 'اختر ضيفًا واحدًا على الأقل',
            numberOfTables: 'عدد الطاولات',
            addTables: 'إضافة طاولات',
            renameTableTitle: 'انقر لإعادة التسمية',
            dragToReorder: 'اسحب لإعادة الترتيب',
            collapseTable: 'طي',
            expandTable: 'توسيع',
            collapseAll: 'طي جميع الطاولات',
            expandAll: 'توسيع جميع الطاولات',
            filterByCategoryHint: 'انقر للتصفية حسب الفئة',
            clearFilter: 'مسح',
            filterBtn: 'تصفية',
            statusFilterHeading: 'حالة الرد',
            categoryFilterHeading: 'الفئة',
            statusComing: 'سيحضرون',
            statusMaybe: 'ربما',
            statusAwaiting: 'بانتظار الرد',
            moreActions: 'إجراءات إضافية',
            expandAll: 'توسيع الكل',
            collapseAll: 'طيّ الكل',
            deleteCategoryHint: 'حذف هذه الفئة',
            changeCategoryColorHint: 'انقر لتغيير لون هذه الفئة',
            deleteCategoryConfirm: 'حذف الفئة "{name}"؟ يؤدي ذلك إلى إزالة الوسم "{name}" من {count} ضيف. يتم الاحتفاظ بتعيينات المقاعد.',
            categoryDeleted: 'تم حذف الفئة "{name}"',
            categoryDeleteFailed: 'تعذّر حذف الفئة. يرجى المحاولة مرة أخرى.',
            noMatchingGuests: 'لا يوجد ضيوف يطابقون عامل التصفية الحالي',
            emptySeats: 'فارغة',
            noCategoryLabel: 'بدون فئة',
            dragCategoryHint: 'اسحب هذه الفئة إلى طاولة لإجلاسهم معاً',
            dragGroupHint: 'اسحب لنقل المجموعة بأكملها',
            assignTo: 'تعيين إلى…',
            partialFillToast: 'تم إجلاس {placed} من {total}. {remaining} لم يتسعوا — اختر طاولة أخرى.',
            seatedAllToast: 'تم إجلاس {count} ضيوف.',
            childGuest: 'طفل',
            maybeGuest: 'ربما يحضر',
            kidsAtTable: '{count} طفل/أطفال على هذه الطاولة',
            maybeAtTable: '{count} ضيف/ضيوف ربما يحضرون على هذه الطاولة',
            mayOverflow: 'قد تمتلئ الطاولة عند وصول الجميع: {count} متوقعون بعد، {free} مقعد/مقاعد شاغرة',
            stillToCome: 'لم يصلوا بعد: {names}',
            markAsChild: 'وضع علامة كطفل',
            markAsAdult: 'وضع علامة كبالغ',
            kidLabel: 'طفل',
            groupSizeHint: 'حجم المجموعة',
            customSeat: 'مخصص',
            customSeatHint: 'يجلس بعيداً عن بقية المجموعة',
            relationshipLegend: 'مفتاح الفئات',
            noRelationship: 'بدون فئة',
            tableDescription: 'وصف (داخلي)',
            tableDescriptionPlaceholder: 'مثال: بجانب حلبة الرقص — تراه أنت فقط',
            tableDescriptionHint: 'ملاحظة خاصة بك. لا تُرسَل أبدًا إلى الضيوف.',
            typeTableHint: 'اكتب رقم الطاولة لإجلاس هذا الضيف',
            tableNotFound: 'لا توجد طاولة باسم "{value}". اكتب رقم أو اسم طاولة موجودة.',
            tableAmbiguous: 'تتطابق عدة طاولات مع "{value}". اكتب اسم الطاولة كاملًا.'
        }
    };

    static LEGEND_LABELS = {
        en: { title: 'Filter by category', noRel: 'No category' },
        es: { title: 'Filtrar por categoría', noRel: 'Sin categoría' },
        fr: { title: 'Filtrer par catégorie', noRel: 'Sans catégorie' },
        pt: { title: 'Filtrar por categoria', noRel: 'Sem categoria' },
        de: { title: 'Nach Kategorie filtern', noRel: 'Ohne Kategorie' },
        he: { title: 'סינון לפי קטגוריה', noRel: 'ללא קטגוריה' },
        ar: { title: 'تصفية حسب الفئة', noRel: 'بدون فئة' }
    };

    static RELATIONSHIP_COLORS = [
        '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
        '#00BCD4', '#FF5722', '#607D8B', '#795548', '#E91E63'
    ];

    constructor() {
        this.formId = null;
        this.config = { tables: [], rules: { keepTogether: [], keepApart: [] }, defaultStrategy: 'manual', tagColors: {} };
        this.allAttendingResponses = [];
        this.selectedStrategy = 'manual';
        this.isInitialized = false;
        this.isDirty = false;
        this.searchFilter = '';
        this.currentFormData = null;
        this.lang = window.DASHBOARD_LANGUAGE || 'en';
        this.undoStack = [];
        this.redoStack = [];
        this._relColorMap = new Map();
        // Translated relationship answer (lowercased) → the host's own wording.
        // Empty until _buildRelationshipCanonMap() runs; an empty map simply
        // means categories group by raw answer, as they did before.
        this._relCanon = new Map();
        // Real-time listener handles
        this._unsubConfig = null;
        this._unsubResponses = null;
        // Flag to suppress snapshot handling during local saves
        this._isSaving = false;
        // Per-tab client ID, stamped onto saves (updatedByClient) so the
        // realtime listener can tell THIS tab's own echo from a change made
        // on another device/tab — which may be the same signed-in account.
        this._clientId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        // Collapse state is a local view preference (per form, in localStorage).
        // Not persisted to Firestore — different users may want different views.
        this._collapsedTables = new Set();
        // Active reorder drag — null when no table is being dragged for reorder.
        this._reorderTableId = null;
        // Active category filter — set of lowercased relationship keys to include.
        // Empty set means "no filter, show all". Combined AND with searchFilter.
        this.categoryFilter = new Set();
        // Active reply-status filter ('yes' | 'maybe' | 'awaiting', see
        // _replyStatus). The pool holds everyone not declined, so a host who
        // seats only confirmed guests needs to hide the awaiting-reply ones
        // (Soller, 2026-11-01). Empty = show all; ANDed with the others.
        this.statusFilter = new Set();
        // Consolidated sidebar controls (task #89): the category pills moved
        // into a Filter dropdown, and Expand/Collapse-all live in a ⋯ menu.
        // Open-state survives re-renders (each filter pick re-renders the list).
        this._filterMenuOpen = false;
        this._moreMenuOpen = false;
        // Per-form view preference: collapsed category sections in the unassigned panel.
        this._collapsedSections = new Set();
        // Legacy member-level multi-select (kept declared; selection now lives in
        // `_bulkSelected`). Keys are `${responseId}::${guestName}`.
        this._selectedMembers = new Set();
        // Bulk selection lives behind a "Select" toggle so a guest row has only
        // ONE tagging control by default (the per-row tag icon). Turning Select on
        // reveals per-row checkboxes; a sticky action bar then auto-appears once
        // ≥1 party is selected, to tag OR drag-assign them all at once.
        this._bulkSelected = new Set();
        this._selectMode = false;
        // Per-party expand state (task #91): the unassigned guest list shows each
        // multi-member party as a COLLAPSED tree by default — only the party
        // leader (header) is visible until the host expands it. Tracks which
        // parties are expanded (by responseId); empty = everything collapsed.
        // Deliberately NOT persisted, so every load starts fully collapsed.
        this._expandedParties = new Set();
    }

    t(key) {
        const strings = DashboardSeating.TRANSLATIONS[this.lang] || DashboardSeating.TRANSLATIONS['en'];
        return strings[key] || DashboardSeating.TRANSLATIONS['en'][key] || key;
    }

    // Firebase restores the persisted session asynchronously, so currentUser is
    // null for the first ~0.5-2s after load. Reading it synchronously meant the
    // chart read went out as the literal string "Bearer undefined", which
    // verifyToken rejects with a 401 — and on the demo form that also defeated
    // allowDemoFormRead, whose whole job is to answer a TOKENLESS GET, so a
    // signed-out prospect opening the Seating tab got 401 and an empty chart.
    // Fast-path a live user, else wait for the first definitive auth state with
    // a bounded fallback so we never hang; send no header at all when genuinely
    // signed out, which is what lets the demo read succeed.
    async _authHeaders() {
        // The demo dashboard is view-only. A signed-in visitor's real token
        // would be checked against the demo form's owner and refused (403),
        // leaving the demo chart empty; no token takes the public demo read.
        if (window.isDemoMode) return {};
        const auth = (typeof firebase !== 'undefined' && firebase.auth)
            ? firebase.auth()
            : window.firebaseAuth;
        if (!auth) return {};
        let user = auth.currentUser;
        if (!user) {
            user = await new Promise(resolve => {
                let settled = false;
                let unsub = null;
                const done = u => {
                    if (settled) return;
                    settled = true;
                    try { if (unsub) unsub(); } catch (e) {}
                    resolve(u || null);
                };
                try {
                    unsub = auth.onAuthStateChanged(u => done(u), () => done(null));
                } catch (e) { done(null); return; }
                setTimeout(() => done(auth.currentUser || null), 4000);
            });
        }
        if (!user) return {};
        try {
            return { 'Authorization': `Bearer ${await user.getIdToken()}` };
        } catch (e) {
            return {};
        }
    }

    isPro(formData) {
        // Seating is a Signature-tier feature. FeatureGates is loaded by
        // dashboard.html before this script — if it's missing, fail closed
        // (lock the tab) rather than fall back to a legacy basic.paid check
        // which would leak seating to post-launch Event Pro Lite forms.
        // The demo shows the real thing, as Check-In does: a prospect met an
        // upgrade lock over an empty guest list.
        if (window.isDemoMode) return true;
        if (window.FeatureGates?.canUse) {
            return window.FeatureGates.canUse(formData, 'seating');
        }
        return false;
    }

    updateTabVisibility(formData) {
        this.currentFormData = formData;
        // Seating tab is always visible
        const tabNav = document.getElementById('seating-tab-nav');
        if (tabNav) tabNav.style.display = '';

        // Update upgrade overlay
        this.updateUpgradeOverlay(formData);

        // Refresh QR banner (depends on form's check-in setting)
        this.updateQrAssignBanner();

        // admin.js calls this right after it fills allResponses. A Seating tab
        // opened first (a &tab=seating link, the support bot's "open seating")
        // took its guests from the still-empty list and said "No attending
        // guests found" until the host left the tab and came back.
        if (this.formId) {
            this.loadAttendingResponses();
            if (!this.isInitialized) return;
            if (this._reconcileSeatNames()) {
                this.autoSave();
                this.render();
            } else {
                this.renderUnassignedList();
                this.updateUnassignedCounter();
            }
        }
    }

    updateUpgradeOverlay(formData) {
        const overlay = document.getElementById('seatingUpgradeOverlay');
        if (!overlay) return;

        if (!this.isPro(formData)) {
            overlay.style.display = '';
            overlay.innerHTML = `
                <div style="display:flex;align-items:center;justify-content:center;padding:80px 20px 40px;border-radius:8px;">
                    <div class="text-center" style="max-width:380px;">
                        <div style="width:64px;height:64px;margin:0 auto 16px;border-radius:50%;background:linear-gradient(135deg,#16243F,#a18bc4);display:flex;align-items:center;justify-content:center;">
                            <i class="bi bi-lock-fill text-white" style="font-size:1.8rem;"></i>
                        </div>
                        <h5 class="fw-bold" style="color:#3a3150;">${this.t('upgradeTitle')}</h5>
                        <p class="text-muted small mb-3">${this.t('upgradeDesc')}</p>
                        <button onclick="triggerHeaderUpgrade()" class="btn btn-primary px-4" style="background:linear-gradient(135deg,#16243F,#a18bc4);border:none;">
                            <i class="bi bi-arrow-up-circle me-1"></i>${this.t('upgradeCta')}
                        </button>
                    </div>
                </div>`;
        } else {
            overlay.style.display = 'none';
            overlay.innerHTML = '';
        }
    }

    async init(formId) {
        if (!formId) {
            this.showNoFormError();
            return;
        }

        // Source the access check from a fresh Firestore read. currentFormData
        // is set by updateTabVisibility(), which runs only after responses load
        // — on tab activation or form-switch it can still hold the previous
        // form's data and wrongly show the upgrade overlay for paid users.
        let formData = this.currentFormData;
        try {
            if (window.formStorage?.db) {
                const snap = await window.formStorage.db
                    .collection('rsvp_forms').doc(formId).get();
                if (snap.exists) formData = snap.data();
            }
        } catch (e) {
            console.warn('Seating: form fetch failed, falling back to cached data', e);
        }
        this.currentFormData = formData;

        // Always sync overlay visibility with the freshly-resolved tier.
        this.updateUpgradeOverlay(formData);

        if (formData && !this.isPro(formData)) {
            const loading = document.getElementById('seatingLoadingState');
            if (loading) loading.style.display = 'none';
            const main = document.getElementById('seatingMainContent');
            if (main) main.style.display = 'none';
            return;
        }

        // Clean up previous listeners if switching forms
        this.destroy();

        this.formId = formId;
        // Until this form's chart is loaded, updateTabVisibility must not
        // reconcile (and autosave) the previous form's chart against it.
        this.isInitialized = false;
        this.showLoading();

        try {
            this.loadAttendingResponses();

            // Must resolve BEFORE the first render, or a multilingual form draws
            // its categories split and only settles on a later repaint.
            await this._buildRelationshipCanonMap();

            const res = await fetch(`/api/seating/${formId}`, {
                headers: await this._authHeaders()
            });
            const data = await res.json();
            if (data.success) {
                this.config = data.config;
            } else {
                this.config = { tables: [], rules: { keepTogether: [], keepApart: [] } };
            }

            this._loadCollapseState();
            this._applyMobileTableCollapseDefault();
            // Repair seat names a Responses-tab rename left stale, and persist
            // so exports/print and other devices see the corrected chart too.
            if (this._reconcileSeatNames()) this.autoSave();
            this.render();
            this.setupEventListeners();
            this.isInitialized = true;

            // Start real-time listeners for seating config + responses
            this._startRealtimeListeners(formId);
        } catch (error) {
            console.error('Error initializing seating tab:', error);
            this.showError(this.t('failedToLoad'));
        }
    }

    /**
     * Start Firestore onSnapshot listeners for live seating updates.
     * - Config listener: picks up seat changes from QR auto-assign
     * - Responses listener: picks up new check-ins so the guest list updates
     */
    _startRealtimeListeners(formId) {
        // The demo is view-only and its visitor is usually signed out: the rules
        // refuse both listeners, and nothing on the demo changes to listen for.
        // The chart itself came from the public HTTP read above.
        if (window.isDemoMode) return;
        const db = firebase.firestore();
        let configFirstSnapshot = true;
        let responsesFirstSnapshot = true;

        // 1. Listen to seating config doc for table assignment changes
        this._unsubConfig = db.collection('rsvp_forms').doc(formId)
            .collection('seating').doc('config')
            .onSnapshot((docSnap) => {
                // Skip the initial snapshot — we already loaded via HTTP
                if (configFirstSnapshot) { configFirstSnapshot = false; return; }

                // Skip if we're in the middle of a local save to avoid re-render loops
                if (this._isSaving) return;

                if (!docSnap.exists) return;
                const incoming = docSnap.data();

                // Skip our own save echoing back: the server stamps each save
                // with the saving tab's clientId. Do NOT compare uid here —
                // the same account on another computer shares the uid, so a
                // uid check swallows legitimate cross-device changes (they
                // only showed up after a manual refresh).
                if (incoming.updatedByClient && incoming.updatedByClient === this._clientId) {
                    return;
                }

                // Rule pairs are stored as {a, b} (Firestore refuses an array
                // inside an array); this tab works in [a, b], as GET returns them.
                if (incoming.rules) {
                    for (const k of ['keepTogether', 'keepApart']) {
                        if (Array.isArray(incoming.rules[k])) {
                            incoming.rules[k] = incoming.rules[k].map(p =>
                                (p && typeof p === 'object' && !Array.isArray(p) ? [p.a, p.b] : p));
                        }
                    }
                }

                // External change (e.g. QR auto-assign from server) — update config and re-render
                this.config = incoming;
                // Incoming config may predate a guest rename; converges (the
                // repaired save echoes back and reconciles to a no-op).
                if (this._reconcileSeatNames()) this.autoSave();
                this.render();
            }, (error) => {
                console.error('Seating config snapshot error:', error);
            });

        // 2. Listen to responses collection for check-in status changes.
        // Bound with limit() — same rationale as check-in. 1000 covers all
        // realistic event sizes; raise + add pagination for >1000 guests.
        const SEATING_LISTENER_LIMIT = 1000;
        // The first load comes from the Responses tab's capped list; every later
        // snapshot reads every row, so apply the same cap here or the first
        // guest edit would put hidden replies on the seating chart.
        const rlm = window.ResponseLimitManager;
        const capPromise = rlm ? rlm.capFor(formId) : Promise.resolve(Infinity);
        // orderBy `timestamp` (not `submittedAt`) — legacy responses written
        // before `submittedAt` was added only have `timestamp`, and Firestore's
        // orderBy silently skips docs missing the indexed field. Using
        // `timestamp` matches getFormResponses() and keeps the full list intact.
        this._unsubResponses = db.collection('rsvp_forms').doc(formId)
            .collection('responses')
            .orderBy('timestamp', 'desc')
            .limit(SEATING_LISTENER_LIMIT)
            .onSnapshot(async (snapshot) => {
                // Skip the initial snapshot — we already loaded responses
                if (responsesFirstSnapshot) { responsesFirstSnapshot = false; return; }

                // Rebuild the allAttendingResponses from the snapshot directly
                const rows = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                const visible = rlm ? rlm.constructor.applyCap(rows, await capPromise).visible : rows;
                this._setAnsweredPredicate(visible);
                const responses = [];
                visible.forEach(data => {
                    if (data.attending !== false && data.attending !== 'no' && data.attending !== 'No'
                        && data.approvalStatus !== 'pending') {
                        responses.push(data);
                    }
                });

                this.allAttendingResponses = responses;

                // A guest rename arrives here — re-link any seats it orphaned.
                // When that happens the tables changed too, so re-render fully.
                if (this._reconcileSeatNames()) {
                    this.autoSave();
                    this.render();
                } else {
                    // Re-render unassigned list + counters (tables stay the same)
                    this.renderUnassignedList();
                    this.updateUnassignedCounter();
                }
            }, (error) => {
                console.error('Seating responses snapshot error:', error);
            });
    }

    /**
     * Clean up real-time listeners
     */
    destroy() {
        if (this._unsubConfig) {
            this._unsubConfig();
            this._unsubConfig = null;
        }
        if (this._unsubResponses) {
            this._unsubResponses();
            this._unsubResponses = null;
        }
        // Leaving the seating view: clear any selection and hide its sticky bar so
        // it never lingers over another tab.
        this._bulkSelected.clear();
        const bar = document.getElementById('seatingBulkBar');
        if (bar) bar.style.display = 'none';
    }

    // "Has this guest answered?" needs every row of the form (its upload-batch
    // test), so it is built from the full list, not the seated subset.
    _setAnsweredPredicate(rows) {
        const fg = window.FeatureGates;
        this._answered = fg && typeof fg.answeredPredicate === 'function'
            ? fg.answeredPredicate(rows)
            : null;
    }

    // The guest's status as the Responses tab shows it: an uploaded guest who
    // has not answered (or a row still 'pending') is "Awaiting reply" there,
    // so it is here too.
    _replyStatus(response) {
        if (!response) return 'yes';
        if (response.attending === 'pending' || (this._answered && !this._answered(response))) return 'awaiting';
        return this._isMaybeResponse(response) ? 'maybe' : 'yes';
    }

    _passesStatusFilter(response) {
        return this.statusFilter.size === 0 || this.statusFilter.has(this._replyStatus(response));
    }

    loadAttendingResponses() {
        if (typeof allResponses !== 'undefined' && allResponses.length > 0) {
            this._setAnsweredPredicate(allResponses);
            this.allAttendingResponses = allResponses.filter(
                r => r.attending !== false && r.attending !== 'no' && r.attending !== 'No'
                    && r.approvalStatus !== 'pending'
            );
        } else {
            this.allAttendingResponses = [];
        }
    }

    // --- UI State ---

    showLoading() {
        const loading = document.getElementById('seatingLoadingState');
        const content = document.getElementById('seatingMainContent');
        const noForm = document.getElementById('seatingNoFormError');
        if (loading) loading.style.display = '';
        if (content) content.style.display = 'none';
        if (noForm) noForm.style.display = 'none';
    }

    showNoFormError() {
        const loading = document.getElementById('seatingLoadingState');
        const content = document.getElementById('seatingMainContent');
        const noForm = document.getElementById('seatingNoFormError');
        if (loading) loading.style.display = 'none';
        if (content) content.style.display = 'none';
        if (noForm) noForm.style.display = '';
    }

    showError(msg) {
        const loading = document.getElementById('seatingLoadingState');
        const content = document.getElementById('seatingMainContent');
        if (loading) loading.style.display = 'none';
        if (content) {
            content.style.display = '';
            content.innerHTML = `<div class="text-center py-4 text-danger">
                <i class="bi bi-exclamation-triangle" style="font-size: 2rem;"></i>
                <p class="mt-2">${msg}</p>
            </div>`;
        }
    }

    // --- Rendering ---

    // Determine "override" seats: a party (group) is normally seated together, so
    // any member placed at a DIFFERENT table than the bulk of their party is an
    // individual override ("Custom"). Populates:
    //   this._overrideSeatKeys  — Set of "responseId::guestName" for override seats
    //   this._splitPartyIds     — Set of responseIds whose party is split across tables
    // The "home" table is where most of the party sits; ties go to the party
    // leader's (primary member's) table. A solo seated guest, or a party all at one
    // table, never counts as an override.
    _computeSeatingOverrides() {
        this._overrideSeatKeys = new Set();
        this._splitPartyIds = new Set();

        const byParty = new Map(); // responseId -> [{ tableId, guestName }]
        for (const table of (this.config.tables || [])) {
            for (const seat of (table.seats || [])) {
                if (!byParty.has(seat.responseId)) byParty.set(seat.responseId, []);
                byParty.get(seat.responseId).push({ tableId: table.id, guestName: seat.guestName });
            }
        }

        for (const [responseId, seats] of byParty) {
            if (seats.length < 2) continue; // solo seated guest can't override a party
            const tableCounts = new Map();
            for (const s of seats) tableCounts.set(s.tableId, (tableCounts.get(s.tableId) || 0) + 1);
            if (tableCounts.size < 2) continue; // whole party at one table → no override

            // Home table = the most-populated table for this party.
            let homeTable = null, best = -1;
            for (const [tid, count] of tableCounts) {
                if (count > best) { best = count; homeTable = tid; }
            }
            // Tie-break: if the party leader's table also has the max count, prefer it.
            const resp = this.allAttendingResponses.find(r => r.id === responseId);
            // The main guest's own seat (task #965: not always the reply's name).
            const primaryName = resp ? this.buildMembersForResponse(resp)[0].guestName : null;
            const primarySeat = primaryName ? seats.find(s => s.guestName === primaryName) : null;
            if (primarySeat && tableCounts.get(primarySeat.tableId) === best) {
                homeTable = primarySeat.tableId;
            }

            for (const s of seats) {
                if (s.tableId !== homeTable) this._overrideSeatKeys.add(`${responseId}::${s.guestName}`);
            }
            this._splitPartyIds.add(responseId);
        }
    }

    render() {
        document.getElementById('seatingLoadingState').style.display = 'none';
        document.getElementById('seatingMainContent').style.display = '';

        // Work out which seated guests are "overrides" (placed at a different table
        // than the rest of their party) BEFORE rendering, so both the table cards
        // and the sidebar can flag them. (task #17)
        this._computeSeatingOverrides();

        this.renderUnassignedList();
        this.renderTables();
        this.updateUnassignedCounter();
        this.syncModeUI();
        this.renderRulesPanel();
        this.updateUndoRedoButtons();
        this._syncStickyOffset();
    }

    // The seating toolbar is sticky at the very top and opaque (z-index 101); the
    // Unassigned panel sticks just beneath it. CSS can't read another element's
    // height, and the toolbar grows when it wraps at narrower widths, so measure it
    // here and expose it as the `--seating-sticky-top` variable that drives the
    // panel's sticky `top` + max-height. Without this the panel slid under the
    // toolbar and its search bar got hidden when scrolling down through the tables.
    _syncStickyOffset() {
        const layout = document.getElementById('seatingLayout');
        if (!layout) return;
        const toolbar = document.querySelector('.seating-toolbar');
        const h = toolbar ? Math.ceil(toolbar.getBoundingClientRect().height) : 0;
        // +8px breathing room between the toolbar and the panel.
        layout.style.setProperty('--seating-sticky-top', (h + 8) + 'px');
        // Also expose it on :root so the body-appended bulk-tag bar (which is
        // NOT a descendant of #seatingLayout) can dock just under the toolbar.
        document.documentElement.style.setProperty('--seating-sticky-top', (h + 8) + 'px');
    }

    syncModeUI() {
        const mode = this.config.assignmentMode || 'manual';
        const strategy = this.config.defaultStrategy || 'roundrobin';
        // QR Auto-Assign is available to all Pro users — it works via the Master QR sign
        // without requiring individual QR check-in to be enabled in the form builder.
        const canUseQrAuto = this.isPro(this.currentFormData);
        const effectiveMode = this.config.assignmentMode || 'manual';

        // Update pill button active state
        const manualBtn = document.getElementById('seatingModeManual');
        const qrAutoBtn = document.getElementById('seatingModeQrAuto');
        if (manualBtn && qrAutoBtn) {
            manualBtn.classList.toggle('active', effectiveMode === 'manual');
            qrAutoBtn.classList.toggle('active', effectiveMode === 'qr_auto');
            qrAutoBtn.disabled = !canUseQrAuto;
            qrAutoBtn.style.opacity = '';
            qrAutoBtn.style.cursor = '';
            qrAutoBtn.title = '';
        }

        // Show/hide strategy dropdown
        const strategyGroup = document.getElementById('seatingStrategyGroup');
        if (strategyGroup) {
            strategyGroup.style.display = effectiveMode === 'qr_auto' ? '' : 'none';
        }
        const strategySelect = document.getElementById('seatingStrategySelect');
        if (strategySelect) {
            strategySelect.value = strategy;
        }

        // Show/hide Auto-Assign button (only in manual mode)
        const autoAssignBtn = document.getElementById('seatingAutoAssignBtn');
        if (autoAssignBtn) {
            autoAssignBtn.style.display = effectiveMode === 'manual' ? '' : 'none';
        }

        // Update QR banner
        this.updateQrAssignBanner();
    }

    updateQrAssignBanner() {
        const banner = document.getElementById('seatingQrBanner');
        if (!banner) return;

        const mode = this.config.assignmentMode || 'manual';
        if (mode !== 'qr_auto') {
            banner.innerHTML = '';
            return;
        }

        banner.innerHTML = `
            <div class="alert alert-success alert-sm py-2 px-3 mb-0 small">
                <div class="d-flex align-items-center"><i class="bi bi-qr-code-scan me-2"></i><span>${this.t('qrAutoHint')}</span></div>
                <div class="text-muted mt-1" style="font-size:0.8em;padding-left:1.5rem;"><i class="bi bi-info-circle me-1"></i>${this.t('qrAutoForwardNote')}</div>
            </div>`;
    }

    getAssignedResponseIds() {
        const assigned = new Set();
        for (const table of this.config.tables) {
            for (const seat of (table.seats || [])) {
                assigned.add(seat.responseId);
            }
        }
        return assigned;
    }

    // Returns Set of 'responseId::guestName' for all seated members
    getAssignedMemberKeys() {
        const keys = new Set();
        for (const table of this.config.tables) {
            for (const seat of (table.seats || [])) {
                keys.add(`${seat.responseId}::${seat.guestName}`);
            }
        }
        return keys;
    }

    getRelationshipColor(relationship) {
        if (!relationship) return null;
        const key = relationship.trim().toLowerCase();
        if (!key) return null;
        // A host-chosen custom color for this category always wins over the
        // auto-assigned palette color.
        const custom = this.config && this.config.tagColors && this.config.tagColors[key];
        if (custom) return custom;
        if (!this._relColorMap.has(key)) {
            const idx = this._relColorMap.size % DashboardSeating.RELATIONSHIP_COLORS.length;
            this._relColorMap.set(key, DashboardSeating.RELATIONSHIP_COLORS[idx]);
        }
        return this._relColorMap.get(key);
    }

    _titleCaseTag(s) {
        return String(s || '').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    // ── Relationship answers across languages ─────────────────────────────
    //
    // A guest's relationship is stored as the option text in the language THEY
    // answered in, so on a multilingual form "Friend", "Freund", "חבר" and
    // "Друг" are four different strings for one category — four legend entries,
    // four colours, and four separate groups in the unassigned panel. Measured
    // on live forms: 7 of 51 multilingual forms are split this way, one of them
    // four ways on a single category.
    //
    // Resolved at READ time rather than by rewriting responses: a backfill would
    // touch guest records to fix a display concern, and could not help the 26%
    // of answers that resolve to nothing anyway (renamed options, imports).
    //
    // Two sources, in order of trust:
    //   1. `relationshipOriginal` — the host's own wording, recorded on the
    //      response itself. Only present going forward.
    //   2. The form's translation docs, which say which source option each
    //      translated string came from. Covers every historical response.
    // Anything that resolves to neither keeps exactly the behaviour it has
    // today: its own text, its own category. Better a category we can't merge
    // than a guest silently filed under the wrong one.
    async _buildRelationshipCanonMap() {
        this._relCanon = new Map();
        const fd = this.currentFormData || {};
        const opts = (Array.isArray(fd.relationshipOptions) ? fd.relationshipOptions : [])
            .map(o => String(o).trim()).filter(Boolean);
        if (!opts.length) return;

        // The host's own options are their own canonical form.
        opts.forEach(o => this._relCanon.set(o.toLowerCase(), o));

        // Only multilingual forms can be split, so only they pay for the read.
        const langs = Array.isArray(fd.enabledLanguages) ? fd.enabledLanguages.filter(Boolean) : [];
        const source = fd.defaultLanguage || fd.lang || 'en';
        if (!langs.some(l => l !== source)) return;

        try {
            const snap = await window.formStorage.db
                .collection('rsvp_forms').doc(this.formId)
                .collection('translations').get();
            snap.forEach(doc => {
                const content = (doc.data() || {}).content || {};
                Object.keys(content).forEach(k => {
                    const m = k.match(/^options\.relationship_(\d+)$/);
                    if (!m) return;
                    const canonical = opts[parseInt(m[1], 10) - 1];
                    const translated = String(content[k] || '').trim();
                    if (canonical && translated) this._relCanon.set(translated.toLowerCase(), canonical);
                });
            });
        } catch (e) {
            // Never block the seating chart on this — an unresolved map just
            // means categories group the way they did before.
            console.warn('Seating: could not load relationship translations', e);
        }
    }

    /** The host's own wording for a guest's relationship, or their raw answer. */
    _canonicalRelationship(response) {
        const raw = ((response && response.relationship) || '').trim();
        if (!raw) return '';
        const original = ((response && response.relationshipOriginal) || '').trim();
        if (original) return original;
        return (this._relCanon && this._relCanon.get(raw.toLowerCase())) || raw;
    }

    // The category a guest is grouped under in the unassigned panel + legend.
    // A seating tag wins (so tagging a guest moves them into that category
    // section and out of Uncategorized), falling back to their RSVP
    // relationship answer, then Uncategorized. Tags are stored lowercased by
    // the server, so the first tag is title-cased for the section heading.
    getCategoryForResponse(response) {
        const hasTagsField = !!(response && Array.isArray(response.seatingTags));
        const tags = hasTagsField ? response.seatingTags.filter(Boolean) : [];
        if (tags.length > 0) {
            const tag = String(tags[0]).trim();
            if (tag) return { key: tag.toLowerCase(), label: this._titleCaseTag(tag) };
        }
        // An EXPLICIT empty seatingTags array means the host cleared this guest's
        // tags on purpose — honour that and move them to Uncategorized instead of
        // silently re-bucketing them by their RSVP relationship (which made removing
        // the last tag look like it did nothing). Guests that were NEVER tagged (no
        // seatingTags field at all) still fall back to their relationship grouping.
        if (!hasTagsField) {
            // Guests who answered in different languages belong in ONE category,
            // labelled in the host's own wording rather than whichever guest
            // happened to be rendered first.
            const rel = this._canonicalRelationship(response);
            if (rel) return { key: rel.toLowerCase(), label: rel };
        }
        return { key: '__nocategory__', label: this.t('noCategoryLabel') };
    }

    renderRelationshipLegend() {
        // Walk every attending response so colors are assigned consistently regardless of
        // current assignment state — the legend should never change when a guest is seated.
        const seen = new Map(); // category key → { label, color }
        for (const r of (this.allAttendingResponses || [])) {
            const cat = this.getCategoryForResponse(r);
            if (cat.key === '__nocategory__') continue;
            const color = this.getRelationshipColor(cat.key);
            if (color && !seen.has(cat.key)) seen.set(cat.key, { label: cat.label, color });
        }

        // A category is "deletable" only when it comes from a seating tag the
        // host added (removing it just clears that tag from the guests). Plain
        // RSVP-relationship categories aren't deletable — they're the guest's
        // own answer, not something the host created here.
        const tagKeys = new Set();
        for (const r of (this.allAttendingResponses || [])) {
            const tags = Array.isArray(r.seatingTags) ? r.seatingTags : [];
            tags.forEach(t => { const k = String(t || '').trim().toLowerCase(); if (k) tagKeys.add(k); });
        }

        const labels = DashboardSeating.LEGEND_LABELS[this.lang] || DashboardSeating.LEGEND_LABELS.en;
        const anyActive = this.categoryFilter.size > 0 || this.statusFilter.size > 0;

        // Reply statuses present in the pool. The section only appears when
        // there is a choice to make (or one is already ticked).
        const statusCounts = { yes: 0, maybe: 0, awaiting: 0 };
        for (const r of (this.allAttendingResponses || [])) statusCounts[this._replyStatus(r)]++;
        const statusKeys = ['yes', 'maybe', 'awaiting']
            .filter(k => statusCounts[k] > 0 || this.statusFilter.has(k));
        const showStatus = statusKeys.length > 1 || this.statusFilter.size > 0;
        const statusLabel = { yes: 'statusComing', maybe: 'statusMaybe', awaiting: 'statusAwaiting' };
        const headingStyle = 'padding:6px 8px 2px;font-size:.7rem;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:#6b7280;';

        // Consolidated controls (task #89): categories live inside ONE Filter
        // dropdown (select/deselect; color + delete affordances kept per row),
        // and Expand/Collapse-all live in a ⋯ menu — no pill row, no top ×'s.
        const menuRow = ([key, info]) => {
            const active = this.categoryFilter.has(key);
            const deletable = tagKeys.has(key);
            const delBtn = deletable ? `
                    <button type="button" class="btn btn-sm p-0 px-1 text-muted"
                        data-action="delete-category"
                        data-category="${this.escapeAttr(key)}"
                        title="${this.t('deleteCategoryHint')}"
                        aria-label="${this.t('deleteCategoryHint')}"
                        style="line-height:1;font-size:.7rem;">
                        <i class="bi bi-x-lg"></i>
                    </button>` : '';
            return `
                <span class="seating-legend-chip-group d-flex align-items-center" style="border-radius:7px;">
                    <button type="button" class="seating-filter-item${active ? ' active' : ''}"
                        data-action="filter-category"
                        data-category="${this.escapeAttr(key)}"
                        title="${this.t('filterByCategoryHint')}"
                        style="flex:1;display:flex;align-items:center;gap:8px;padding:6px 8px;background:${active ? '#eef3ff' : 'none'};border:none;border-radius:7px;font-size:.85rem;color:#1f2937;text-align:start;cursor:pointer;min-width:0;">
                        <span class="seating-legend-dot" style="background:${info.color};width:11px;height:11px;border-radius:50%;flex-shrink:0;display:inline-block;"
                            data-color-cat="${this.escapeAttr(key)}"
                            title="${this.t('changeCategoryColorHint')}"></span>
                        <span class="text-truncate" style="flex:1;">${this.escapeHtml(info.label)}</span>
                        ${active ? '<i class="bi bi-check-lg text-primary" style="flex-shrink:0;"></i>' : ''}
                    </button><input type="color" class="seating-legend-color-input"
                        data-category="${this.escapeAttr(key)}" value="${info.color}"
                        aria-label="${this.t('changeCategoryColorHint')}"
                        style="position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;">${delBtn}
                </span>
            `;
        };
        const statusRow = (key) => {
            const active = this.statusFilter.has(key);
            return `
                <button type="button" class="seating-filter-item${active ? ' active' : ''}"
                    data-action="filter-status" data-status="${key}"
                    style="width:100%;display:flex;align-items:center;gap:8px;padding:6px 8px;background:${active ? '#eef3ff' : 'none'};border:none;border-radius:7px;font-size:.85rem;color:#1f2937;text-align:start;cursor:pointer;">
                    <span class="text-truncate" style="flex:1;">${this.escapeHtml(this.t(statusLabel[key]))}</span>
                    <span class="text-muted" style="font-size:.75rem;">${statusCounts[key]}</span>
                    ${active ? '<i class="bi bi-check-lg text-primary" style="flex-shrink:0;"></i>' : ''}
                </button>`;
        };
        const categoryItems = Array.from(seen.entries()).map(menuRow).join('');
        const menuItems = showStatus
            ? `<div style="${headingStyle}">${this.t('statusFilterHeading')}</div>${statusKeys.map(statusRow).join('')}`
                + (seen.size ? `<div style="border-top:1px solid #eef0f2;margin-top:4px;${headingStyle}">${this.t('categoryFilterHeading')}</div>${categoryItems}` : '')
            : categoryItems;
        const clearRow = anyActive ? `
            <div style="border-top:1px solid #eef0f2;margin-top:4px;padding-top:4px;">
                <button type="button" class="seating-filter-item" data-action="clear-category-filter"
                    style="width:100%;display:flex;align-items:center;gap:8px;padding:6px 8px;background:none;border:none;border-radius:7px;font-size:.82rem;color:#6b7280;cursor:pointer;">
                    <i class="bi bi-x-circle"></i>${this.t('clearFilter')}
                </button>
            </div>` : '';

        const ddMenuStyle = 'position:absolute;inset-inline-end:0;top:calc(100% + 4px);z-index:1050;background:#fff;border:1px solid #dee2e6;border-radius:9px;box-shadow:0 8px 24px rgba(0,0,0,.14);padding:5px;';

        // Header controls only (task #249): the Filter dropdown and the ⋯ menu
        // render into the #seatingLegendControls slot ON THE SAME ROW as the
        // search box and the tag-select toggle — there's no separate
        // "Filter by category" bar anymore. The Filter button title carries
        // the old bar's wording for discoverability.
        const filterGroup = seen.size === 0 && !showStatus ? '' : `
                    <div style="position:relative;" class="flex-shrink-0">
                        <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2${anyActive ? ' seating-filter-active' : ''}" data-action="toggle-filter-menu" aria-expanded="${this._filterMenuOpen}" title="${this.escapeAttr(labels.title)}" style="font-size:.78rem;height:31px;">
                            <i class="bi bi-funnel"></i> ${this.t('filterBtn')}
                        </button>
                        <div data-role="seating-filter-menu" style="${ddMenuStyle}min-width:230px;max-height:290px;overflow:auto;display:${this._filterMenuOpen ? 'block' : 'none'};">
                            ${menuItems}
                            ${clearRow}
                        </div>
                    </div>`;
        return `${filterGroup}
                    <div style="position:relative;" class="flex-shrink-0">
                        <button type="button" class="btn btn-sm btn-outline-secondary py-0 px-2" data-action="toggle-seating-more" aria-expanded="${this._moreMenuOpen}" title="${this.t('moreActions')}" style="font-size:.78rem;height:31px;">
                            <i class="bi bi-three-dots"></i>
                        </button>
                        <div data-role="seating-more-menu" style="${ddMenuStyle}min-width:180px;display:${this._moreMenuOpen ? 'block' : 'none'};">
                            <button type="button" class="seating-filter-item" data-action="expand-all-sections"
                                style="width:100%;display:flex;align-items:center;gap:8px;padding:6px 8px;background:none;border:none;border-radius:7px;font-size:.85rem;color:#1f2937;cursor:pointer;">
                                <i class="bi bi-arrows-expand"></i>${this.t('expandAll')}
                            </button>
                            <button type="button" class="seating-filter-item" data-action="collapse-all-sections"
                                style="width:100%;display:flex;align-items:center;gap:8px;padding:6px 8px;background:none;border:none;border-radius:7px;font-size:.85rem;color:#1f2937;cursor:pointer;">
                                <i class="bi bi-arrows-collapse"></i>${this.t('collapseAll')}
                            </button>
                        </div>
                    </div>`;
    }

    pushUndoState() {
        this.undoStack.push(JSON.parse(JSON.stringify(this.config)));
        if (this.undoStack.length > 20) this.undoStack.shift();
        this.redoStack = [];
    }

    undo() {
        if (this.undoStack.length === 0) return;
        this.redoStack.push(JSON.parse(JSON.stringify(this.config)));
        this.config = this.undoStack.pop();
        this.isDirty = true;
        this.render();
        this.autoSave();
    }

    redo() {
        if (this.redoStack.length === 0) return;
        this.undoStack.push(JSON.parse(JSON.stringify(this.config)));
        this.config = this.redoStack.pop();
        this.isDirty = true;
        this.render();
        this.autoSave();
    }

    updateUndoRedoButtons() {
        const undoBtn = document.getElementById('seatingUndoBtn');
        const redoBtn = document.getElementById('seatingRedoBtn');
        if (undoBtn) undoBtn.disabled = this.undoStack.length === 0;
        if (redoBtn) redoBtn.disabled = this.redoStack.length === 0;
    }

    checkTableRuleViolations(table) {
        const rules = this.config.rules || { keepTogether: [], keepApart: [] };
        const tableIds = new Set((table.seats || []).map(s => s.responseId));
        const violations = [];
        // Check keep-apart: two guests on same table who should be apart
        for (const [id1, id2] of (rules.keepApart || [])) {
            if (tableIds.has(id1) && tableIds.has(id2)) {
                violations.push({ type: 'apart', ids: [id1, id2] });
            }
        }
        return violations;
    }

    checkKeepTogetherViolations() {
        const rules = this.config.rules || { keepTogether: [], keepApart: [] };
        const violations = [];
        for (const [id1, id2] of (rules.keepTogether || [])) {
            let table1 = null, table2 = null;
            for (const t of this.config.tables) {
                if ((t.seats || []).some(s => s.responseId === id1)) table1 = t.id;
                if ((t.seats || []).some(s => s.responseId === id2)) table2 = t.id;
            }
            if (table1 && table2 && table1 !== table2) {
                violations.push({ ids: [id1, id2], tables: [table1, table2] });
            }
        }
        return violations;
    }

    renderRulesPanel() {
        const panel = document.getElementById('seatingRulesPanel');
        if (!panel) return;
        const rules = this.config.rules || { keepTogether: [], keepApart: [] };
        const allRules = [
            ...(rules.keepTogether || []).map(([a, b]) => ({ type: 'together', a, b })),
            ...(rules.keepApart || []).map(([a, b]) => ({ type: 'apart', a, b }))
        ];

        if (allRules.length === 0 && !this._showRuleForm) {
            panel.innerHTML = '';
            return;
        }

        const getName = (rid) => {
            const r = this.allAttendingResponses.find(x => x.id === rid);
            return r ? (r.guestName || r.name || rid) : rid;
        };

        const ktViolations = this.checkKeepTogetherViolations();
        const isViolated = (type, a, b) => {
            if (type === 'together') return ktViolations.some(v => (v.ids[0] === a && v.ids[1] === b) || (v.ids[0] === b && v.ids[1] === a));
            // For apart violations, check if both on same table
            for (const t of this.config.tables) {
                const ids = new Set((t.seats || []).map(s => s.responseId));
                if (ids.has(a) && ids.has(b)) return true;
            }
            return false;
        };

        let html = '<div class="mt-2">';
        html += allRules.map(r => {
            const icon = r.type === 'together' ? '🤝' : '🚫';
            const label = r.type === 'together' ? this.t('keepTogether') : this.t('keepApart');
            const violated = isViolated(r.type, r.a, r.b);
            const cls = violated ? 'badge bg-danger' : 'badge bg-light text-dark border';
            return `<span class="${cls} me-1 mb-1" style="font-size:0.8em;">
                ${icon} ${this.escapeHtml(getName(r.a))} ↔ ${this.escapeHtml(getName(r.b))}
                <i class="bi bi-x-circle ms-1" style="cursor:pointer;"
                   data-action="remove-rule" data-rule-type="${r.type}"
                   data-rule-a="${r.a}" data-rule-b="${r.b}"></i>
            </span>`;
        }).join('');
        html += '</div>';
        panel.innerHTML = html;
    }

    renderUnassignedList() {
        const container = document.getElementById('seatingUnassignedList');
        if (!container) return;

        const assignedKeys = this.getAssignedMemberKeys();

        // Build unassigned member groups per response
        let groups = [];
        for (const r of this.allAttendingResponses) {
            const allMembers = this.buildMembersForResponse(r);
            const unassigned = allMembers.filter(m => !assignedKeys.has(`${m.responseId}::${m.guestName}`));
            if (unassigned.length > 0) {
                groups.push({ response: r, members: unassigned, totalMembers: allMembers.length });
            }
        }

        if (this.categoryFilter.size > 0) {
            groups = groups.filter(g => this.categoryFilter.has(this.getCategoryForResponse(g.response).key));
        }
        if (this.statusFilter.size > 0) {
            groups = groups.filter(g => this._passesStatusFilter(g.response));
        }

        if (this.searchFilter) {
            const q = this.searchFilter.trim().toLowerCase();
            groups = groups.filter(g =>
                g.members.some(m => m.guestName.toLowerCase().includes(q)) ||
                (g.response.guestName || g.response.name || '').toLowerCase().includes(q)
            );
        }

        // Category filter + ⋯ controls live in the card header, on the same
        // row as the search box and the tag-select toggle (task #249). Built
        // from every attending response (not just unassigned) so the filter
        // stays stable as guests get assigned.
        const controlsHost = document.getElementById('seatingLegendControls');
        if (controlsHost) controlsHost.innerHTML = this.renderRelationshipLegend();
        // While a header menu is open, the card lifts its overflow clip so the
        // dropdown renders fully on top of the guest list (task #311 — same
        // escape pattern as the table-card three-dots menu).
        document.getElementById('seatingUnassignedCard')?.classList.toggle(
            'seating-menus-open', !!(this._filterMenuOpen || this._moreMenuOpen));

        if (groups.length === 0) {
            const filtersActive = this.categoryFilter.size > 0 || this.statusFilter.size > 0 || this.searchFilter;
            const emptyMsg = filtersActive
                ? this.t('noMatchingGuests')
                : (this.allAttendingResponses.length > 0 ? this.t('allGuestsAssigned') : this.t('noAttendingGuests'));
            container.innerHTML = `<div class="text-center py-3 text-muted small">${emptyMsg}</div>`;
            return;
        }

        // Group response-groups by category for sectioned rendering
        const NO_CATEGORY = '__nocategory__';
        const sections = new Map(); // key -> { label, color, groups: [], memberCount }
        for (const g of groups) {
            const cat = this.getCategoryForResponse(g.response);
            const key = cat.key;
            if (!sections.has(key)) {
                sections.set(key, {
                    key,
                    label: cat.label,
                    color: key !== NO_CATEGORY ? this.getRelationshipColor(key) : null,
                    groups: [],
                    memberCount: 0
                });
            }
            const section = sections.get(key);
            section.groups.push(g);
            section.memberCount += g.members.length;
        }

        let html = this._selectBarHtml();
        for (const section of sections.values()) {
            const collapsed = this._collapsedSections.has(section.key);
            const chevronIcon = collapsed ? 'bi-chevron-right' : 'bi-chevron-down';
            const dot = section.color
                ? `<span class="seating-section-dot" style="background:${section.color};"></span>`
                : `<span class="seating-section-dot seating-section-dot-empty"></span>`;
            const draggable = section.key !== NO_CATEGORY;

            html += `
                <div class="seating-section${collapsed ? ' collapsed' : ''}" data-section-key="${this.escapeAttr(section.key)}">
                    <div class="seating-section-header"
                        ${draggable ? `draggable="true" data-drag-mode="category" data-category-key="${this.escapeAttr(section.key)}"` : ''}
                        data-action="toggle-section"
                        data-section-key="${this.escapeAttr(section.key)}"
                        ${draggable ? `title="${this.t('dragCategoryHint')}"` : ''}>
                        <i class="bi ${chevronIcon} seating-section-chevron"></i>
                        ${dot}
                        <span class="seating-section-label">${this.escapeHtml(section.label)}</span>
                        <span class="seating-section-count">${section.memberCount}</span>
                    </div>
                    <div class="seating-section-body">
                        ${section.groups.map(g => this._renderUnassignedGroup(g, section.color)).join('')}
                    </div>
                </div>
            `;
        }

        container.innerHTML = html;
        this.setupGuestDragEvents(container);
        this._ensureBulkTagUI();
        this._updateBulkTagBar();
    }

    // A long name is ellipsized to keep the columns aligned, which hides the end
    // of it — so the cut-off ones carry the full name as a hover tooltip (task
    // #470). Only the truncated ones: a tooltip on a name you can already read
    // is noise, and on a party header it would mask the "drag to seat the whole
    // party" hint that lives on the row.
    //
    // Decided AT HOVER, not at render. The first attempt measured every name
    // once the list was painted, which fails in two ways that between them
    // covered every real case:
    //   * a panel with no layout yet — the seating pane during its tab
    //     transition, or a collapsed section — reports scrollWidth 94 against
    //     clientWidth 0, so EVERY name looks clipped and gets a spurious
    //     tooltip, while names inside a closed party report 0 vs 0 and get
    //     none. Neither is re-measured when the panel finally has a width.
    //   * it also silently missed seated guests entirely (see the selector
    //     below).
    // On hover the element is on screen by definition, so the measurement is
    // trustworthy with no observers, no resize listener, and no dependence on
    // when anything was painted. Setting `title` during mouseover is in time
    // for the browser's own tooltip delay.
    //
    // Both name elements are covered: `.guest-name` in the sidebar list and
    // `.guest-name-text` on a table card's seat chip. The second is where
    // clipping is MOST likely — a table card is far narrower than the sidebar —
    // and it was the one being missed.
    static get NAME_TOOLTIP_SELECTOR() {
        return '.seating-guest-item .guest-name, .seating-seat-item .guest-name-text';
    }

    _watchNameTooltips() {
        if (this._nameTooltipWatcher) return;
        this._nameTooltipWatcher = (e) => {
            const el = e.target.closest && e.target.closest(DashboardSeating.NAME_TOOLTIP_SELECTOR);
            if (el) this._applyNameTooltip(el);
        };
        // `mouseover` rather than `mouseenter` — it bubbles, so one listener
        // covers every row and every seat chip, including ones rendered later.
        document.addEventListener('mouseover', this._nameTooltipWatcher);
    }

    _applyNameTooltip(el) {
        // 1px of slack: sub-pixel text metrics push scrollWidth a fraction past
        // clientWidth on names that are not actually clipped.
        if (el.scrollWidth > el.clientWidth + 1) {
            // A seat chip's textContent also contains the "Kid" chip and the
            // "Custom" pill, so prefer the name the row already carries as data.
            const owner = el.closest('[data-guest-name]');
            el.title = (owner && owner.getAttribute('data-guest-name')) || el.textContent.trim();
        } else {
            el.removeAttribute('title');
        }
    }

    // Reflect select mode on the header toggle: filled/active while selecting.
    _syncSelectModeBtn() {
        const btn = document.getElementById('seatingSelectModeBtn');
        if (!btn) return;
        btn.classList.toggle('btn-primary', !!this._selectMode);
        btn.classList.toggle('btn-outline-secondary', !this._selectMode);
        btn.title = this._selectMode ? this.t('selectDone') : this.t('selectMultiple');
    }

    // Bulk-select mode is toggled by the checkbox icon in the sidebar header
    // (#seatingSelectModeBtn, task #87). While on, a slim hint bar sits above
    // the list; off renders nothing so the list starts clean.
    _selectBarHtml() {
        if (!this._selectMode) return '';
        return `
            <div class="seating-select-bar" style="display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;padding:.4rem .5rem;border-bottom:1px solid #eef0f2;">
                <span class="seating-select-hint" style="font-size:.78rem;color:#6c757d;">${this.escapeHtml(this.t('selectMultipleHint'))}</span>
            </div>`;
    }

    // --- Bulk tagging ---------------------------------------------------------
    // Lets a host select many parties at once and add/remove seating tags across
    // all of them in one action, instead of opening the tag editor per guest.

    // Inject the sticky action bar (into <body>) exactly once. It auto-appears
    // when ≥1 party is selected (selection checkboxes are revealed by the
    // "Select" toggle — see _selectBarHtml + the bulkCheckbox renderer).
    _ensureBulkTagUI() {
        if (!document.getElementById('seatingBulkBar')) {
            const bar = document.createElement('div');
            bar.id = 'seatingBulkBar';
            bar.className = 'seating-bulk-bar';
            bar.style.display = 'none';
            bar.innerHTML = `
                <span class="seating-bulk-count" id="seatingBulkCount"></span>
                <div class="seating-bulk-actions">
                    <button type="button" class="btn btn-sm btn-light" id="seatingBulkClear">${this.t('bulkClearSel')}</button>
                    <button type="button" class="btn btn-sm btn-primary" id="seatingBulkTagBtn">
                        <i class="bi bi-tags me-1"></i><span id="seatingBulkTagLabel">${this.t('bulkTagSelected')}</span>
                    </button>
                </div>`;
            // Place the bar directly above the guest list (between the panel header
            // and the scrollable list) so it sits where the selection happens —
            // instead of floating disconnected at the top-center of the screen.
            // Falls back to <body> if the list isn't in the DOM yet.
            const listEl = document.getElementById('seatingUnassignedList');
            if (listEl && listEl.parentNode) {
                listEl.parentNode.insertBefore(bar, listEl);
            } else {
                document.body.appendChild(bar);
            }
            bar.querySelector('#seatingBulkClear').addEventListener('click', () => {
                this._bulkSelected.clear();
                this.renderUnassignedList();
            });
            bar.querySelector('#seatingBulkTagBtn').addEventListener('click', () => this.showBulkTagModal());
        }
    }

    _updateBulkTagBar() {
        const bar = document.getElementById('seatingBulkBar');
        if (!bar) return;
        const n = this._bulkSelected.size;
        // Auto-appear the moment ≥1 party is selected; hide when nothing is selected.
        if (n > 0) {
            bar.style.display = 'flex';
            const cnt = document.getElementById('seatingBulkCount');
            if (cnt) cnt.innerHTML = `<i class="bi bi-check2-square me-1"></i>${n}`;
            const lbl = document.getElementById('seatingBulkTagLabel');
            if (lbl) lbl.textContent = this.t('bulkTagSelected');
        } else {
            bar.style.display = 'none';
        }
    }

    // Modal to add/remove tags across all selected parties at once. Existing
    // event tags are listed with a tri-state checkbox (checked = all selected
    // have it, dash = only some, empty = none). Checking adds it to everyone
    // selected; unchecking removes it. New tags can be typed in to create them.
    showBulkTagModal() {
        const ids = [...this._bulkSelected];
        if (!ids.length) { this.showToast(this.t('bulkSelectGuests'), 'warning'); return; }
        const selected = (this.allAttendingResponses || []).filter(r => this._bulkSelected.has(r.id));

        // Union of all tags used anywhere in the event, plus their state among
        // the selected parties.
        const allTags = [...new Set(
            (this.allAttendingResponses || [])
                .flatMap(r => Array.isArray(r.seatingTags) ? r.seatingTags : [])
                .filter(Boolean)
        )].sort((a, b) => a.localeCompare(b));
        const stateOf = (tag) => {
            const have = selected.filter(r => (r.seatingTags || []).includes(tag)).length;
            return have === 0 ? 'none' : (have === selected.length ? 'all' : 'some');
        };

        let newTags = [];
        const modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.innerHTML = `
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header py-2">
                        <h6 class="modal-title">${this.t('bulkTitle')} (${ids.length})</h6>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <p class="text-muted small mb-2">${this.t('bulkHint')}</p>
                        <div id="bulkTagList" class="seating-bulk-taglist">${
                            allTags.length
                                ? allTags.map(tag => {
                                    const st = stateOf(tag);
                                    return `<label class="seating-bulk-tagrow">
                                        <input type="checkbox" class="form-check-input bulk-tag-cb" data-tag="${this.escapeAttr(tag)}" data-initial="${st}" ${st === 'all' ? 'checked' : ''}>
                                        <span>${this.escapeHtml(tag)}</span>
                                    </label>`;
                                }).join('')
                                : `<span class="text-muted small">${this.t('noTagsYet')}</span>`
                        }</div>
                        <div class="seating-tag-chips mt-2" id="bulkNewChips"></div>
                        <input type="text" class="form-control form-control-sm mt-2" id="bulkNewTagInput"
                            placeholder="${this.escapeAttr(this.t('addTagPlaceholder'))}" autocomplete="off" maxlength="40">
                    </div>
                    <div class="modal-footer py-1">
                        <button type="button" class="btn btn-primary btn-sm" id="bulkTagApplyBtn">${this.t('bulkApply')}</button>
                    </div>
                </div>
            </div>`;
        document.body.appendChild(modal);
        const bsModal = new bootstrap.Modal(modal);
        bsModal.show();

        // Set the indeterminate ("some") state on the relevant checkboxes.
        modal.querySelectorAll('.bulk-tag-cb').forEach(cb => {
            if (cb.dataset.initial === 'some') cb.indeterminate = true;
        });

        // New-tag chip entry (creates a tag and applies it to everyone selected).
        const chipsEl = modal.querySelector('#bulkNewChips');
        const inputEl = modal.querySelector('#bulkNewTagInput');
        const renderChips = () => {
            chipsEl.innerHTML = newTags.map((tg, i) =>
                `<span class="seating-tag-chip">${this.escapeHtml(tg)}<button type="button" class="seating-tag-remove" data-idx="${i}" aria-label="remove">&times;</button></span>`).join('');
        };
        const addNew = (raw) => {
            const v = String(raw || '').trim();
            if (v && !newTags.some(t => t.toLowerCase() === v.toLowerCase()) && newTags.length < 12) {
                newTags.push(v.slice(0, 40));
            }
            inputEl.value = '';
            renderChips();
        };
        inputEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addNew(inputEl.value); }
        });
        chipsEl.addEventListener('click', (e) => {
            const rm = e.target.closest('.seating-tag-remove');
            if (rm) { newTags.splice(parseInt(rm.dataset.idx, 10), 1); renderChips(); }
        });

        modal.querySelector('#bulkTagApplyBtn').addEventListener('click', async () => {
            if (inputEl.value.trim()) addNew(inputEl.value);
            const applyBtn = modal.querySelector('#bulkTagApplyBtn');
            applyBtn.disabled = true;

            const add = [];
            const remove = [];
            modal.querySelectorAll('.bulk-tag-cb').forEach(cb => {
                if (cb.indeterminate) return;              // untouched "some" → leave as-is
                const tag = cb.dataset.tag;
                if (cb.checked && cb.dataset.initial !== 'all') add.push(tag);
                else if (!cb.checked && cb.dataset.initial !== 'none') remove.push(tag);
            });
            newTags.forEach(t => add.push(t));

            if (!add.length && !remove.length) { bsModal.hide(); return; }

            try {
                const res = await fetch(`/api/seating/${this.formId}/tags-bulk`, {
                    method: 'PUT',
                    headers: { ...(await this._authHeaders()), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ responseIds: ids, add, remove })
                });
                const data = await res.json();
                if (data.success) {
                    // Reflect the new tags locally so re-renders + auto-assign use them.
                    Object.entries(data.results || {}).forEach(([rid, tags]) => {
                        const r = (this.allAttendingResponses || []).find(x => x.id === rid);
                        if (r) r.seatingTags = tags;
                    });
                    // No toast: renderUnassignedList() below redraws the guests with
                    // their new tags, in front of the host (task #449).
                    // Clear the selection — the re-render drops the checkboxes' checked
                    // state and the action bar auto-hides (selection size is now 0).
                    this._bulkSelected.clear();
                    this.renderUnassignedList();
                    bsModal.hide();
                } else {
                    this.showToast(data.error || this.t('tagsSaveFailed'), 'danger');
                    applyBtn.disabled = false;
                }
            } catch (err) {
                console.error('Bulk tag save error:', err);
                this.showToast(this.t('tagsSaveFailed'), 'danger');
                applyBtn.disabled = false;
            }
        });

        modal.addEventListener('hidden.bs.modal', () => modal.remove());
    }

    // Small inline chips showing a guest's seating tags right in their row, so
    // hosts can see tags at a glance without opening the Edit-tags editor. Tags
    // live on the response (shared by the whole party), so they render on
    // single-guest rows and group headers — not on each group member row.
    _renderTagChips(resp) {
        const tags = (resp && Array.isArray(resp.seatingTags)) ? resp.seatingTags.filter(Boolean) : [];
        if (!tags.length) return '';
        return `<span class="seating-guest-tags">${
            tags.map(t => `<span class="seating-tag-chip" title="${this.escapeAttr(t)}">${this.escapeHtml(t)}</span>`).join('')
        }</span>`;
    }

    // Per-member tags (memberSeatingTags map, task #153) — personal attributes
    // like Gluten Free that belong to one person, not the whole party.
    _renderMemberTagChips(resp, memberName) {
        const map = (resp && resp.memberSeatingTags) || {};
        const tags = Array.isArray(map[memberName]) ? map[memberName].filter(Boolean) : [];
        if (!tags.length) return '';
        return `<span class="seating-guest-tags">${
            tags.map(t => `<span class="seating-tag-chip" title="${this.escapeAttr(t)}">${this.escapeHtml(t)}</span>`).join('')
        }</span>`;
    }

    _renderUnassignedGroup(g, relColor) {
        // The old three-dot menu listed every table for quick assignment, but that
        // is redundant — hosts assign by drag-and-drop or the inline # box. It is
        // replaced by a dedicated Tag icon that opens the guest's tag editor.
        // Header/single rows edit the party's grouping tags; rows for individual
        // party members set data-member so the editor scopes to that one person
        // instead of tagging the whole party (task #153).
        const tagButton = (responseId, guestName, isMember = false) => `
            <div class="seating-guest-actions">
                <button class="btn btn-sm btn-link p-0 seating-tag-btn" type="button"
                    data-action="edit-tags"
                    data-response-id="${responseId}"
                    data-guest-name="${this.escapeAttr(guestName)}"
                    ${isMember ? 'data-member="1"' : ''}
                    title="${this.t('editTags')}">
                    <i class="bi bi-tags"></i>
                </button>
            </div>`;
        // Response-representative rows (single guests + group headers) get a
        // selection checkbox ONLY in Select mode, so by default a row carries a
        // single tagging control (the tag icon). Selection is per responseId
        // (tags are response-level; drag bundles each party's members).
        const bulkCheckbox = (responseId) =>
            this._selectMode
                ? `<input type="checkbox" class="form-check-input seating-bulk-cb" data-response-id="${responseId}" ${this._bulkSelected.has(responseId) ? 'checked' : ''} title="${this.escapeAttr(this.t('bulkTag'))}">`
                : '';
        const childToggle = (m) => {
            const toggleTitle = m.isChild ? this.t('markAsAdult') : this.t('markAsChild');
            return `<span class="seat-child-toggle"
                data-action="toggle-child"
                data-response-id="${m.responseId}"
                data-guest-name="${this.escapeAttr(m.guestName)}"
                title="${toggleTitle}">${this.t('kidLabel')}</span>`;
        };
        // A party HEADER is not a person, so it has no Kid chip — which left it
        // one sibling short of every other row. `.guest-info` is the flexible
        // element in the row, so it swallowed the difference (measured 36px) and
        // shoved the bubble, chevron and tag columns left on single-guest rows
        // relative to party rows (task #470 round 2). Reserving the slot keeps
        // the columns lined up. It carries the same LABEL, not a fixed width, so
        // it stays exactly as wide as the real chip in every language.
        const childToggleSlot = () =>
            `<span class="seat-child-toggle is-slot" aria-hidden="true">${this.t('kidLabel')}</span>`;

        if (g.members.length === 1 && g.totalMembers === 1) {
            const m = g.members[0];
            const selKey = `${m.responseId}::${m.guestName}`;
            const selected = this._selectedMembers.has(selKey);
            const maybeMark = m.isMaybe ? `<i class="bi bi-question-circle seat-maybe-mark" title="${this.t('maybeGuest')}"></i>` : '';
            const extraClasses = `${m.isChild ? ' is-child' : ''}${m.isMaybe ? ' is-maybe' : ''}`;
            const bulkSel = this._bulkSelected.has(m.responseId);
            return `<div class="seating-guest-item${selected ? ' selected' : ''}${bulkSel ? ' bulk-selected' : ''}${extraClasses}" draggable="true"
                data-response-id="${m.responseId}" data-guest-name="${this.escapeAttr(m.guestName)}"
                data-drag-mode="member"
                style="--seat-color:${relColor || 'transparent'};">
                ${bulkCheckbox(m.responseId)}
                <div class="guest-info">${maybeMark}<span class="guest-name"${m.meal ? ` title="${this.escapeAttr(m.meal)}"` : ''}>${this.escapeHtml(m.guestName)}</span>${m.meal ? `<span class="seat-meal-chip" title="${this.escapeAttr(m.meal)}"><i class="bi bi-egg-fried"></i></span>` : ''}${this._renderTagChips(g.response)}</div>
                ${childToggle(m)}
                ${this._tableInputHtml({ responseId: m.responseId, guestName: m.guestName, dragMode: 'member' })}
                ${tagButton(m.responseId, m.guestName)}
            </div>`;
        }

        const primaryName = g.response.guestName || g.response.name || this.t('guest');
        const headerBulkSel = this._bulkSelected.has(g.response.id);
        // Nested-tree view (task #91): a multi-member party is a COLLAPSED group by
        // default — the leader row acts as the header, the members hide until the
        // host expands the party with the chevron. Dragging the header still seats
        // the whole party (group drag); expand to override an individual member.
        const expanded = this._expandedParties.has(g.response.id);
        const partyChevron = expanded ? 'bi-chevron-down' : 'bi-chevron-right';
        return `<div class="seating-guest-group${expanded ? ' is-expanded' : ''}">
            <div class="seating-guest-item seating-group-header${headerBulkSel ? ' bulk-selected' : ''}" draggable="true"
                data-response-id="${g.response.id}" data-guest-name="${this.escapeAttr(primaryName)}"
                data-drag-mode="group" title="${this.t('dragGroupHint')}"
                style="--seat-color:${relColor || 'transparent'};">
                ${bulkCheckbox(g.response.id)}
                <div class="guest-info">
                    <span class="guest-name">${this.escapeHtml(primaryName)}</span>
                    <span class="badge bg-secondary guest-count-badge" title="${this.escapeAttr(this.t('groupSizeHint') || 'Party size')}">${g.totalMembers}</span>
                    <button type="button" class="seating-party-toggle" data-action="toggle-party" draggable="false"
                        data-response-id="${g.response.id}" aria-expanded="${expanded}"
                        title="${this.escapeAttr(this.t('togglePartyHint'))}">
                        <i class="bi ${partyChevron}"></i>
                    </button>
                    ${(this._splitPartyIds && this._splitPartyIds.has(g.response.id)) ? `<span class="seating-custom-pill" title="${this.escapeAttr(this.t('customSeatHint'))}">${this.escapeHtml(this.t('customSeat'))}</span>` : ''}
                    ${this._renderTagChips(g.response)}
                </div>
                ${childToggleSlot()}
                ${this._tableInputHtml({ responseId: g.response.id, guestName: primaryName, dragMode: 'group' })}
                ${tagButton(g.response.id, primaryName)}
            </div>
            <div class="seating-party-members${expanded ? ' is-expanded' : ''}">
            ${g.members
                // Condensed view: the primary guest IS the group header row, so we no
                // longer repeat them as the first child when the group is expanded
                // (task #17). Only the additional party members (companions / kids)
                // are listed below the header. Assigning a table or tag to the header
                // still cascades to the whole party via the group drag/input.
                .filter(m => m.isGroupMember)
                .map(m => {
                const selKey = `${m.responseId}::${m.guestName}`;
                const selected = this._selectedMembers.has(selKey);
                const maybeMark = m.isMaybe ? `<i class="bi bi-question-circle seat-maybe-mark" title="${this.t('maybeGuest')}"></i>` : '';
                const extraClasses = `${m.isChild ? ' is-child' : ''}${m.isMaybe ? ' is-maybe' : ''}`;
                return `<div class="seating-guest-item seating-guest-member${selected ? ' selected' : ''}${extraClasses}" draggable="true"
                    data-response-id="${m.responseId}" data-guest-name="${this.escapeAttr(m.guestName)}"
                    data-drag-mode="member"
                    style="--member-color:${relColor || '#dee2e6'};">
                    <div class="guest-info">${maybeMark}<span class="guest-name"${m.meal ? ` title="${this.escapeAttr(m.meal)}"` : ''}>${this.escapeHtml(m.guestName)}</span>${m.meal ? `<span class="seat-meal-chip" title="${this.escapeAttr(m.meal)}"><i class="bi bi-egg-fried"></i></span>` : ''}${this._renderMemberTagChips(g.response, m.guestName)}</div>
                    ${childToggle(m)}
                    ${this._tableInputHtml({ responseId: m.responseId, guestName: m.guestName, dragMode: 'member' })}
                    ${tagButton(m.responseId, m.guestName, true)}
                </div>`;
            }).join('')}
            </div>
        </div>`;
    }

    /**
     * Task #887: at check-in only the people who have ARRIVED get seats, so a
     * table can look fine with one of a party of five at it. Per table: the
     * rest of each party that has checked in and sits there (counted at the
     * table holding most of them, where they will join), and whether they
     * would fit. Server mirror: seating.service.js _overflowNote (the door's
     * heads-up). Map tableId -> { toCome, free, who }, overflowing tables only.
     */
    _tableOverflow() {
        const out = new Map();
        const seatsOf = new Map();
        (this.config.tables || []).forEach(t => (t.seats || []).forEach((s) => {
            if (!s || !s.responseId) return;
            if (!seatsOf.has(s.responseId)) seatsOf.set(s.responseId, new Map());
            const m = seatsOf.get(s.responseId);
            m.set(t.id, (m.get(t.id) || 0) + 1);
        }));
        if (!seatsOf.size) return out;
        const expected = new Map();
        (this.allAttendingResponses || []).forEach((r) => {
            const m = seatsOf.get(r.id);
            if (!m || !(r.checkedInAt || r.checkinStatus === true || r.checkedIn === true)) return;
            let seated = 0, home = null, most = 0;
            m.forEach((n, tid) => { seated += n; if (n > most) { most = n; home = tid; } });
            const toCome = this.buildMembersForResponse(r).length - seated;
            if (toCome <= 0) return;
            if (!expected.has(home)) expected.set(home, { toCome: 0, who: [] });
            const e = expected.get(home);
            e.toCome += toCome;
            e.who.push(`${r.guestName || r.name || ''} (+${toCome})`);
        });
        (this.config.tables || []).forEach((t) => {
            const e = expected.get(t.id);
            const cap = parseInt(t.capacity, 10) || 0;
            if (!e || !cap) return;
            const free = Math.max(0, cap - (t.seats || []).length);
            if (e.toCome > free) out.set(t.id, { toCome: e.toCome, free, who: e.who });
        });
        return out;
    }

    renderTables() {
        const container = document.getElementById('seatingTablesContainer');
        if (!container) return;

        if (this.config.tables.length === 0) {
            container.innerHTML = `
                <div class="col-12 text-center py-3 text-muted">
                    <i class="bi bi-diagram-3" style="font-size: 1.75rem;"></i>
                    <p class="mt-2 mb-0">${this.t('noTablesYet')}</p>
                </div>
                <div class="col-md-6 col-lg-4 mx-auto d-flex justify-content-center seating-add-tile-col">
                    <button type="button" class="seating-add-table-tile" data-action="add-table-tile" title="${this.t('addTable')}">
                        <i class="bi bi-plus-lg"></i>
                        <span>${this.t('addTable')}</span>
                    </button>
                </div>
            `;
            this._updateCollapseAllButton();
            return;
        }

        const overflowByTable = this._tableOverflow();
        const tableCards = this.config.tables.map(table => {
            const seatCount = (table.seats || []).length;
            const capacityClass = seatCount > table.capacity ? 'over' : seatCount === table.capacity ? 'full' : 'available';
            const fillPct = table.capacity > 0 ? Math.min(100, (seatCount / table.capacity) * 100) : 0;
            const typeIcon = table.type === 'round' ? 'bi-circle' : 'bi-square';
            const violations = this.checkTableRuleViolations(table);
            const violationBadge = violations.length > 0 ? `<span class="badge bg-danger ms-1" title="${this.t('ruleConflict')}">${this.t('ruleConflict')}</span>` : '';
            const collapsed = this._collapsedTables.has(table.id);
            const chevronIcon = collapsed ? 'bi-chevron-down' : 'bi-chevron-up';
            const collapseTitle = collapsed ? this.t('expandTable') : this.t('collapseTable');

            // Resolve per-seat flags (child / maybe) and count kids in this table
            const seatFlags = (table.seats || []).map(s => this._getMemberFlags(s));
            const kidsCount = seatFlags.filter(f => f.isChild).length;
            const maybeCount = seatFlags.filter(f => f.isMaybe).length;
            const kidsBadge = kidsCount > 0
                ? `<span class="table-kids-badge" title="${this.t('kidsAtTable').replace('{count}', kidsCount)}">${this.t('kidLabel')} ${kidsCount}</span>`
                : '';
            const maybeBadge = maybeCount > 0
                ? `<span class="table-maybe-badge" title="${this.t('maybeAtTable').replace('{count}', maybeCount)}"><i class="bi bi-question-circle"></i>${maybeCount}</span>`
                : '';

            return `
                <div class="col-md-6 col-lg-4 seating-table-col" data-table-id="${table.id}">
                    <div class="seating-table-card card ${violations.length > 0 ? 'border-danger' : ''}${collapsed ? ' collapsed' : ''}" data-table-id="${table.id}">
                        <div class="card-header">
                            <div class="d-flex align-items-center gap-1 min-w-0 seating-table-title">
                                <div class="seating-table-name-row">
                                    <i class="bi bi-grip-vertical seating-table-drag-handle"
                                        data-table-id="${table.id}"
                                        draggable="true"
                                        title="${this.t('dragToReorder')}"></i>
                                    <strong class="seating-table-name-editable"
                                        data-action="edit-table-name"
                                        data-table-id="${table.id}"
                                        title="${this.escapeAttr(table.name)} — ${this.t('renameTableTitle')}">${this.escapeHtml(table.name)}</strong>
                                </div>
                                <span class="seating-table-badges">
                                    <span class="table-capacity table-capacity-inline ${capacityClass}"
                                        title="${table.type === 'round' ? this.t('round') : this.t('rectangular')}"><i class="bi ${typeIcon}"></i>${seatCount}/${table.capacity}</span>
                                    ${kidsBadge}
                                    ${maybeBadge}
                                    ${violationBadge}
                                </span>
                            </div>
                            <div class="d-flex align-items-center gap-2 seating-table-actions">
                                <button type="button" class="btn btn-sm btn-link p-0 seating-collapse-btn"
                                    data-action="toggle-collapse"
                                    data-table-id="${table.id}"
                                    title="${collapseTitle}">
                                    <i class="bi ${chevronIcon}"></i>
                                </button>
                                <div class="dropdown">
                                    <button class="btn btn-sm btn-link p-0" data-bs-toggle="dropdown">
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end">
                                        <li><a class="dropdown-item" href="#" data-action="edit-table" data-table-id="${table.id}">
                                            <i class="bi bi-pencil me-2"></i>${this.t('edit')}</a></li>
                                        <li><a class="dropdown-item text-danger" href="#" data-action="delete-table" data-table-id="${table.id}">
                                            <i class="bi bi-trash me-2"></i>${this.t('delete')}</a></li>
                                    </ul>
                                </div>
                            </div>
                            <div class="table-capacity-bar" aria-hidden="true">
                                <div class="table-capacity-bar-fill" data-state="${capacityClass}" style="width:${fillPct}%;"></div>
                            </div>
                        </div>
                        ${overflowByTable.has(table.id) ? (() => {
                const o = overflowByTable.get(table.id);
                return `<div class="seating-table-overflow" title="${this.escapeAttr(this.t('stillToCome').replace('{names}', o.who.join(', ')))}"><i class="bi bi-exclamation-circle"></i><span>${this.escapeHtml(this.t('mayOverflow').replace('{count}', o.toCome).replace('{free}', o.free))}</span></div>`;
            })() : ''}
                        ${table.description ? `<div class="seating-table-desc" title="${this.escapeAttr(table.description)}"><i class="bi bi-eye-slash"></i><span>${this.escapeHtml(table.description)}</span></div>` : ''}
                        <div class="table-seats" data-table-id="${table.id}">
                            ${(table.seats || []).map((seat, idx) => {
                const resp = this.allAttendingResponses.find(r => r.id === seat.responseId);
                // Colour a seated guest by the SAME category the legend and the
                // unassigned panel use, not by their raw relationship string.
                // Reading the raw field meant a guest who answered in another
                // language got a colour that appears nowhere in the legend — and
                // a tagged guest was coloured by their relationship while being
                // listed under their tag.
                const relCat = resp ? this.getCategoryForResponse(resp) : null;
                const relColor = (relCat && relCat.key !== '__nocategory__')
                    ? this.getRelationshipColor(relCat.key) : null;
                const flags = seatFlags[idx] || { isChild: false, isMaybe: false };
                const maybeMark = flags.isMaybe ? `<i class="bi bi-question-circle seat-maybe-mark" title="${this.t('maybeGuest')}"></i>` : '';
                const extraClasses = `${flags.isChild ? ' is-child' : ''}${flags.isMaybe ? ' is-maybe' : ''}`;
                const childTitle = flags.isChild ? this.t('markAsAdult') : this.t('markAsChild');
                const isOverride = this._overrideSeatKeys && this._overrideSeatKeys.has(`${seat.responseId}::${seat.guestName}`);
                const customPill = isOverride
                    ? `<span class="seating-custom-pill" title="${this.escapeAttr(this.t('customSeatHint'))}">${this.escapeHtml(this.t('customSeat'))}</span>`
                    : '';
                return `
                                <div class="seating-seat-item${extraClasses}${isOverride ? ' is-override' : ''}" draggable="true"
                                     data-response-id="${seat.responseId}"
                                     data-guest-name="${this.escapeAttr(seat.guestName)}"
                                     data-source-table-id="${table.id}"
                                     style="--seat-color:${relColor || 'transparent'};">
                                    <span class="guest-name-text">
                                        <span class="seat-child-toggle"
                                              data-action="toggle-child"
                                              data-response-id="${seat.responseId}"
                                              data-guest-name="${this.escapeAttr(seat.guestName)}"
                                              title="${childTitle}">${this.t('kidLabel')}</span>
                                        ${maybeMark}${this.escapeHtml(seat.guestName)}${customPill}
                                    </span>
                                    ${this._tableInputHtml({ responseId: seat.responseId, guestName: seat.guestName, dragMode: 'member', sourceTableId: table.id, value: this._tableNumberLabel(table) })}
                                    <i class="bi bi-x-circle remove-seat"
                                       data-action="remove-seat"
                                       data-table-id="${table.id}"
                                       data-response-id="${seat.responseId}"
                                       data-guest-name="${this.escapeAttr(seat.guestName)}"></i>
                                </div>
                            `}).join('')}
                            ${this._renderEmptySlots(table.capacity - seatCount)}
                        </div>
                    </div>
                </div>
            `;
        }).join('');

        const addTile = `
            <div class="col-md-6 col-lg-4 seating-add-tile-col">
                <button type="button" class="seating-add-table-tile" data-action="add-table-tile" title="${this.t('addTable')}">
                    <i class="bi bi-plus-lg"></i>
                    <span>${this.t('addTable')}</span>
                </button>
            </div>
        `;

        container.innerHTML = tableCards + addTile;

        this.setupTableDropEvents(container);
        this.setupTableReorderEvents(container);
        this._updateCollapseAllButton();
    }

    _renderEmptySlots(count) {
        if (count <= 0) return '';
        return `<div class="empty-seats-footer" aria-label="${count} ${this.t('emptySeats')}">
            <i class="bi bi-plus-circle"></i>
            <span>${count} ${this.t('emptySeats')}</span>
        </div>`;
    }

    /**
     * Compact "type a table number" input shown on each guest row (the
     * keyboard-driven alternative to drag-and-drop). For a seated guest the
     * `value` is pre-filled with the table's number so it stays in sync with
     * drag-and-drop; for an unassigned guest it starts empty.
     */
    _tableInputHtml({ responseId, guestName, dragMode = 'member', sourceTableId = null, value = '' }) {
        return `<input type="text" class="seating-table-input${sourceTableId ? ' seating-seat-table-input' : ''}" draggable="false"
            value="${this.escapeAttr(value)}" placeholder="#"
            data-response-id="${this.escapeAttr(responseId)}"
            data-guest-name="${this.escapeAttr(guestName)}"
            data-drag-mode="${dragMode}"${sourceTableId ? ` data-source-table-id="${this.escapeAttr(sourceTableId)}"` : ''}
            title="${this.t('typeTableHint')}" aria-label="${this.t('typeTableHint')}">`;
    }

    /**
     * The short label shown inside a seated guest's table input. Uses the bare
     * number from the table name (e.g. "Table 7" → "7") when that number
     * uniquely identifies a table; otherwise falls back to the full name so the
     * value round-trips unambiguously when typed back in.
     */
    _tableNumberLabel(table) {
        const name = (table && table.name ? String(table.name) : '').trim();
        const m = name.match(/\d+/);
        if (!m) return name;
        const num = m[0];
        const sameNum = (this.config.tables || []).filter(t => {
            const mm = String(t.name || '').match(/\d+/);
            return mm && mm[0] === num;
        });
        return sameNum.length === 1 ? num : name;
    }

    /**
     * Resolve a value typed into a table input to a table.
     * Returns { table } | { unassign: true } | { ambiguous: true } | { notFound: true }.
     * Match order: empty → unassign; exact name (case-insensitive); bare number
     * that uniquely maps to one table; "Table N".
     */
    _resolveTableFromTypedValue(raw) {
        const s = (raw || '').trim();
        if (!s) return { unassign: true };
        const tables = this.config.tables || [];
        const lc = s.toLowerCase();

        const byName = tables.find(t => String(t.name || '').trim().toLowerCase() === lc);
        if (byName) return { table: byName };

        if (/^\d+$/.test(s)) {
            const numMatches = tables.filter(t => {
                const mm = String(t.name || '').match(/\d+/);
                return mm && mm[0] === s;
            });
            if (numMatches.length === 1) return { table: numMatches[0] };
            const byTableN = tables.find(t => String(t.name || '').trim().toLowerCase() === `table ${s}`);
            if (byTableN) return { table: byTableN };
            if (numMatches.length > 1) return { ambiguous: true };
        }
        return { notFound: true };
    }

    /**
     * Commit a table input: assign / move / unassign the guest to match what
     * the host typed. Mirrors the drag-and-drop result so both stay in sync.
     */
    _handleTableInputCommit(input) {
        const responseId = input.dataset.responseId;
        const guestName = input.dataset.guestName;
        const dragMode = input.dataset.dragMode || 'member';
        const sourceTableId = input.dataset.sourceTableId || null;
        const res = this._resolveTableFromTypedValue(input.value);

        if (res.unassign) {
            // Cleared the field → unassign if currently seated; otherwise reset.
            if (sourceTableId) {
                this.removeGuestFromTable(responseId, guestName, sourceTableId);
            } else {
                this.renderUnassignedList();
            }
            return;
        }
        if (res.ambiguous) {
            this.showToast(this.t('tableAmbiguous').replace('{value}', input.value.trim()), 'error');
            this.render();
            return;
        }
        if (res.notFound) {
            this.showToast(this.t('tableNotFound').replace('{value}', input.value.trim()), 'error');
            this.render();
            return;
        }
        // Already at this table — nothing to do (avoid a redundant undo entry).
        if (sourceTableId && res.table.id === sourceTableId) return;

        const data = { responseId, guestName, dragMode };
        if (sourceTableId) { data.source = 'table'; data.sourceTableId = sourceTableId; }
        this.assignGuestToTable(data, res.table.id);
    }

    // Count guests (party members) who still need a seat. Shared by the counter
    // badge and the mobile adaptive default-tab decision.
    _countUnassignedMembers() {
        const assignedKeys = this.getAssignedMemberKeys();
        let total = 0;
        for (const r of (this.allAttendingResponses || [])) {
            const members = this.buildMembersForResponse(r);
            total += members.filter(m => !assignedKeys.has(`${m.responseId}::${m.guestName}`)).length;
        }
        return total;
    }

    /**
     * "Ready for the event?" at the foot of the guest panel (task #1024,
     * Esther): once guests have tables, the sign that lets them find theirs
     * at the door is the next thing to make, and it lived only on the
     * Check-In tab. Opens that same Master QR window in place. Shown while at
     * least one guest is seated, whatever the list's filter; never on an
     * event without Check-In, where the button would be an upgrade wall.
     */
    updateQrSignCallout(hasAssignments) {
        const list = document.getElementById('seatingUnassignedList');
        if (!list) return;
        const checkin = window.dashboardCheckin;
        const show = !!hasAssignments && !!checkin && checkin.locked !== true
            && typeof checkin.showMasterQrModal === 'function';
        let box = document.getElementById('seatingQrSignCallout');
        if (!show) { if (box) box.hidden = true; return; }
        if (!box) {
            box = document.createElement('div');
            box.id = 'seatingQrSignCallout';
            box.className = 'seating-qr-sign seating-unassigned-collapsible';
            list.insertAdjacentElement('afterend', box);
            box.addEventListener('click', (e) => {
                if (e.target.closest('.seating-qr-sign-btn')) window.dashboardCheckin?.showMasterQrModal?.(this.formId);
            });
        }
        box.innerHTML = `
            <div class="seating-qr-sign-title"><i class="bi bi-qr-code" aria-hidden="true"></i> ${this.escapeHtml(this.t('qrSignTitle'))}</div>
            <div class="seating-qr-sign-desc">${this.escapeHtml(this.t('qrSignDesc'))}</div>
            <button type="button" class="btn btn-sm btn-outline-primary seating-qr-sign-btn">
                <i class="bi bi-printer me-1" aria-hidden="true"></i>${this.escapeHtml(this.t('qrSignBtn'))}
            </button>`;
        box.hidden = false;
    }

    updateUnassignedCounter() {
        const assignedKeys = this.getAssignedMemberKeys();
        const totalUnassigned = this._countUnassignedMembers();

        // "Clear all assignments" (More menu) only exists while there is
        // something to clear — hidden on an empty chart, reappears with the
        // first assignment, vanishes again when assignments drop back to zero.
        const hasAssignments = assignedKeys.size > 0;
        const clearItem = document.getElementById('seatingClearAllItem');
        const clearDivider = document.getElementById('seatingClearAllDividerItem');
        if (clearItem) clearItem.style.display = hasAssignments ? '' : 'none';
        if (clearDivider) clearDivider.style.display = hasAssignments ? '' : 'none';
        this.updateQrSignCallout(hasAssignments);
        const counter = document.getElementById('seatingUnassignedCounter');
        if (counter) {
            const label = counter.querySelector('span');
            const state = totalUnassigned === 0 ? 'done' : 'pending';
            if (label) {
                // Split the number from the word so mobile can show a compact
                // "👥 N" pill (the word is hidden via CSS) that fits on the
                // toolbar's first row beside the mode toggle.
                label.innerHTML = `<span class="seating-unassigned-num">${totalUnassigned}</span> <span class="seating-unassigned-word">${this.escapeHtml(this.t('unassigned'))}</span>`;
                counter.dataset.state = state;
            } else {
                // Fallback for older markup (badge-style)
                counter.textContent = `${totalUnassigned} ${this.t('unassigned')}`;
                counter.className = `ms-auto badge ${state === 'done' ? 'bg-success' : 'bg-info'}`;
            }
        }

        // Keep the mobile "Unassigned" tab's count badge in sync.
        const tabCount = document.getElementById('seatingMobileTabCount');
        if (tabCount) {
            tabCount.textContent = totalUnassigned;
            tabCount.classList.toggle('is-zero', totalUnassigned === 0);
        }
    }

    // Build the mobile Tables/Unassigned tab switcher once and apply the default
    // tab. Hidden on desktop (d-md-none). Idempotent — safe to call on every setup.
    _setupMobileSeatingTabs() {
        const layout = document.getElementById('seatingLayout');
        if (!layout) return;
        if (!document.getElementById('seatingMobileTabs')) {
            const bar = document.createElement('div');
            bar.id = 'seatingMobileTabs';
            bar.className = 'seating-mobile-tabs d-md-none';
            bar.setAttribute('role', 'tablist');
            // Unassigned sits on the LEFT (mirrors the desktop layout, where the
            // Unassigned column is on the left); Tables on the right.
            bar.innerHTML = `
                <button type="button" class="seating-mobile-tab" data-mobile-tab="unassigned" role="tab" aria-selected="false">
                    <i class="bi bi-people me-1"></i>${this.escapeHtml(this.t('unassignedTab'))}
                    <span class="seating-mobile-tab-count is-zero" id="seatingMobileTabCount">0</span>
                </button>
                <button type="button" class="seating-mobile-tab active" data-mobile-tab="tables" role="tab" aria-selected="true">
                    <i class="bi bi-grid-3x3-gap me-1"></i>${this.escapeHtml(this.t('tablesTab'))}
                </button>`;
            layout.parentNode.insertBefore(bar, layout);
            bar.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-mobile-tab]');
                if (btn) this._applyMobileTab(btn.dataset.mobileTab);
            });
        }
        // Adaptive default: land on the Unassigned list while there are still
        // guests to seat (that's the host's to-do, with the count front-and-center),
        // and on the Tables overview once everyone's assigned (review the finished
        // plan). Once the host taps a tab, this._mobileTab is set and we keep it.
        const defaultTab = this._mobileTab || (this._countUnassignedMembers() > 0 ? 'unassigned' : 'tables');
        this._applyMobileTab(defaultTab);
        // Populate the tab's count badge immediately — this setup runs AFTER the
        // first render(), so without this the badge would keep its initial 0 until
        // the next re-render.
        this.updateUnassignedCounter();
    }

    _applyMobileTab(tab) {
        this._mobileTab = tab;
        const layout = document.getElementById('seatingLayout');
        if (layout) {
            layout.classList.toggle('mobile-tab-tables', tab === 'tables');
            layout.classList.toggle('mobile-tab-unassigned', tab === 'unassigned');
        }
        document.querySelectorAll('#seatingMobileTabs .seating-mobile-tab').forEach(b => {
            const on = b.dataset.mobileTab === tab;
            b.classList.toggle('active', on);
            b.setAttribute('aria-selected', String(on));
        });
    }

    // Lift open row-action menus into <body> so no ancestor can clip them.
    //
    // The unassigned list is a scroll box (overflow-y:auto) inside an
    // overflow:hidden card, and the table cards are overflow:hidden too. Bootstrap's
    // Popper (even with strategy:'fixed') was still being clipped/mis-placed in the
    // live dashboard, so the three-dots menu showed only its top edge or appeared
    // detached. We turn off Popper (data-bs-display="static") and, while a menu is
    // open, move it to <body> with fixed positioning computed from its toggle — a
    // <body> child can't be clipped by any ancestor's overflow/transform. The menu
    // is returned to its row on close. Listeners are delegated and bound once per
    // container; the static attribute is (re)applied to toggles on every render.
    _setupRowMenuClipEscape(container) {
        if (!container) return;
        // Disable Popper for these toggles so it can't fight our manual placement.
        container.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(t => t.setAttribute('data-bs-display', 'static'));

        // Safety net: drop any menu orphaned in <body> by a re-render that happened
        // while it was open (its toggle is gone, so it would never be restored).
        document.querySelectorAll('body > .dropdown-menu.seating-escaped-menu').forEach(m => {
            if (!m._escHome || !m._escHome.isConnected) m.remove();
        });

        if (container._rowMenuEscapeBound) return;
        container._rowMenuEscapeBound = true;

        const positionMenu = (toggle, menu) => {
            const r = toggle.getBoundingClientRect();
            const vw = document.documentElement.clientWidth;
            const vh = document.documentElement.clientHeight;
            const mw = menu.offsetWidth;
            const mh = menu.offsetHeight;
            const gap = 4;
            // Anchor the menu's corner to the three-dots toggle and open it INTO its
            // own row/card so it never spills onto the neighboring table. The toggle
            // sits at the card's inline-END edge — the RIGHT edge in English (LTR),
            // the LEFT edge in Hebrew/Arabic (RTL). So the open direction must MIRROR:
            //   • LTR: align the menu's RIGHT edge to the toggle → opens down-LEFT,
            //          staying inside the card (toggle is at the card's right edge).
            //   • RTL: align the menu's LEFT edge to the toggle → opens down-RIGHT,
            //          staying inside the card (toggle is at the card's left edge).
            // A non-mirrored "always open left" was the reopened RTL bug: from a
            // left-edge toggle the menu extended left, out of its card and visually
            // under the table to its left. We only flip to the other direction when
            // there isn't room on that side (e.g. a toggle near a screen edge), so
            // the menu can never detach to the far side of the screen.
            const isRTL = document.documentElement.getAttribute('dir') === 'rtl';
            let left;
            if (isRTL) {
                left = Math.round(r.left);                               // align left edges → open down-right
                if (left + mw > vw - 8) left = Math.round(r.right - mw); // no room on the right → open down-left
            } else {
                left = Math.round(r.right - mw);                         // align right edges → open down-left
                if (left < 8) left = Math.round(r.left);                 // no room on the left → open down-right
            }
            let top = Math.round(r.bottom + gap);
            if (top + mh > vh - 8) top = Math.round(r.top - gap - mh); // flip up when no room below
            top = Math.max(8, Math.min(top, vh - mh - 8));
            left = Math.max(8, Math.min(left, vw - mw - 8));
            // Set the position props with `important` priority. In RTL the global
            // rule `html[dir="rtl"] .dropdown-menu-end { left:0 !important;
            // right:auto !important }` (rtl-system.css) outranks a plain inline
            // `left`, so the escaped table menu (which carries .dropdown-menu-end)
            // was pinned to the far-LEFT of the viewport no matter what we computed
            // above — the reopened "pops up on a random part of the page" RTL bug.
            // `top` looked fine only because no !important rule competed for it.
            // An important inline declaration beats the stylesheet's !important.
            menu.style.setProperty('position', 'fixed', 'important');
            menu.style.setProperty('top', top + 'px', 'important');
            menu.style.setProperty('left', left + 'px', 'important');
            menu.style.setProperty('right', 'auto', 'important');
            menu.style.setProperty('margin', '0', 'important');
            menu.style.zIndex = '1060';
            // The menu's own text/icon orientation follows the PAGE language.
            menu.setAttribute('dir', document.documentElement.getAttribute('dir') === 'rtl' ? 'rtl' : 'ltr');
        };

        container.addEventListener('show.bs.dropdown', (e) => {
            const toggle = e.target.closest('[data-bs-toggle="dropdown"]');
            if (!toggle) return;
            const menu = toggle.parentElement.querySelector(':scope > .dropdown-menu');
            if (!menu) return;
            toggle._escMenu = menu;
            menu._escHome = toggle.parentElement;
            menu.classList.add('seating-escaped-menu');
            menu.style.visibility = 'hidden'; // hide until positioned to avoid a flash
            document.body.appendChild(menu);
        });
        container.addEventListener('shown.bs.dropdown', (e) => {
            const toggle = e.target.closest('[data-bs-toggle="dropdown"]');
            if (!toggle || !toggle._escMenu) return;
            positionMenu(toggle, toggle._escMenu); // measure + place once it's laid out
            toggle._escMenu.style.visibility = '';
        });
        container.addEventListener('hide.bs.dropdown', (e) => {
            const toggle = e.target.closest('[data-bs-toggle="dropdown"]');
            if (!toggle || !toggle._escMenu) return;
            const menu = toggle._escMenu;
            menu.removeAttribute('style');
            menu.removeAttribute('dir');
            menu.classList.remove('seating-escaped-menu');
            if (menu._escHome && menu._escHome.isConnected) menu._escHome.appendChild(menu);
            else menu.remove(); // its row was re-rendered away — drop the orphan
            toggle._escMenu = null;
        });
    }

    // --- Drag and Drop ---

    setupGuestDragEvents(container) {
        // The unassigned list is a scroll box (`max-height: 600px; overflow-y:
        // auto`) inside an `overflow:hidden` card, which clips the row action
        // dropdowns — only the menu's top edge showed. Popper's `strategy:'fixed'`
        // wasn't reliable in the live dashboard, so we lift the open menu into
        // <body> instead (see _setupRowMenuClipEscape).
        this._setupRowMenuClipEscape(container);

        container.querySelectorAll('[draggable="true"]').forEach(item => {
            item.addEventListener('dragstart', (e) => {
                // Typing in the table-number input must not start a row drag.
                if (e.target.closest('input, textarea, select')) { e.preventDefault(); return; }
                let payload;
                const mode = item.dataset.dragMode || 'member';
                if (mode === 'category') {
                    payload = {
                        dragMode: 'category',
                        categoryKey: item.dataset.categoryKey,
                        source: 'unassigned'
                    };
                } else if (this._bulkSelected.size > 1 && this._bulkSelected.has(item.dataset.responseId)) {
                    // Multi-drag: the dragged card is part of a multi-party selection,
                    // so bundle EVERY selected party's members and assign them all at
                    // once on drop. Expand each selected responseId to its member keys.
                    const memberKeys = [];
                    for (const rid of this._bulkSelected) {
                        const resp = this.allAttendingResponses.find(r => r.id === rid);
                        if (!resp) continue;
                        for (const mem of this.buildMembersForResponse(resp)) {
                            memberKeys.push(`${mem.responseId}::${mem.guestName}`);
                        }
                    }
                    payload = {
                        dragMode: 'multi',
                        memberKeys,
                        source: 'unassigned'
                    };
                } else {
                    payload = {
                        responseId: item.dataset.responseId,
                        guestName: item.dataset.guestName,
                        dragMode: mode,
                        source: 'unassigned'
                    };
                }
                e.dataTransfer.setData('text/plain', JSON.stringify(payload));
                item.classList.add('dragging');
            });

            item.addEventListener('dragend', () => {
                item.classList.remove('dragging');
            });
        });
    }

    setupTableDropEvents(container) {
        // Table cards are `overflow:hidden` too, so their per-seat action menus
        // can be clipped by the card edge. Use the same <body>-escape as the
        // unassigned list for consistent, never-clipped row menus.
        this._setupRowMenuClipEscape(container);

        // The drop target is the whole card (not just `.table-seats`) so a
        // collapsed table — whose `.table-seats` is `display:none` — can still
        // accept a dropped guest on its visible header WITHOUT expanding. This
        // keeps each table's collapsed/expanded state stable during drag.
        container.querySelectorAll('.seating-table-card').forEach(dropZone => {
            dropZone.addEventListener('dragover', (e) => {
                if (this._reorderTableId) return; // reorder drag — let the col handler take it
                e.preventDefault();
                dropZone.classList.add('drag-over');
            });

            dropZone.addEventListener('dragleave', (e) => {
                // Only remove if leaving the card entirely
                if (!dropZone.contains(e.relatedTarget)) {
                    dropZone.classList.remove('drag-over');
                }
            });

            dropZone.addEventListener('drop', (e) => {
                if (this._reorderTableId) return; // reorder drop is handled at the col level
                e.preventDefault();
                dropZone.classList.remove('drag-over');

                try {
                    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (data && data.type === 'reorder') return;
                    const targetTableId = dropZone.dataset.tableId;
                    this.assignGuestToTable(data, targetTableId);
                } catch (err) {
                    console.error('Drop error:', err);
                }
            });
        });

        // Setup seated item drag (to move between tables)
        container.querySelectorAll('.seating-seat-item').forEach(item => {
            item.addEventListener('dragstart', (e) => {
                // Typing in the table-number input must not start a seat drag.
                if (e.target.closest('input, textarea, select')) { e.preventDefault(); return; }
                e.dataTransfer.setData('text/plain', JSON.stringify({
                    responseId: item.dataset.responseId,
                    guestName: item.dataset.guestName,
                    dragMode: 'member',
                    source: 'table',
                    sourceTableId: item.dataset.sourceTableId
                }));
            });
        });

        // Allow dropping back to unassigned list
        const unassignedList = document.getElementById('seatingUnassignedList');
        if (unassignedList) {
            unassignedList.addEventListener('dragover', (e) => e.preventDefault());
            unassignedList.addEventListener('drop', (e) => {
                e.preventDefault();
                try {
                    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (data.source === 'table' && data.sourceTableId) {
                        this.removeGuestFromTable(data.responseId, data.guestName, data.sourceTableId);
                    }
                } catch (err) { /* ignore */ }
            });
        }
    }

    setupTableReorderEvents(container) {
        // Grip-handle dragstart: capture the table id; use the card as the drag preview.
        container.querySelectorAll('.seating-table-drag-handle').forEach(handle => {
            handle.addEventListener('dragstart', (e) => {
                const tableId = handle.dataset.tableId;
                this._reorderTableId = tableId;
                document.body.classList.add('seating-reordering');
                const card = handle.closest('.seating-table-card');
                if (card) {
                    card.classList.add('reorder-source');
                    try {
                        const r = card.getBoundingClientRect();
                        e.dataTransfer.setDragImage(card, e.clientX - r.left, e.clientY - r.top);
                    } catch (err) { /* setDragImage may fail in some browsers */ }
                }
                e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'reorder', tableId }));
                e.dataTransfer.effectAllowed = 'move';
            });

            handle.addEventListener('dragend', () => {
                this._reorderTableId = null;
                document.body.classList.remove('seating-reordering');
                container.querySelectorAll('.reorder-source, .reorder-target')
                    .forEach(el => el.classList.remove('reorder-source', 'reorder-target'));
            });
        });

        // Each card listens for reorder dragover/drop.
        container.querySelectorAll('.seating-table-col').forEach(col => {
            col.addEventListener('dragover', (e) => {
                if (!this._reorderTableId) return;
                if (col.dataset.tableId === this._reorderTableId) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                container.querySelectorAll('.reorder-target')
                    .forEach(el => el.classList.remove('reorder-target'));
                col.querySelector('.seating-table-card')?.classList.add('reorder-target');
            });

            col.addEventListener('dragleave', (e) => {
                if (!this._reorderTableId) return;
                const card = col.querySelector('.seating-table-card');
                if (card && !col.contains(e.relatedTarget)) {
                    card.classList.remove('reorder-target');
                }
            });

            col.addEventListener('drop', (e) => {
                if (!this._reorderTableId) return;
                e.preventDefault();
                e.stopPropagation();
                const srcId = this._reorderTableId;
                const targetId = col.dataset.tableId;
                if (srcId && targetId && srcId !== targetId) {
                    this.moveTableBefore(srcId, targetId);
                }
            });
        });
    }

    // --- Assignment Logic ---

    /**
     * Place ALL `candidates` on `table`. Hosts may intentionally over-allocate a
     * table, so multi-guest / bulk / group / category drops are NOT capped at the
     * table capacity — this matches the single-guest drag path (which never
     * capped) and removes the inconsistent block that prevented dragging a guest
     * + plus-ones (or a bulk selection) onto a nearly-full table. The table still
     * shows an "over" capacity badge; it just no longer refuses the assignment.
     * (Auto-assign fills tables server-side and is unaffected by this method.)
     */
    _fillTableWithCandidates(table, candidates) {
        candidates.forEach(m => table.seats.push(m));
        return { placed: candidates.length, overflow: 0 };
    }

    _notifyPartialFill(placed, total) {
        if (total > placed) {
            const remaining = total - placed;
            const msg = this.t('partialFillToast')
                .replace('{placed}', placed)
                .replace('{total}', total)
                .replace('{remaining}', remaining);
            this.showToast(msg, placed > 0 ? 'info' : 'error');
        } else if (placed > 0 && total === placed && placed > 1) {
            const msg = this.t('seatedAllToast').replace('{count}', placed);
            this.showToast(msg, 'success');
        }
    }

    assignGuestToTable(data, tableId) {
        this.pushUndoState();
        const table = this.config.tables.find(t => t.id === tableId);
        if (!table) return;
        if (!table.seats) table.seats = [];

        const assignedKeys = this.getAssignedMemberKeys();

        if (data.dragMode === 'category') {
            // Collect every unassigned member whose category (tag, else relationship) matches.
            // Only those the reply-status filter shows: a host who narrowed the
            // pool to "Coming" must not seat the awaiting guests hidden from it.
            const candidates = [];
            for (const r of this.allAttendingResponses) {
                const catKey = this.getCategoryForResponse(r).key;
                if (catKey !== data.categoryKey) continue;
                if (!this._passesStatusFilter(r)) continue;
                const isGroup = this.buildMembersForResponse(r).length > 1;
                for (const m of this.buildMembersForResponse(r)) {
                    if (!assignedKeys.has(`${m.responseId}::${m.guestName}`)) {
                        candidates.push({ ...m, isGroupMember: isGroup });
                    }
                }
            }
            const { placed, overflow } = this._fillTableWithCandidates(table, candidates);
            this._notifyPartialFill(placed, placed + overflow);
        } else if (data.dragMode === 'multi') {
            // Collect every unassigned member by the supplied keys
            const candidates = [];
            for (const key of (data.memberKeys || [])) {
                if (assignedKeys.has(key)) continue;
                const [responseId, ...rest] = key.split('::');
                const guestName = rest.join('::');
                const response = this.allAttendingResponses.find(r => r.id === responseId);
                if (!response) continue;
                const isGroup = this.buildMembersForResponse(response).length > 1;
                candidates.push({ responseId, guestName, isGroupMember: isGroup });
            }
            const { placed, overflow } = this._fillTableWithCandidates(table, candidates);
            this._notifyPartialFill(placed, placed + overflow);
            this._bulkSelected.clear(); // selection consumed by the assignment
        } else if (data.dragMode === 'group') {
            // Move every unassigned member of the response, but only as many as fit
            const response = this.allAttendingResponses.find(r => r.id === data.responseId);
            if (!response) return;
            const allMembers = this.buildMembersForResponse(response);
            const candidates = allMembers
                .filter(m => !assignedKeys.has(`${m.responseId}::${m.guestName}`))
                .map(m => ({ ...m, isGroupMember: true }));
            const { placed, overflow } = this._fillTableWithCandidates(table, candidates);
            this._notifyPartialFill(placed, placed + overflow);
        } else {
            // Single member — remove from any existing table first
            if (data.source === 'table' && data.sourceTableId) {
                const srcTable = this.config.tables.find(t => t.id === data.sourceTableId);
                if (srcTable) {
                    srcTable.seats = (srcTable.seats || []).filter(s =>
                        !(s.responseId === data.responseId && s.guestName === data.guestName)
                    );
                }
            }
            const response = this.allAttendingResponses.find(r => r.id === data.responseId);
            const isGroup = response ? (this.buildMembersForResponse(response).length > 1) : false;
            table.seats.push({
                responseId: data.responseId,
                guestName: data.guestName,
                isGroupMember: isGroup
            });
        }

        this.isDirty = true;
        this.render();
        this.autoSave();
    }

    removeGuestFromTable(responseId, guestName, tableId) {
        this.pushUndoState();
        const table = this.config.tables.find(t => t.id === tableId);
        if (table) {
            table.seats = (table.seats || []).filter(s =>
                !(s.responseId === responseId && s.guestName === guestName)
            );
            this.isDirty = true;
            this.render();
            this.autoSave();
        }
    }

    buildMembersForResponse(response) {
        // Shared resolver (assets/js/utils/guest-counts.js) — seats must match
        // the party size check-in and the dashboard report for the same row.
        const _counts = resolveGuestCounts(response);
        const totalGuests = _counts.total;
        const adultCount = _counts.hasSplit ? _counts.adults : totalGuests;
        const childrenCount = _counts.hasSplit ? _counts.children : 0;

        const isMaybe = this._isMaybeResponse(response);
        const name = response.guestName || response.name || this.t('guest');
        // Per-guest meal choice (task #509): individualNames[].dietary keys are
        // resolved to the host's option text via admin.js's shared resolver.
        const mealLabelFor = (entry) => {
            if (!entry || typeof entry !== 'object' || !entry.dietary) return '';
            if (typeof resolveGuestMealLabel !== 'function') return '';
            return resolveGuestMealLabel(entry.dietary, this.currentFormData) || '';
        };
        const pos1Entry = Array.isArray(response.individualNames)
            ? response.individualNames.find(e => e && typeof e === 'object' && e.position === 1)
            : null;
        // Seat names by the shared rule (task #965), the same ones server
        // auto-assign and check-in give: "Tsivia & Hillel" with Hillel listed
        // again is Tsivia, "Irwin & Susan" for 2 is Irwin and Susan, and "Esti"
        // on the Stolbach Family's invitation is Esti Stolbach.
        const H = window.HouseholdNames;
        const group = response.inviteGroupName;
        const mainSeats = H ? H.mainSeatNames(name, H.partyOthers(response.individualNames, name), totalGuests, group) : [name];
        const seatNameFor = (n) => (H ? H.memberSeatName(n, name, group) : n);
        const members = mainSeats.map((seatName, i) => ({
            responseId: response.id, guestName: seatName, isGroupMember: i > 0, isChild: false, isMaybe,
            ...(i === 0 ? { meal: mealLabelFor(pos1Entry) } : { hasExplicitType: true })
        }));

        // individualNames can be objects {position, name} or plain strings
        if (response.individualNames && Array.isArray(response.individualNames)) {
            response.individualNames.forEach((entry) => {
                const entryName = (typeof entry === 'object' && entry !== null) ? (entry.name || '') : String(entry || '');
                if (!entryName.trim()) return;
                // Skip if this is the primary guest (position 1 or name matches)
                const pos = (typeof entry === 'object' && entry !== null) ? entry.position : undefined;
                if (pos === 1 || entryName === name) return;
                const seatName = seatNameFor(entryName);
                // Avoid duplicates
                if (!members.some(m => m.guestName === seatName)) {
                    // Newer responses stamp each name with guestType ('adult'|'child')
                    // — when present it overrides the positional fallback below.
                    const explicitType = (typeof entry === 'object' && entry !== null) ? entry.guestType : undefined;
                    members.push({
                        responseId: response.id,
                        guestName: seatName,
                        isGroupMember: true,
                        isChild: explicitType === 'child',
                        hasExplicitType: explicitType === 'child' || explicitType === 'adult',
                        isMaybe,
                        meal: mealLabelFor(entry)
                    });
                }
            });
        }

        // A host who lowers the party in the edit window keeps the names on the
        // row, so names can outnumber the party. Seats follow the party.
        if (members.length > totalGuests) members.length = totalGuests;

        // Pad remaining unnamed guests up to totalGuests
        while (members.length < totalGuests) {
            members.push({
                responseId: response.id,
                guestName: `${name} +${members.length}`,
                isGroupMember: true,
                isChild: false,
                isMaybe
            });
        }

        // Children fill the trailing positions — index >= adultCount = child.
        // The form collects totals (not per-name age) so we use this convention
        // for entries WITHOUT an explicit guestType (older responses, padded
        // unnamed guests).
        for (let i = 0; i < members.length; i++) {
            if (!members[i].hasExplicitType && i >= adultCount) members[i].isChild = true;
        }

        // Per-member host override (set from the seating tab) wins over the
        // position-based default. Lets a planner correct edge cases where the
        // RSVPer entered names in a different order than adults-then-children.
        const overrides = (this.config && this.config.childOverrides) || {};
        for (const m of members) {
            const key = `${m.responseId}::${m.guestName}`;
            if (Object.prototype.hasOwnProperty.call(overrides, key)) {
                m.isChild = !!overrides[key];
            }
        }
        return members;
    }

    /**
     * Repair seats orphaned by a guest rename. Seats store guestName as it
     * was at assignment time; when the host edits a name in the Responses
     * tab nothing re-links the seat — the chart keeps the old name forever
     * and the renamed guest reappears as unassigned. For each response,
     * re-derive the current member list and rename any seat whose name no
     * longer matches, pairing stale seats with unseated members by name
     * similarity first (a rename is usually a small edit), then by order.
     * childOverrides keyed by the old name move with the rename.
     * Idempotent; returns true if anything changed (caller persists).
     */
    _reconcileSeatNames() {
        if (!this.config || !Array.isArray(this.config.tables)) return false;
        if (!Array.isArray(this.allAttendingResponses) || this.allAttendingResponses.length === 0) return false;

        const seatsByResponse = new Map(); // responseId -> seats in table/seat order
        for (const table of this.config.tables) {
            for (const seat of (table.seats || [])) {
                if (!seat || !seat.responseId) continue;
                if (!seatsByResponse.has(seat.responseId)) seatsByResponse.set(seat.responseId, []);
                seatsByResponse.get(seat.responseId).push(seat);
            }
        }

        let changed = false;
        for (const resp of this.allAttendingResponses) {
            const seats = seatsByResponse.get(resp.id);
            if (!seats || seats.length === 0) continue;

            const memberNames = this.buildMembersForResponse(resp).map(m => m.guestName);
            const currentSet = new Set(memberNames);
            const staleSeats = seats.filter(s => !currentSet.has(s.guestName));
            if (staleSeats.length === 0) continue;

            const seatedNames = new Set(seats.map(s => s.guestName));
            let unseated = memberNames.filter(n => !seatedNames.has(n));
            if (unseated.length === 0) continue; // party shrank — leave extras alone

            // Best-similarity pairs first, so fixing a typo in one member of a
            // group never re-labels a different member's seat.
            const pairs = [];
            for (const seat of staleSeats) {
                for (const name of unseated) {
                    pairs.push({ seat, name, d: this._nameEditDistance(seat.guestName, name) });
                }
            }
            pairs.sort((a, b) => a.d - b.d);
            const doneSeats = new Set();
            for (const { seat, name } of pairs) {
                if (doneSeats.has(seat) || !unseated.includes(name)) continue;
                doneSeats.add(seat);
                unseated = unseated.filter(n => n !== name);
                const oldKey = `${resp.id}::${seat.guestName}`;
                seat.guestName = name;
                const ov = this.config.childOverrides;
                if (ov && Object.prototype.hasOwnProperty.call(ov, oldKey)) {
                    ov[`${resp.id}::${name}`] = ov[oldKey];
                    delete ov[oldKey];
                }
                changed = true;
            }
        }
        if (changed) this.isDirty = true;
        return changed;
    }

    // Levenshtein distance on lowercased names — guest names are short, so the
    // O(len²) table is negligible. Used only to rank rename candidates.
    _nameEditDistance(a, b) {
        a = String(a || '').toLowerCase();
        b = String(b || '').toLowerCase();
        if (a === b) return 0;
        const m = a.length, n = b.length;
        if (!m || !n) return m + n;
        let prev = Array.from({ length: n + 1 }, (_, j) => j);
        for (let i = 1; i <= m; i++) {
            const row = [i];
            for (let j = 1; j <= n; j++) {
                row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
            }
            prev = row;
        }
        return prev[n];
    }

    toggleChildOverride(responseId, guestName) {
        if (!this.config) return;
        if (!this.config.childOverrides) this.config.childOverrides = {};
        const key = `${responseId}::${guestName}`;
        const current = this._getMemberFlags({ responseId, guestName }).isChild;
        // Set the explicit opposite; null/undefined would fall back to heuristic
        // and feel like the click did nothing if the heuristic agrees.
        this.pushUndoState();
        this.config.childOverrides[key] = !current;
        this.isDirty = true;
        this.render();
        this.autoSave();
    }

    /**
     * Whether a response is a "maybe" (undecided) RSVP.
     * Includes attending values like 'maybe', 'Maybe', or 'MAYBE'.
     */
    _isMaybeResponse(response) {
        const a = response && response.attending;
        if (typeof a !== 'string') return false;
        return a.trim().toLowerCase() === 'maybe';
    }

    /**
     * Look up the live child/maybe flags for a seated guest by matching
     * the seat's (responseId, guestName) against the response's members.
     * Re-derived on every render so re-RSVPs update the indicators.
     */
    _getMemberFlags(seat) {
        if (!seat) return { isChild: false, isMaybe: false };
        const resp = this.allAttendingResponses.find(r => r.id === seat.responseId);
        if (!resp) return { isChild: false, isMaybe: !!seat.isMaybe };
        const isMaybe = this._isMaybeResponse(resp);
        const members = this.buildMembersForResponse(resp);
        const member = members.find(m => m.guestName === seat.guestName);
        return {
            isChild: !!(member && member.isChild),
            isMaybe
        };
    }

    // --- Table CRUD ---

    showAddTableModal() {
        this._showTableModal(null);
    }

    showEditTableModal(tableId) {
        const table = this.config.tables.find(t => t.id === tableId);
        if (!table) return;
        this._showTableModal(table);
    }

    _showTableModal(existingTable) {
        const isEdit = !!existingTable;
        const modalId = 'seatingTableModal';

        // Remove existing modal if any
        const existing = document.getElementById(modalId);
        if (existing) existing.remove();

        const modal = document.createElement('div');
        modal.id = modalId;
        modal.className = 'modal fade';
        modal.tabIndex = -1;
        modal.innerHTML = `
            <div class="modal-dialog modal-sm">
                <div class="modal-content">
                    <div class="modal-header py-2">
                        <h6 class="modal-title">${isEdit ? this.t('editTable') : this.t('addTable')}</h6>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        ${!isEdit ? `<div class="mb-3">
                            <label class="form-label small fw-medium">${this.t('numberOfTables')}</label>
                            <input type="number" class="form-control form-control-sm" id="seatingTableCount"
                                min="1" max="50" value="1">
                        </div>` : ''}
                        <div class="mb-3" id="seatingTableNameGroup">
                            <label class="form-label small fw-medium">${this.t('tableName')}</label>
                            <input type="text" class="form-control form-control-sm" id="seatingTableName"
                                value="${isEdit ? this.escapeAttr(existingTable.name) : ''}"
                                placeholder="${this.t('tableNamePlaceholder')}">
                        </div>
                        <div class="mb-3" id="seatingTableDescGroup">
                            <label class="form-label small fw-medium">${this.t('tableDescription')}</label>
                            <textarea class="form-control form-control-sm" id="seatingTableDescription" rows="2"
                                placeholder="${this.t('tableDescriptionPlaceholder')}">${isEdit ? this.escapeHtml(existingTable.description || '') : ''}</textarea>
                            <div class="form-text" style="font-size:0.72rem;"><i class="bi bi-eye-slash me-1"></i>${this.t('tableDescriptionHint')}</div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label small fw-medium">${this.t('capacity')}</label>
                            <input type="number" class="form-control form-control-sm" id="seatingTableCapacity"
                                min="1" max="50" value="${isEdit ? existingTable.capacity : 10}">
                        </div>
                    </div>
                    <div class="modal-footer py-2">
                        <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">${this.t('cancel')}</button>
                        <button type="button" class="btn btn-primary btn-sm" id="seatingTableSaveBtn">
                            ${isEdit ? this.t('save') : this.t('addTable')}
                        </button>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Count input toggles name field visibility and button text (add mode only)
        if (!isEdit) {
            const countInput = modal.querySelector('#seatingTableCount');
            const nameGroup = modal.querySelector('#seatingTableNameGroup');
            const descGroup = modal.querySelector('#seatingTableDescGroup');
            const saveBtn = modal.querySelector('#seatingTableSaveBtn');
            countInput.addEventListener('input', () => {
                const count = parseInt(countInput.value) || 1;
                if (count > 1) {
                    // Bulk-add only sets capacity; per-table name/note are
                    // meaningless across many tables, so hide both.
                    nameGroup.style.display = 'none';
                    descGroup.style.display = 'none';
                    saveBtn.textContent = this.t('addTables');
                } else {
                    nameGroup.style.display = '';
                    descGroup.style.display = '';
                    saveBtn.textContent = this.t('addTable');
                }
            });
        }

        // Shape picker removed (task #499): the seating view is a list, so the
        // choice never surfaced anywhere a host could see. Editing a table keeps
        // whatever shape it was saved with; new tables are round.
        const selectedType = isEdit ? existingTable.type : 'round';

        // Save handler
        modal.querySelector('#seatingTableSaveBtn').addEventListener('click', () => {
            const capacity = parseInt(modal.querySelector('#seatingTableCapacity').value) || 10;
            const count = isEdit ? 1 : (parseInt(modal.querySelector('#seatingTableCount')?.value) || 1);

            // Validate before snapshotting so early returns don't pollute undo stack
            if (!isEdit && count <= 1) {
                const name = modal.querySelector('#seatingTableName').value.trim();
                if (!name) {
                    modal.querySelector('#seatingTableName').classList.add('is-invalid');
                    return;
                }
            }
            if (isEdit) {
                const name = modal.querySelector('#seatingTableName').value.trim();
                if (!name) {
                    modal.querySelector('#seatingTableName').classList.add('is-invalid');
                    return;
                }
            }

            this.pushUndoState();

            if (isEdit) {
                const name = modal.querySelector('#seatingTableName').value.trim();
                existingTable.name = name;
                existingTable.capacity = capacity;
                existingTable.type = selectedType;
                existingTable.description = modal.querySelector('#seatingTableDescription').value.trim();
            } else if (count > 1) {
                const startNum = this.config.tables.length + 1;
                for (let i = 0; i < count; i++) {
                    this.config.tables.push({
                        id: this.generateId(),
                        name: `Table ${startNum + i}`,
                        capacity: capacity,
                        type: selectedType,
                        seats: []
                    });
                }
            } else {
                const name = modal.querySelector('#seatingTableName').value.trim();
                const description = modal.querySelector('#seatingTableDescription').value.trim();
                this.config.tables.push({
                    id: this.generateId(),
                    name: name,
                    description: description,
                    capacity: capacity,
                    type: selectedType,
                    seats: []
                });
            }

            this.isDirty = true;
            this.render();
            this.autoSave();

            const bsModal = bootstrap.Modal.getInstance(modal);
            if (bsModal) bsModal.hide();
        });

        const bsModal = new bootstrap.Modal(modal);
        bsModal.show();

        // Clean up on close
        modal.addEventListener('hidden.bs.modal', () => {
            modal.remove();
        });
    }

    showAddRuleModal() {
        const responses = this.allAttendingResponses;
        // A–Z, and searchable (task #971): a long guest list was one long
        // <select> to scroll. Two guests of the same name are told apart by
        // their email or phone.
        const nameOf = (r) => String(r.guestName || r.name || r.id || '').trim();
        const counts = new Map();
        responses.forEach(r => { const k = nameOf(r).toLowerCase(); counts.set(k, (counts.get(k) || 0) + 1); });
        const guests = responses.map(r => ({
            id: r.id,
            name: nameOf(r),
            detail: counts.get(nameOf(r).toLowerCase()) > 1 ? String(r.email || r.phoneNumber || '').trim() : ''
        })).sort((x, y) => x.name.localeCompare(y.name, undefined, { sensitivity: 'base' }));
        const comboHtml = (id) => `
                        <div class="seating-guest-combo mb-2" data-combo="${id}">
                            <input type="text" class="form-control form-control-sm" id="${id}" autocomplete="off" dir="auto"
                                   role="combobox" aria-expanded="false" aria-autocomplete="list" aria-controls="${id}-list"
                                   placeholder="${this.escapeHtml(this.t('selectGuest'))}" aria-label="${this.escapeHtml(this.t('selectGuest'))}">
                            <div class="seating-combo-list" id="${id}-list" role="listbox" hidden></div>
                        </div>`;

        const modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.innerHTML = `
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header py-2">
                        <h6 class="modal-title">${this.t('addRule')}</h6>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        ${comboHtml('ruleGuestA')}
                        ${comboHtml('ruleGuestB')}
                        <div class="btn-group w-100" role="group">
                            <input type="radio" class="btn-check" name="ruleType" id="ruleTypeTogether" value="together" checked>
                            <label class="btn btn-outline-success btn-sm" for="ruleTypeTogether">🤝 ${this.t('keepTogether')}</label>
                            <input type="radio" class="btn-check" name="ruleType" id="ruleTypeApart" value="apart">
                            <label class="btn btn-outline-danger btn-sm" for="ruleTypeApart">🚫 ${this.t('keepApart')}</label>
                        </div>
                    </div>
                    <div class="modal-footer py-1">
                        <button type="button" class="btn btn-primary btn-sm" id="saveRuleBtn">${this.t('addRule')}</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
        modal.querySelectorAll('.seating-guest-combo').forEach(el => this._wireGuestCombo(el, guests));
        const bsModal = new bootstrap.Modal(modal);
        bsModal.show();

        modal.querySelector('#saveRuleBtn').addEventListener('click', () => {
            const a = modal.querySelector('#ruleGuestA').dataset.value || '';
            const b = modal.querySelector('#ruleGuestB').dataset.value || '';
            const type = modal.querySelector('input[name="ruleType"]:checked').value;
            if (!a || !b || a === b) return;

            this.pushUndoState();
            if (!this.config.rules) this.config.rules = { keepTogether: [], keepApart: [] };
            const arr = type === 'together' ? this.config.rules.keepTogether : this.config.rules.keepApart;
            // Avoid duplicates
            const exists = arr.some(r => (r[0] === a && r[1] === b) || (r[0] === b && r[1] === a));
            if (!exists) arr.push([a, b]);

            this.isDirty = true;
            this.render();
            this.autoSave();
            bsModal.hide();
        });

        modal.addEventListener('hidden.bs.modal', () => modal.remove());
    }

    /**
     * A type-to-filter guest picker (task #971). Typing or pasting filters the
     * A–Z list at once (case and accents ignored); arrows move, Enter or a
     * click picks, an emptied box shows everyone again. The chosen guest's id
     * is the input's data-value; editing the text after a pick clears it.
     */
    _wireGuestCombo(wrap, guests) {
        const input = wrap.querySelector('input');
        const list = wrap.querySelector('.seating-combo-list');
        const fold = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        let shown = [];
        let active = -1;
        const close = () => { list.hidden = true; input.setAttribute('aria-expanded', 'false'); active = -1; };
        const paint = () => {
            list.innerHTML = shown.map((g, i) => `<div class="seating-combo-option${i === active ? ' is-active' : ''}" role="option" id="${input.id}-opt-${i}"
                aria-selected="${i === active}" data-i="${i}"><span dir="auto">${this.escapeHtml(g.name)}</span>${g.detail ? `<small dir="auto">${this.escapeHtml(g.detail)}</small>` : ''}</div>`).join('');
            if (active >= 0) {
                input.setAttribute('aria-activedescendant', `${input.id}-opt-${active}`);
                list.children[active]?.scrollIntoView({ block: 'nearest' });
            } else {
                input.removeAttribute('aria-activedescendant');
            }
        };
        const open = () => {
            const q = fold(input.value.trim());
            shown = (q ? guests.filter(g => fold(g.name).includes(q) || fold(g.detail).includes(q)) : guests).slice(0, 100);
            active = shown.length ? 0 : -1;
            list.hidden = !shown.length;
            input.setAttribute('aria-expanded', String(!list.hidden));
            paint();
        };
        const pick = (g) => {
            if (!g) return;
            input.value = g.name;
            input.dataset.value = g.id;
            close();
        };
        input.addEventListener('focus', open);
        input.addEventListener('input', () => { delete input.dataset.value; open(); });
        input.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                if (list.hidden) { open(); return; }
                if (!shown.length) return;
                active = (active + (e.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length;
                paint();
            } else if (e.key === 'Enter') {
                if (!list.hidden && shown[active]) { e.preventDefault(); pick(shown[active]); }
            } else if (e.key === 'Escape' && !list.hidden) {
                e.stopPropagation(); // the list, not the window
                close();
            }
        });
        // mousedown, not click: the input's blur would close the list first.
        list.addEventListener('mousedown', (e) => {
            const opt = e.target.closest('[data-i]');
            if (!opt) return;
            e.preventDefault();
            pick(shown[parseInt(opt.dataset.i, 10)]);
        });
        input.addEventListener('blur', () => setTimeout(close, 100));
    }

    // De-dupe (case-insensitive), trim, cap length/count — client-side sanity
    // before sending to the server (which lower-cases + de-dupes authoritatively).
    _normalizeTagList(list) {
        const seen = new Set();
        const out = [];
        for (const raw of (list || [])) {
            const v = String(raw == null ? '' : raw).trim().slice(0, 40);
            if (!v) continue;
            const k = v.toLowerCase();
            if (seen.has(k)) continue;
            seen.add(k);
            out.push(v);
            if (out.length >= 12) break;
        }
        return out;
    }

    // Host-only editor for a guest's seating tags. Tags drive the "Group by
    // shared tags" auto-assign strategy; they are never shown to or set by the
    // guest. Seeded from existing seatingTags, falling back to the guest's
    // Relationship answer (mirrors how the server seeds tags for auto-assign).
    showTagEditorModal(responseId, memberName = null) {
        const resp = (this.allAttendingResponses || []).find(r => r.id === responseId);
        if (!resp) return;
        // Member mode (task #153): tags for ONE named person in the party,
        // stored in the response's memberSeatingTags map. Party mode (default)
        // keeps the response-level tags that drive grouping and auto-assign.
        const isMemberMode = !!memberName;
        const guestName = isMemberMode
            ? memberName
            : (resp.guestName || resp.name || this.t('guest'));
        let tags;
        if (isMemberMode) {
            const map = resp.memberSeatingTags || {};
            tags = Array.isArray(map[memberName]) ? [...map[memberName]] : [];
        } else {
            // Seed from the host's own wording, so tagging a guest who answered
            // in another language doesn't mint a second tag for a category that
            // already exists.
            tags = (Array.isArray(resp.seatingTags) && resp.seatingTags.length)
                ? [...resp.seatingTags]
                : (this._canonicalRelationship(resp) ? [this._canonicalRelationship(resp)] : []);
        }
        tags = this._normalizeTagList(tags);
        // Existing event-wide categories — same union the bulk-tag modal uses — so
        // the individual editor offers them for one-tap selection instead of a bare
        // "No tags yet". Keeps both tagging workflows on one shared category list.
        // Member tags join the union so attributes like "Gluten Free" are one tap
        // to reuse on the next guest.
        const allTags = [...new Set(
            (this.allAttendingResponses || [])
                .flatMap(r => [
                    ...(Array.isArray(r.seatingTags) ? r.seatingTags : []),
                    ...Object.values(r.memberSeatingTags || {}).flat()
                ])
                .filter(Boolean)
        )].sort((a, b) => a.localeCompare(b));

        const modal = document.createElement('div');
        modal.className = 'modal fade';
        modal.innerHTML = `
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header py-2">
                        <h6 class="modal-title">${this.t('editTags')} — ${this.escapeHtml(guestName)}</h6>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <p class="text-muted small mb-2">${this.t(isMemberMode ? 'memberTagsHint' : 'tagsHint')}</p>
                        <div class="seating-tag-chips" id="seatingTagChips"></div>
                        <div class="seating-tag-suggest" id="seatingTagSuggest"></div>
                        <input type="text" class="form-control form-control-sm mt-2" id="seatingTagInput"
                            placeholder="${this.escapeAttr(this.t('addTagPlaceholder'))}" autocomplete="off" maxlength="40">
                    </div>
                    <div class="modal-footer py-1">
                        <button type="button" class="btn btn-primary btn-sm" id="seatingTagSaveBtn">${this.t('saveTags')}</button>
                    </div>
                </div>
            </div>`;
        document.body.appendChild(modal);
        const bsModal = new bootstrap.Modal(modal);
        bsModal.show();

        const chipsEl = modal.querySelector('#seatingTagChips');
        const inputEl = modal.querySelector('#seatingTagInput');
        const suggestEl = modal.querySelector('#seatingTagSuggest');
        // Existing categories not already on this guest — tap a "+ Tag" chip to add.
        const renderSuggestions = () => {
            const avail = allTags.filter(tg => !tags.some(t => t.toLowerCase() === tg.toLowerCase()));
            suggestEl.innerHTML = avail.map(tg =>
                `<button type="button" class="seating-tag-suggest-chip" data-tag="${this.escapeAttr(tg)}">+ ${this.escapeHtml(tg)}</button>`).join('');
        };
        const renderChips = () => {
            chipsEl.innerHTML = tags.length
                ? tags.map((tg, i) => `<span class="seating-tag-chip">${this.escapeHtml(tg)}<button type="button" class="seating-tag-remove" data-idx="${i}" aria-label="remove">&times;</button></span>`).join('')
                : `<span class="text-muted small">${this.t('noTagsYet')}</span>`;
            renderSuggestions();
        };
        const addTag = (raw) => {
            const v = String(raw || '').trim();
            if (!v) return;
            if (!tags.some(t => t.toLowerCase() === v.toLowerCase()) && tags.length < 12) {
                tags.push(v.slice(0, 40));
            }
            inputEl.value = '';
            renderChips();
        };
        renderChips();
        setTimeout(() => inputEl.focus(), 200);

        inputEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addTag(inputEl.value);
            } else if (e.key === 'Backspace' && !inputEl.value && tags.length) {
                tags.pop();
                renderChips();
            }
        });
        chipsEl.addEventListener('click', (e) => {
            const rm = e.target.closest('.seating-tag-remove');
            if (rm) {
                tags.splice(parseInt(rm.dataset.idx, 10), 1);
                renderChips();
            }
        });
        // Tap an existing-category suggestion to add it to this guest.
        suggestEl.addEventListener('click', (e) => {
            const chip = e.target.closest('.seating-tag-suggest-chip');
            if (chip) addTag(chip.dataset.tag);
        });

        modal.querySelector('#seatingTagSaveBtn').addEventListener('click', async () => {
            if (inputEl.value.trim()) addTag(inputEl.value);
            const saveBtn = modal.querySelector('#seatingTagSaveBtn');
            saveBtn.disabled = true;
            const finalTags = this._normalizeTagList(tags);
            try {
                const res = await fetch(`/api/seating/${this.formId}/tags/${responseId}`, {
                    method: 'PUT',
                    headers: { ...(await this._authHeaders()), 'Content-Type': 'application/json' },
                    body: JSON.stringify(isMemberMode ? { tags: finalTags, memberName } : { tags: finalTags })
                });
                const data = await res.json();
                if (data.success) {
                    // Reflect locally so re-renders + auto-assign use the new tags.
                    const savedTags = Array.isArray(data.tags) ? data.tags : finalTags;
                    if (isMemberMode) {
                        const map = { ...(resp.memberSeatingTags || {}) };
                        if (savedTags.length) map[memberName] = savedTags;
                        else delete map[memberName];
                        resp.memberSeatingTags = map;
                    } else resp.seatingTags = savedTags;
                    // No toast: the guest visibly moves into its category section below.
                    bsModal.hide();
                    // Re-render so the guest immediately moves into its category
                    // section (and out of Uncategorized) — without this the list
                    // looked unchanged until a manual reload.
                    this.renderUnassignedList();
                } else {
                    this.showToast(data.error || this.t('tagsSaveFailed'), 'danger');
                    saveBtn.disabled = false;
                }
            } catch (err) {
                console.error('Save seating tags error:', err);
                this.showToast(this.t('tagsSaveFailed'), 'danger');
                saveBtn.disabled = false;
            }
        });

        modal.addEventListener('hidden.bs.modal', () => modal.remove());
    }

    // Remove a tag-category entirely: strip that tag from every guest who has
    // it (via the bulk endpoint). Guests fall back to their RSVP relationship
    // category (or Uncategorized). Does NOT touch RSVP relationships.
    async deleteCategory(key) {
        const k = String(key || '').trim().toLowerCase();
        if (!k) return;
        const affected = (this.allAttendingResponses || []).filter(r =>
            Array.isArray(r.seatingTags) &&
            r.seatingTags.some(t => String(t || '').trim().toLowerCase() === k));
        if (!affected.length) return;

        const label = this._titleCaseTag(k);
        const msg = this.t('deleteCategoryConfirm').replace('{name}', label).replace('{count}', affected.length);
        if (!confirm(msg)) return;

        try {
            const res = await fetch(`/api/seating/${this.formId}/tags-bulk`, {
                method: 'PUT',
                headers: { ...(await this._authHeaders()), 'Content-Type': 'application/json' },
                body: JSON.stringify({ responseIds: affected.map(r => r.id), remove: [k] })
            });
            const data = await res.json();
            if (data && data.success) {
                // Reflect the removal locally so the legend + sections re-render
                // without the category, and clear any active filter on it.
                affected.forEach(r => {
                    r.seatingTags = (r.seatingTags || []).filter(t => String(t || '').trim().toLowerCase() !== k);
                });
                this.categoryFilter.delete(k);
                // No toast: the category disappears from the legend as we re-render.
                this.renderUnassignedList();
            } else {
                this.showToast((data && data.error) || this.t('categoryDeleteFailed'), 'danger');
            }
        } catch (err) {
            console.error('Delete category error:', err);
            this.showToast(this.t('categoryDeleteFailed'), 'danger');
        }
    }

    deleteTable(tableId) {
        if (!confirm(this.t('deleteTableConfirm'))) return;
        this.pushUndoState();
        this.config.tables = this.config.tables.filter(t => t.id !== tableId);
        this.isDirty = true;
        this.render();
        this.autoSave();
    }

    // --- Auto-Assign ---

    async runAutoAssign() {
        if (this.config.tables.length === 0) {
            this.showToast(this.t('createTablesFirst'), 'warning');
            return;
        }

        // Task #378: the one-click bulk "Auto-Assign" ALWAYS groups guests by
        // their shared tags — clustering people who share a tag (or, when no tags
        // are set, their relationship) onto the same tables, keeping every party
        // together and balancing how full the tables are. It must NOT inherit the
        // QR-check-in placement strategy (config.defaultStrategy): that setting
        // governs how a SINGLE guest is placed when they scan in at the door, and
        // enabling QR Auto-Assign quietly defaults it to 'roundrobin' — which
        // turned this button into a random scatter (the exact complaint here).
        // Grouping is the whole point of this button; the door strategy is separate.
        const bulkStrategy = 'tags';

        this.pushUndoState();

        const btn = document.getElementById('seatingAutoAssignBtn');
        btn.disabled = true;
        btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${this.t('assigning')}`;

        try {
            this._isSaving = true;
            const res = await fetch(`/api/seating/${this.formId}/auto-assign`, {
                method: 'POST',
                headers: {
                    ...(await this._authHeaders()),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    strategy: bulkStrategy,
                    currentConfig: this.config,
                    clientId: this._clientId
                })
            });

            const data = await res.json();
            if (data.success) {
                this.config = data.config;
                this.isDirty = false;
                this.render();
                // No toast: render() has just repopulated the whole chart with the
                // guests it seated — the result IS the screen (task #449).
            } else {
                this.showToast(data.error || this.t('autoAssignFailed'), 'danger');
            }
        } catch (error) {
            console.error('Auto-assign error:', error);
            this.showToast(this.t('autoAssignFailed'), 'danger');
        } finally {
            btn.disabled = false;
            btn.innerHTML = `<i class="bi bi-magic me-1"></i>${this.t('autoAssign')}`;
            setTimeout(() => { this._isSaving = false; }, 1500);
        }
    }

    // --- Export ---

    exportSeatingCSV() {
        const tables = this.config.tables || [];
        const hasAssignments = tables.some(t => (t.seats || []).length > 0);
        if (!hasAssignments) {
            this.showToast(this.t('noAssignmentsToExport'), 'warning');
            return;
        }

        // One row per SEAT — a party of three is already three rows ("Ann",
        // "Ann +1", "Ann +2"), so there is no party size to carry: every seat
        // is one person. The old "Party Size" column printed a literal 1 on
        // every row (nothing ever writes seat.guestCount) and read as a
        // contradiction next to the expanded names.
        const rows = ['Table,Type,Guest Name'];
        for (const table of tables) {
            if (!table.seats || table.seats.length === 0) {
                rows.push(`"${table.name}","${table.type || ''}","(empty)"`);
                continue;
            }
            for (const seat of table.seats) {
                rows.push(`"${table.name}","${table.type || ''}","${(seat.guestName || '').replace(/"/g, '""')}"`);
            }
        }

        const csv = rows.join('\n');
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `seating-assignments-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    printSeatingView() {
        const tables = this.config.tables || [];
        const hasAssignments = tables.some(t => (t.seats || []).length > 0);
        if (!hasAssignments) {
            this.showToast(this.t('noAssignmentsToExport'), 'warning');
            return;
        }

        const dateStr = new Date().toLocaleDateString();
        let tableCards = '';
        for (const table of tables) {
            const seats = table.seats || [];
            // Same story as the CSV: one row per seat, so the old "Party"
            // column was a column of 1s. Numbered instead, which is what a
            // printed table sheet is actually read against.
            const guestRows = seats.length === 0
                ? '<tr><td colspan="2" style="color:#999;font-style:italic">— empty —</td></tr>'
                : seats.map((s, i) => `<tr><td style="text-align:center;color:#999">${i + 1}</td><td>${this.escapeHtml(s.guestName || '—')}</td></tr>`).join('');
            const occupancy = seats.length;
            tableCards += `
                <div style="break-inside:avoid;border:1px solid #ccc;border-radius:8px;padding:12px;margin-bottom:12px">
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;border-bottom:2px solid #333;padding-bottom:6px">
                        <strong style="font-size:15px">${this.escapeHtml(table.name)}</strong>
                        <span style="font-size:12px;color:#666">${occupancy} / ${table.capacity} ${table.type ? '(' + table.type + ')' : ''}</span>
                    </div>
                    <table style="width:100%;border-collapse:collapse;font-size:13px">
                        <thead><tr style="border-bottom:1px solid #ddd"><th style="text-align:center;padding:3px 0;width:28px">#</th><th style="text-align:left;padding:3px 0">Guest</th></tr></thead>
                        <tbody>${guestRows}</tbody>
                    </table>
                </div>`;
        }

        const totalGuests = tables.reduce((sum, t) => sum + (t.seats || []).length, 0);
        const totalCapacity = tables.reduce((sum, t) => sum + (t.capacity || 0), 0);

        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Seating Assignments</title>
            <style>
                body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; color: #333; }
                h1 { font-size: 20px; margin-bottom: 4px; }
                .meta { font-size: 12px; color: #666; margin-bottom: 16px; }
                .grid { columns: 2; column-gap: 16px; }
                @media print { body { padding: 0; } }
                @media (max-width: 600px) { .grid { columns: 1; } }
            </style></head><body>
            <h1>🪑 Seating Assignments</h1>
            <div class="meta">${dateStr} &nbsp;•&nbsp; ${tables.length} tables &nbsp;•&nbsp; ${totalGuests} / ${totalCapacity} guests</div>
            <div class="grid">${tableCards}</div>
        </body></html>`;

        const w = window.open('', '_blank', 'width=800,height=600');
        w.document.write(html);
        w.document.close();
        setTimeout(() => w.print(), 400);
    }

    // --- Save / Auto-Save ---

    async autoSave() {
        clearTimeout(this._saveTimeout);
        this._saveTimeout = setTimeout(() => this.save(), 500);
    }

    async save() {
        // View-only demo: every write is owner-gated, so a PUT only comes back
        // 401 and reports a save failure the visitor never caused.
        if (window.isDemoMode) return;
        try {
            this._isSaving = true;
            const res = await fetch(`/api/seating/${this.formId}`, {
                method: 'PUT',
                headers: {
                    ...(await this._authHeaders()),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    tables: this.config.tables,
                    rules: this.config.rules,
                    assignmentMode: this.config.assignmentMode || 'manual',
                    defaultStrategy: this.config.defaultStrategy || 'roundrobin',
                    childOverrides: this.config.childOverrides || {},
                    tagColors: this.config.tagColors || {},
                    clientId: this._clientId
                })
            });
            const data = await res.json().catch(() => ({}));

            // The response used to be dropped on the floor and isDirty cleared
            // regardless, so a PUT that came back 401 looked exactly like a save:
            // the chart stayed on screen, the dirty flag said "saved", and the
            // edit was gone at the next load with nothing said to the host. That
            // 401 is not hypothetical - _authHeaders() returns {} whenever auth
            // has not resolved inside its 4s bound, which is the Safari
            // token-loss case in save_failure_telemetry.md, and the PUT then goes
            // out with no Authorization header at all. Keep the work dirty so the
            // next save retries it, and say so out loud: console.error is picked
            // up by QRErrorBuffer, so the failure reaches support with the chat.
            // Found by the 2026-09-18 14:00Z autopilot run.
            if (!res.ok || data.success === false) {
                this.isDirty = true;
                console.error(`Seating save failed: HTTP ${res.status}`, data.error || '');
                this.showToast(data.error || this.t('chartSaveFailed'), 'danger');
                return;
            }

            this.isDirty = false;
            // The campaigns tab caches "does this form have tables?" to decide
            // whether the +Table Number merge tag is usable. A host who creates
            // their first table and immediately writes a reminder would
            // otherwise meet a pill that still thinks there's no chart.
            window.dashboardCampaigns?.invalidateSeatingStatus?.();
        } catch (error) {
            // A network throw leaves isDirty as it was - still true - which is
            // correct, but it was said only to a console the host never opens.
            console.error('Save error:', error);
            this.showToast(this.t('chartSaveFailed'), 'danger');
        } finally {
            // Delay clearing the flag slightly so the Firestore echo has time to arrive and be skipped
            setTimeout(() => { this._isSaving = false; }, 1500);
        }
    }

    // --- Event Listeners ---

    setupEventListeners() {
        if (this._listenersAttached) return;
        this._listenersAttached = true;

        // The Dietary summary bar stays visible on the Seating tab, matching every
        // other dashboard tab (Responses / Campaigns). It's a useful aid for
        // dietary-aware seating planning, and hosts expect it everywhere the bar
        // normally appears. (It previously got "tucked" away here to raise the
        // chart; that was removed per host feedback — feature parity wins.)
        // Note: it's still hidden on the Check-In tab on phones via admin.css,
        // which is a separate, intentional space-saving rule.

        // Reveal a cut-off guest name on hover (sidebar rows and seat chips alike).
        this._watchNameTooltips();

        // Auto-assign button
        document.getElementById('seatingAutoAssignBtn')?.addEventListener('click', () => this.runAutoAssign());

        // Export dropdown items
        document.getElementById('seatingExportCSV')?.addEventListener('click', (e) => { e.preventDefault(); this.exportSeatingCSV(); });
        document.getElementById('seatingExportPrint')?.addEventListener('click', (e) => { e.preventDefault(); this.printSeatingView(); });

        // Mode pill buttons
        document.getElementById('seatingModeManual')?.addEventListener('click', () => {
            this.config.assignmentMode = 'manual';
            this.isDirty = true;
            this.syncModeUI();
            this.autoSave();
        });
        document.getElementById('seatingModeQrAuto')?.addEventListener('click', () => {
            if (!this.isPro(this.currentFormData)) return;
            this.config.assignmentMode = 'qr_auto';
            if (!this.config.defaultStrategy || this.config.defaultStrategy === 'manual') {
                this.config.defaultStrategy = 'roundrobin';
            }
            this.isDirty = true;
            this.syncModeUI();
            this.autoSave();
        });

        // Strategy sub-dropdown (visible in QR Auto-Assign mode)
        document.getElementById('seatingStrategySelect')?.addEventListener('change', (e) => {
            this.config.defaultStrategy = e.target.value;
            this.isDirty = true;
            this.autoSave();
        });

        // Add table button
        document.getElementById('seatingAddTableBtn')?.addEventListener('click', () => this.showAddTableModal());

        // Clear all button
        document.getElementById('seatingClearAllBtn')?.addEventListener('click', async () => {
            if (!confirm(this.t('clearAllConfirm'))) return;
            this.pushUndoState();
            this.config.tables.forEach(t => t.seats = []);
            this.isDirty = true;
            this.render();
            this.autoSave();
        });

        // Search filter
        document.getElementById('seatingGuestSearch')?.addEventListener('input', (e) => {
            this.searchFilter = e.target.value;
            this.renderUnassignedList();
        });

        // Bulk-select mode toggle — the checkbox icon beside the search field
        // (moved out of the list into the header row, task #87).
        const selectModeBtn = document.getElementById('seatingSelectModeBtn');
        if (selectModeBtn && !selectModeBtn._seatingBound) {
            selectModeBtn._seatingBound = true;
            selectModeBtn.addEventListener('click', () => {
                this._selectMode = !this._selectMode;
                if (!this._selectMode) this._bulkSelected.clear();
                this._syncSelectModeBtn();
                this.renderUnassignedList();
                this._updateBulkTagBar();
            });
            this._syncSelectModeBtn();
        }

        // Mobile: Tables / Unassigned tab switcher (task #9). On phones the two
        // panels are shown one-at-a-time via tabs instead of stacked — this kills
        // the nested-scroll jank (the old design pinned the Unassigned panel with
        // its own inner scroll, which fought the page scroll) and gives each panel
        // the full, smoothly-scrolling screen. Desktop keeps the side-by-side view.
        this._setupMobileSeatingTabs();

        // Type-in table assignment: commit when the host presses Enter or the
        // input loses focus (change). Delegated on the stable main-content wrapper
        // so it survives the unassigned-list / tables re-renders. Stays in sync
        // with drag-and-drop because both funnel through assignGuestToTable.
        const seatingMain = document.getElementById('seatingMainContent');
        if (seatingMain) {
            seatingMain.addEventListener('keydown', (e) => {
                const input = e.target.closest && e.target.closest('.seating-table-input');
                if (input && e.key === 'Enter') {
                    e.preventDefault();
                    input.blur(); // triggers the change handler below
                }
            });
            seatingMain.addEventListener('change', (e) => {
                const input = e.target.closest && e.target.closest('.seating-table-input');
                if (input) this._handleTableInputCommit(input);
            });
        }

        // Everything from here down delegates on STATIC elements (the card,
        // the tables container, document) — bind once per page life. Without
        // this guard, switching forms re-runs setupEventListeners and stacks
        // duplicate listeners (a menu toggle would then flip twice per click).
        const seatingCard = document.getElementById('seatingUnassignedCard');
        if (seatingCard && seatingCard._seatingCardBound) return;
        if (seatingCard) seatingCard._seatingCardBound = true;

        // Bulk-tag selection: toggle a party in/out of the selection set.
        // Delegated on the CARD (not the list) so the category-filter / ⋯
        // controls hosted in the card header are covered too (task #249).
        seatingCard?.addEventListener('change', (e) => {
            // Custom category color chosen from the legend's hidden color input.
            const colorInput = e.target.closest('.seating-legend-color-input');
            if (colorInput) {
                const key = colorInput.dataset.category;
                const val = colorInput.value;
                if (key && val) {
                    if (!this.config.tagColors) this.config.tagColors = {};
                    this.config.tagColors[key] = val;
                    this.isDirty = true;
                    this.renderUnassignedList();
                    this.autoSave();
                }
                return;
            }
            const cb = e.target.closest('.seating-bulk-cb');
            if (!cb) return;
            const responseId = cb.dataset.responseId;
            if (!responseId) return;
            if (cb.checked) this._bulkSelected.add(responseId);
            else this._bulkSelected.delete(responseId);
            const row = cb.closest('.seating-guest-item');
            if (row) row.classList.toggle('bulk-selected', cb.checked);
            this._updateBulkTagBar();
        });

        // Click delegation for unassigned-list + header-control interactions
        document.getElementById('seatingUnassignedCard')?.addEventListener('click', (e) => {
            // Toggle child/adult override from the unassigned dropdown menu
            const toggleChild = e.target.closest('[data-action="toggle-child"]');
            if (toggleChild) {
                e.preventDefault();
                const responseId = toggleChild.dataset.responseId;
                const guestName = toggleChild.dataset.guestName;
                if (responseId && guestName) this.toggleChildOverride(responseId, guestName);
                return;
            }

            // Edit a guest's host-only seating tags (multi-tag grouping). The tag
            // icon carries data-response-id directly; fall back to a legacy trigger
            // lookup for any other call site.
            const editTags = e.target.closest('[data-action="edit-tags"]');
            if (editTags) {
                e.preventDefault();
                const responseId = editTags.dataset.responseId
                    || editTags.closest('.seating-guest-actions')?.querySelector('[data-response-id]')?.dataset.responseId;
                // data-member marks an individual party member's row — scope the
                // editor to that person instead of the whole party (task #153).
                const memberName = editTags.dataset.member === '1' ? (editTags.dataset.guestName || null) : null;
                if (responseId) this.showTagEditorModal(responseId, memberName);
                return;
            }

            // Delete an entire tag-category (legend chip × button). Checked
            // BEFORE the filter handler so clicking × doesn't also toggle the filter.
            const delCat = e.target.closest('[data-action="delete-category"]');
            if (delCat) {
                e.preventDefault();
                e.stopPropagation();
                const key = delCat.dataset.category;
                if (key) this.deleteCategory(key);
                return;
            }

            // Clicking the colored dot opens a color picker for that category
            // (rather than toggling the filter). The hidden <input type="color">
            // lives as a sibling of the chip button.
            const colorDot = e.target.closest('[data-color-cat]');
            if (colorDot) {
                e.preventDefault();
                e.stopPropagation();
                const group = colorDot.closest('.seating-legend-chip-group');
                const input = group && group.querySelector('.seating-legend-color-input');
                if (input) input.click();
                return;
            }

            // Filter dropdown + ⋯ menu toggles (task #89). Opening one closes
            // the other; the open flag survives re-renders so multi-selecting
            // categories doesn't snap the menu shut.
            const filterToggle = e.target.closest('[data-action="toggle-filter-menu"]');
            if (filterToggle) {
                e.preventDefault();
                e.stopPropagation();
                this._filterMenuOpen = !this._filterMenuOpen;
                this._moreMenuOpen = false;
                this.renderUnassignedList();
                return;
            }
            const moreToggle = e.target.closest('[data-action="toggle-seating-more"]');
            if (moreToggle) {
                e.preventDefault();
                e.stopPropagation();
                this._moreMenuOpen = !this._moreMenuOpen;
                this._filterMenuOpen = false;
                this.renderUnassignedList();
                return;
            }
            const expandAllBtn = e.target.closest('[data-action="expand-all-sections"]');
            if (expandAllBtn) {
                e.preventDefault();
                this._moreMenuOpen = false;
                this.expandAllSections();
                return;
            }
            const collapseAllBtn = e.target.closest('[data-action="collapse-all-sections"]');
            if (collapseAllBtn) {
                e.preventDefault();
                this._moreMenuOpen = false;
                this.collapseAllSections();
                return;
            }

            // Category filter row (inside the Filter dropdown)
            const chip = e.target.closest('[data-action="filter-category"]');
            if (chip) {
                e.preventDefault();
                const key = chip.dataset.category;
                if (!key) return;
                if (this.categoryFilter.has(key)) this.categoryFilter.delete(key);
                else this.categoryFilter.add(key);
                this.renderUnassignedList();
                return;
            }
            const statusItem = e.target.closest('[data-action="filter-status"]');
            if (statusItem) {
                e.preventDefault();
                const key = statusItem.dataset.status;
                if (!key) return;
                if (this.statusFilter.has(key)) this.statusFilter.delete(key);
                else this.statusFilter.add(key);
                this.renderUnassignedList();
                return;
            }
            const clearBtn = e.target.closest('[data-action="clear-category-filter"]');
            if (clearBtn) {
                e.preventDefault();
                this.categoryFilter.clear();
                this.statusFilter.clear();
                this.renderUnassignedList();
                return;
            }

            // Any click that reaches here while a dropdown is open — and isn't
            // inside one — closes them (the toggles above stopPropagation, so
            // this only fires for genuine outside clicks in the seating UI).
            if ((this._filterMenuOpen || this._moreMenuOpen)
                && !e.target.closest('[data-role="seating-filter-menu"]')
                && !e.target.closest('[data-role="seating-more-menu"]')) {
                this._filterMenuOpen = false;
                this._moreMenuOpen = false;
                this.renderUnassignedList();
            }

            // Section collapse/expand toggle
            const sectionHeader = e.target.closest('[data-action="toggle-section"]');
            if (sectionHeader && !e.target.closest('.seating-guest-actions')) {
                e.preventDefault();
                const key = sectionHeader.dataset.sectionKey;
                if (key) this.toggleSectionCollapse(key);
                return;
            }

            // Party expand/collapse toggle (the chevron on a multi-member party).
            const partyToggle = e.target.closest('[data-action="toggle-party"]');
            if (partyToggle) {
                e.preventDefault();
                e.stopPropagation();
                const rid = partyToggle.dataset.responseId;
                if (rid) this.togglePartyExpand(rid);
                return;
            }

            // Full-row selection toggle. In Select mode the ENTIRE row is a
            // selection hit-box so the host can rapidly pick parties without aiming
            // at the small checkbox. Direct checkbox clicks are handled by the
            // `change` listener above; here we skip the checkbox itself and any
            // interactive control on the row.
            const guestItem = e.target.closest('.seating-guest-item');
            if (guestItem
                && this._selectMode
                && !e.target.closest('.seating-bulk-cb')
                && !e.target.closest('.seating-guest-actions')
                && !e.target.closest('.seating-table-input')
                && !e.target.closest('[data-action]')) {
                e.preventDefault();
                const responseId = guestItem.dataset.responseId;
                if (!responseId) return;
                if (this._bulkSelected.has(responseId)) this._bulkSelected.delete(responseId);
                else this._bulkSelected.add(responseId);
                this.renderUnassignedList(); // re-render syncs checkbox state, highlight + bar
            }
        });

        // Delegate click events for table actions
        document.getElementById('seatingTablesContainer')?.addEventListener('click', (e) => {
            const actionEl = e.target.closest('[data-action]');
            if (!actionEl) return;

            const action = actionEl.dataset.action;
            const tableId = actionEl.dataset.tableId;

            if (action === 'add-table-tile') {
                e.preventDefault();
                this.showAddTableModal();
                return;
            }
            // NOTE: edit-table / delete-table are intentionally NOT handled here —
            // the three-dots menu is lifted into <body> while open (clip-escape), so
            // its clicks never bubble back to this container. They're caught by the
            // document-level listener below instead.
            if (action === 'edit-table-name' && tableId) {
                e.preventDefault();
                this.startInlineRename(actionEl, tableId);
            } else if (action === 'toggle-collapse' && tableId) {
                e.preventDefault();
                this.toggleTableCollapse(tableId);
            } else if (action === 'remove-seat') {
                const responseId = actionEl.dataset.responseId;
                const guestName = actionEl.dataset.guestName;
                const tId = actionEl.dataset.tableId;
                this.removeGuestFromTable(responseId, guestName, tId);
            } else if (action === 'toggle-child') {
                e.preventDefault();
                const responseId = actionEl.dataset.responseId;
                const guestName = actionEl.dataset.guestName;
                if (responseId && guestName) this.toggleChildOverride(responseId, guestName);
            }
        });

        // Edit / Delete live in the table three-dots menu, which the clip-escape lifts
        // into <body> while open — so those clicks bypass the #seatingTablesContainer
        // listener above (they no longer share an ancestor). Catch them at the document
        // level. Bound once per instance; scoped to the seating table menu (escaped to
        // <body> OR still in the card) so it can't collide with any other [data-action].
        if (!this._tableMenuActionsBound) {
            this._tableMenuActionsBound = true;
            document.addEventListener('click', (e) => {
                const el = e.target.closest('[data-action="edit-table"], [data-action="delete-table"]');
                if (!el) return;
                if (!el.closest('.seating-escaped-menu') && !el.closest('#seatingTablesContainer')) return;
                const tableId = el.dataset.tableId;
                if (!tableId) return;
                e.preventDefault();
                if (el.dataset.action === 'edit-table') this.showEditTableModal(tableId);
                else this.deleteTable(tableId);
            });
        }

        // Keep the sticky Unassigned panel's top offset in sync with the toolbar
        // height. Watch the toolbar itself, not the window: its height also
        // changes with no resize - the unassigned counter's text, Manual/QR
        // Auto adding the strategy select, and the Seating tab being shown
        // after it rendered hidden (measured at 0). A window listener missed
        // all three and left a blank block above the panel. Bound once.
        if (!this._stickyResizeBound) {
            this._stickyResizeBound = true;
            const toolbar = document.querySelector('.seating-toolbar');
            if (toolbar && window.ResizeObserver) {
                new ResizeObserver(() => this._syncStickyOffset()).observe(toolbar);
            } else {
                let raf = null;
                window.addEventListener('resize', () => {
                    if (raf) cancelAnimationFrame(raf);
                    raf = requestAnimationFrame(() => this._syncStickyOffset());
                });
            }
        }

        // Undo/Redo
        document.getElementById('seatingUndoBtn')?.addEventListener('click', () => this.undo());
        document.getElementById('seatingRedoBtn')?.addEventListener('click', () => this.redo());

        // Add Rule button
        document.getElementById('seatingAddRuleBtn')?.addEventListener('click', () => {
            this.showAddRuleModal();
        });

        // Global collapse/expand-all toggle
        document.getElementById('seatingCollapseAllBtn')?.addEventListener('click', () => {
            const btn = document.getElementById('seatingCollapseAllBtn');
            const allCollapsed = btn?.dataset?.state === 'collapsed';
            this.collapseAllTables(!allCollapsed);
        });

        // Rules panel: remove rule
        document.getElementById('seatingRulesPanel')?.addEventListener('click', (e) => {
            const el = e.target.closest('[data-action="remove-rule"]');
            if (!el) return;
            this.pushUndoState();
            const type = el.dataset.ruleType;
            const a = el.dataset.ruleA;
            const b = el.dataset.ruleB;
            if (!this.config.rules) return;
            const arr = type === 'together' ? this.config.rules.keepTogether : this.config.rules.keepApart;
            const idx = arr.findIndex(r => (r[0] === a && r[1] === b) || (r[0] === b && r[1] === a));
            if (idx !== -1) arr.splice(idx, 1);
            this.isDirty = true;
            this.render();
            this.autoSave();
        });
    }

    _collapseStorageKey() {
        return `seating_collapsed_${this.formId}`;
    }

    _loadCollapseState() {
        this._collapsedTables = new Set();
        try {
            const raw = localStorage.getItem(this._collapseStorageKey());
            if (raw) {
                const ids = JSON.parse(raw);
                if (Array.isArray(ids)) this._collapsedTables = new Set(ids);
            }
        } catch (e) { /* ignore corrupt localStorage */ }

        // Also load section (category) collapse state
        this._collapsedSections = new Set();
        try {
            const raw = localStorage.getItem(`seating_sections_${this.formId}`);
            if (raw) {
                const keys = JSON.parse(raw);
                if (Array.isArray(keys)) this._collapsedSections = new Set(keys);
            }
        } catch (e) { /* ignore */ }
    }

    // On phones, start the tables COLLAPSED by default so the host gets a
    // scannable overview of every table (name + fill + badges) instead of a long
    // scroll through every seat — tap a table to expand it. Applied only:
    //   • on mobile (desktop has the room to show seats),
    //   • when the host hasn't already customized collapse on this device
    //     (a saved localStorage key means their choice wins — never override it),
    //   • and when there are enough tables that an overview actually helps.
    // Intentionally NOT saved: it's a first-view default. The moment the host
    // expands/collapses anything, toggleTableCollapse persists their set and this
    // guard (saved key present) stops re-applying.
    _applyMobileTableCollapseDefault() {
        try {
            if (!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches)) return;
            if (localStorage.getItem(this._collapseStorageKey())) return;
        } catch (e) { return; }
        const tables = (this.config && this.config.tables) || [];
        if (tables.length < 4) return;
        tables.forEach(t => this._collapsedTables.add(t.id));
    }

    _saveCollapseState() {
        try {
            const validIds = this.config.tables.map(t => t.id);
            const filtered = [...this._collapsedTables].filter(id => validIds.includes(id));
            localStorage.setItem(this._collapseStorageKey(), JSON.stringify(filtered));
        } catch (e) { /* localStorage may be unavailable */ }
    }

    _saveSectionsState() {
        try {
            localStorage.setItem(`seating_sections_${this.formId}`, JSON.stringify([...this._collapsedSections]));
        } catch (e) { /* ignore */ }
    }

    toggleSectionCollapse(sectionKey) {
        if (this._collapsedSections.has(sectionKey)) {
            this._collapsedSections.delete(sectionKey);
        } else {
            this._collapsedSections.add(sectionKey);
        }
        this._saveSectionsState();
        this.renderUnassignedList();
    }

    // Bulk section controls, reachable from the sidebar's ⋯ menu (task #89).
    expandAllSections() {
        this._collapsedSections.clear();
        this._saveSectionsState();
        this.renderUnassignedList();
    }

    collapseAllSections() {
        document.querySelectorAll('.seating-section[data-section-key]').forEach(el => {
            this._collapsedSections.add(el.dataset.sectionKey);
        });
        this._saveSectionsState();
        this.renderUnassignedList();
    }

    // Expand/collapse a single party's member rows in the unassigned list
    // (task #91). Not persisted — every load starts fully collapsed.
    togglePartyExpand(responseId) {
        if (this._expandedParties.has(responseId)) {
            this._expandedParties.delete(responseId);
        } else {
            this._expandedParties.add(responseId);
        }
        this.renderUnassignedList();
    }

    toggleTableCollapse(tableId) {
        if (this._collapsedTables.has(tableId)) {
            this._collapsedTables.delete(tableId);
        } else {
            this._collapsedTables.add(tableId);
        }
        this._saveCollapseState();
        this.renderTables();
    }

    collapseAllTables(collapse) {
        if (collapse) {
            this.config.tables.forEach(t => this._collapsedTables.add(t.id));
        } else {
            this._collapsedTables.clear();
        }
        this._saveCollapseState();
        this.renderTables();
        this._updateCollapseAllButton();
    }

    _updateCollapseAllButton() {
        const btn = document.getElementById('seatingCollapseAllBtn');
        if (!btn) return;
        const allCollapsed = this.config.tables.length > 0
            && this.config.tables.every(t => this._collapsedTables.has(t.id));
        const icon = btn.querySelector('i');
        if (icon) icon.className = (allCollapsed ? 'bi bi-arrows-expand' : 'bi bi-arrows-collapse') + ' me-2';
        const label = btn.querySelector('.seating-collapse-label');
        if (label) label.textContent = allCollapsed ? this.t('expandAll') : this.t('collapseAll');
        btn.title = allCollapsed ? this.t('expandAll') : this.t('collapseAll');
        btn.dataset.state = allCollapsed ? 'collapsed' : 'expanded';
    }

    moveTableBefore(srcId, targetId) {
        if (srcId === targetId) return;
        const tables = this.config.tables;
        const srcIdx = tables.findIndex(t => t.id === srcId);
        const targetIdx = tables.findIndex(t => t.id === targetId);
        if (srcIdx === -1 || targetIdx === -1) return;
        this.pushUndoState();
        const [moved] = tables.splice(srcIdx, 1);
        // After splice, recompute target index since indices may have shifted
        const newTargetIdx = tables.findIndex(t => t.id === targetId);
        tables.splice(newTargetIdx, 0, moved);
        this.isDirty = true;
        this.renderTables();
        this.autoSave();
    }

    moveTableToEnd(srcId) {
        const tables = this.config.tables;
        const srcIdx = tables.findIndex(t => t.id === srcId);
        if (srcIdx === -1 || srcIdx === tables.length - 1) return;
        this.pushUndoState();
        const [moved] = tables.splice(srcIdx, 1);
        tables.push(moved);
        this.isDirty = true;
        this.renderTables();
        this.autoSave();
    }

    startInlineRename(nameEl, tableId) {
        if (nameEl.dataset.editing === 'true') return;
        const table = this.config.tables.find(t => t.id === tableId);
        if (!table) return;

        nameEl.dataset.editing = 'true';
        const originalName = table.name || '';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'seating-table-name-input';
        input.value = originalName;
        input.maxLength = 100;
        input.setAttribute('aria-label', this.t('tableName'));

        nameEl.style.display = 'none';
        nameEl.parentNode.insertBefore(input, nameEl);
        input.focus();
        input.select();

        let finalized = false;
        const cleanup = () => {
            input.remove();
            nameEl.style.display = '';
            delete nameEl.dataset.editing;
        };

        const commit = () => {
            if (finalized) return;
            finalized = true;
            const newName = input.value.trim();
            if (!newName || newName === originalName) {
                cleanup();
                return;
            }
            this.pushUndoState();
            table.name = newName;
            nameEl.textContent = newName;
            this.isDirty = true;
            cleanup();
            this.autoSave();
        };

        const cancel = () => {
            if (finalized) return;
            finalized = true;
            cleanup();
        };

        input.addEventListener('blur', commit);
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                input.blur();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
            }
        });
        input.addEventListener('click', (e) => e.stopPropagation());
        input.addEventListener('mousedown', (e) => e.stopPropagation());
    }

    // --- Utilities ---

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text || '';
        return div.innerHTML;
    }

    escapeAttr(text) {
        return (text || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    generateId() {
        return 'table-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6);
    }

    showToast(message, type) {
        if (typeof showToast === 'function') {
            showToast(message, type);
        } else {
            // Fallback: simple alert-style toast
            const container = document.getElementById('seatingMainContent');
            if (!container) return;
            const alert = document.createElement('div');
            alert.className = `alert alert-${type} alert-dismissible fade show py-2 mb-2`;
            alert.innerHTML = `${message}<button type="button" class="btn-close btn-sm" data-bs-dismiss="alert"></button>`;
            container.insertBefore(alert, container.firstChild);
            setTimeout(() => alert.remove(), 3000);
        }
    }
}

// Create global instance
window.dashboardSeating = new DashboardSeating();

// Listen for tab activation and form changes
document.addEventListener('DOMContentLoaded', () => {
    document.getElementById('seating-tab')?.addEventListener('shown.bs.tab', () => {
        const formId = typeof getCurrentFormId === 'function' ? getCurrentFormId() : null;
        if (formId) {
            window.dashboardSeating.init(formId);
        } else {
            window.dashboardSeating.showNoFormError();
        }
    });

    // Clean up listeners when leaving the seating tab
    document.getElementById('seating-tab')?.addEventListener('hidden.bs.tab', () => {
        window.dashboardSeating.destroy();
    });

    // Re-init on form change if seating tab is active
    document.getElementById('form-select')?.addEventListener('change', (e) => {
        if (document.getElementById('seating-tab')?.classList.contains('active')) {
            window.dashboardSeating.init(e.target.value);
        }
    });
});
